import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import LZString from 'lz-string';
import {
    collection,
    addDoc,
    setDoc,
    doc,
    deleteDoc,
    onSnapshot,
    query,
    orderBy,
    serverTimestamp,
    updateDoc,
    getDocs,
    getDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { signOut } from 'firebase/auth';
import { auth, db, functions } from '../lib/firebase';
import { useAuth } from '../lib/AuthContext';
import { getComplianceLabel, parseAxeResults, VIOLATION_COSTS, deduplicateViolations, getGradeFromRisk } from '../lib/parseResults';

const ScanResultsView = React.lazy(() => import('../components/ScanResultsView'));
import { ConfirmModal, Toast } from '../components/InAppNotification';
import UpgradePromptModal from '../components/UpgradePromptModal';
import { RiskScoreTrendTremor, SeverityTrendTremor, WcagCriteriaBreakdownNivo, ImpactPieChartNivo } from '../components/AnalyticsCharts';
import { Card, Metric, Text, BadgeDelta, Flex, Grid as TremorGrid } from '@tremor/react';
import Footer from '../components/Footer';
import MultiSelectDropdown from '../components/MultiSelectDropdown';



const COMPLIANCE_CONTEXTS = [
    {
        value: 'state-local',
        label: 'State / Local Govt',
        description: 'ADA Title II & Section 508',
        icon: '🏛️',
        examples: 'Public portals, city nodes, transit networks, education clusters',
        tags: ['wcag2a', 'wcag2aa', 'wcag21a', 'wcag21aa', 'section508'],
    },
    {
        value: 'federal',
        label: 'Federal / Funded',
        description: 'Section 508 Standard',
        icon: '📡',
        examples: 'Agency hubs, centralized research, healthcare infrastructure',
        tags: ['wcag2a', 'wcag2aa', 'wcag21a', 'wcag21aa', 'section508'],
    },
    {
        value: 'private',
        label: 'Private Sector',
        description: 'ADA Title III Corporate Standard',
        icon: '💎',
        examples: 'Commercial platforms, retail nodes, enterprise interfaces',
        tags: ['wcag2a', 'wcag2aa', 'wcag21a', 'wcag21aa'],
    },
];

const STATUS_STYLES = {
    pending: { label: 'In Queue', bg: 'bg-gray-500/10', text: 'text-gray-500', icon: '⏳' },
    scheduled: { label: 'Pending Run', bg: 'bg-blue-500/10', text: 'text-blue-500', icon: '📅' },
    processing: { label: 'Scrambling...', bg: 'bg-(--accent)/10', text: 'text-(--accent)', icon: '⚡' },
    completed: { label: 'Analyzed', bg: 'bg-(--success)/10', text: 'text-(--success)', icon: '💎' },
    failed: { label: 'Error', bg: 'bg-(--error)/10', text: 'text-(--error)', icon: '🚨' },
};

const DEVICE_PROFILES = [
    { id: 'desktop', label: '💻 Desktop Chrome' },
    { id: 'mobile', label: '📱 Mobile Safari (iPhone)' },
    { id: 'tablet', label: '📱 Tablet (iPad)' }
];

const getPolicyFromTags = (tags = []) => {
    if (!tags || tags.length === 0) return 'WCAG 2.1 AA'; // Fallback

    // Check for Section 508 explicitly
    if (tags.some(t => t.toLowerCase() === 'section508')) return 'SECTION 508';

    // Check for Best Practice explicitly
    if (tags.some(t => t.toLowerCase() === 'best-practice')) return 'BEST PRACTICE';

    // Check for WCAG versions directly (wcag2a, wcag2aa, wcag21a, wcag21aa, wcag22aa)
    if (tags.some(t => t.toLowerCase().startsWith('wcag'))) {
        // Return highest specific spec if we want to be fancy, or just generic AA
        // For now, standardize on WCAG 2.1 AA since that's our core target
        return 'WCAG 2.1 AA';
    }

    return 'WCAG 2.1 AA'; // Default to standard framework if unknown
};

function DashboardPage({
    onViewChange,
    externalTab,
    setExternalTab,
    setUnviewedCount,
    onlineAgents = [],
    onSiteSelect,
    initialIssuesFilter,
    onClearInitialFilter,
    initialUrl,
    onClearInitialUrl
}) {
    const { user, profile, userRole, planLimits } = useAuth();
    const [url, setUrl] = useState('');
    const [selectedDevices, setSelectedDevices] = useState(['desktop']);
    const [complianceType, setComplianceType] = useState('');
    const [includeBestPractices, setIncludeBestPractices] = useState(true);
    const [includePrivacy, setIncludePrivacy] = useState(true);
    const [depth, setDepth] = useState('single');
    const [pageCap, setPageCap] = useState(50);
    const [scans, setScans] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [importedUrls, setImportedUrls] = useState([]);
    const [error, setError] = useState('');
    const [expandedScan, setExpandedScan] = useState(null);
    const [analyticsTimeRange, setAnalyticsTimeRange] = useState('30d'); // '7d', '30d', '90d', 'all', 'custom'
    const [analyticsCustomStart, setAnalyticsCustomStart] = useState('');
    const [analyticsCustomEnd, setAnalyticsCustomEnd] = useState('');
    const [analyticsSiteFilter, setAnalyticsSiteFilter] = useState('all');
    const [hydratedResults, setHydratedResults] = useState({});
    const [isLoadingChunks, setIsLoadingChunks] = useState(false);
    const analyticsRef = useRef(null); // For PNG/PDF export

    // Crawl Review State
    const [activeCrawlScan, setActiveCrawlScan] = useState(null);
    const [crawlUrlFilter, setCrawlUrlFilter] = useState('');
    const [crawlUrlExclude, setCrawlUrlExclude] = useState('');
    const [crawlSelectedUrls, setCrawlSelectedUrls] = useState(new Set());
    const [crawlUrlSort, setCrawlUrlSort] = useState('none'); // 'none' | 'asc' | 'desc'
    const [groupByDomain, setGroupByDomain] = useState(false);

    // Discovery Filters (Pre-Scan)
    const [scanIncludeRegex, setScanIncludeRegex] = useState('');
    const [scanExcludeRegex, setScanExcludeRegex] = useState('');
    const [pendingCrawlId, setPendingCrawlId] = useState(null);

    // Filters & Archiving State
    const [viewMode, setViewMode] = useState('active'); // 'active' | 'archived' | 'audit'
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'completed' | 'failed' | 'processing' | 'pending'
    const [domainFilter, setDomainFilter] = useState('all');
    const [scansSort, setScansSort] = useState({ key: 'date', direction: 'desc' });
    const [pagesSort, setPagesSort] = useState({ key: 'lastScan', direction: 'desc' });
    const [issuesSort, setIssuesSort] = useState({ key: 'totalCount', direction: 'desc' });
    const [issuesSearchQuery, setIssuesSearchQuery] = useState('');
    const [issuesPageFilter, setIssuesPageFilter] = useState([]);
    const [issuesSiteFilter, setIssuesSiteFilter] = useState([]);
    const [issuesSeverityFilter, setIssuesSeverityFilter] = useState('all');
    const [analyticsSiteSearch, setAnalyticsSiteSearch] = useState('');
    const [reportsSearchQuery, setReportsSearchQuery] = useState('');
    const [reportsSort, setReportsSort] = useState({ key: 'date', direction: 'desc' });
    const [journeysSort, setJourneysSort] = useState({ key: 'createdAt', direction: 'desc' });
    const [targetAgentId, setTargetAgentId] = useState('auto');
    const [distributed, setDistributed] = useState(false);
    const [journeys, setJourneys] = useState([]);
    const [selectedJourney, setSelectedJourney] = useState(null);

    // UI Notifications State
    const [scheduledAt, setScheduledAt] = useState(''); // New state for scan scheduling
    const [confirmState, setConfirmState] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
    const [toast, setToast] = useState(null); // { message: string, type: 'success' | 'error' }
    const [deleteConfirmScanId, setDeleteConfirmScanId] = useState(null);
    const [selectedViolationRuleId, setSelectedViolationRuleId] = useState(null);
    const [showIssuePrompt, setShowIssuePrompt] = useState(false);
    const [upgradeModal, setUpgradeModal] = useState({ isOpen: false, title: '', message: '' });
    const [issueCopied, setIssueCopied] = useState(false);
    
    // Usage Tracking State
    const [monthlyPagesScanned, setMonthlyPagesScanned] = useState(0);

    // Inline Editing State
    const [editingJourneyId, setEditingJourneyId] = useState(null);
    const [tempJourneyName, setTempJourneyName] = useState('');

    // Reports State
    const [reports, setReports] = useState([]);
    const [isGenerateReportModalOpen, setIsGenerateReportModalOpen] = useState(false);
    const [selectedReportId, setSelectedReportId] = useState(null);
    const [downloadingPDF, setDownloadingPDF] = useState(null);
    const [reportForm, setReportForm] = useState({
        name: '',
        domain: 'all',
        startDate: '',
        endDate: '',
    });

    // Pagination & Bulk Actions
    const [currentPage, setCurrentPage] = useState(1);
    const [scansPerPage, setScansPerPage] = useState(50); // 5, 10, 50, 100, 'All'
    const [selectedScans, setSelectedScans] = useState(new Set());
    const [pagesSearchQuery, setPagesSearchQuery] = useState('');
    const [pagesSeverityFilter, setPagesSeverityFilter] = useState('all');
    const [pagesSiteFilter, setPagesSiteFilter] = useState('all');
    const [selectedPages, setSelectedPages] = useState(new Set());
    const [pagesCurrentPage, setPagesCurrentPage] = useState(1);
    const [pagesPerPage, setPagesPerPage] = useState(50);
    
    // Scheduling & Failure Options
    const [isScheduled, setIsScheduled] = useState(false);
    const [scheduleFrequency, setScheduleFrequency] = useState('daily');
    const [maxRetries, setMaxRetries] = useState(0);
    const [failOnPageError, setFailOnPageError] = useState(false);

    const dashboardTab = externalTab || 'analytics';
    const setDashboardTab = setExternalTab || (() => { });

    // Load User Preferences on Mount
    useEffect(() => {
        if (!user) return;
        const fetchPreferences = async () => {
            try {
                const docSnap = await getDoc(doc(db, 'users', user.uid));
                if (docSnap.exists() && docSnap.data().preferences) {
                    const prefs = docSnap.data().preferences;
                    if (prefs.defaultScansPerPage) setScansPerPage(prefs.defaultScansPerPage);
                    if (prefs.defaultPagesPerPage) setPagesPerPage(prefs.defaultPagesPerPage);
                    if (prefs.defaultDomainGrouping !== undefined) setGroupByDomain(prefs.defaultDomainGrouping);
                }
            } catch (err) {
                console.error("Failed to load user preferences:", err);
            }
        };
        fetchPreferences();
    }, [user]);

    // Auto-persist pagination preferences to Firestore
    const prefsLoadedRef = useRef(false);
    useEffect(() => {
        if (!user) return;
        // Skip the initial mount (values come from defaults, not user action)
        if (!prefsLoadedRef.current) {
            prefsLoadedRef.current = true;
            return;
        }
        const savePrefs = async () => {
            try {
                await updateDoc(doc(db, 'users', user.uid), {
                    'preferences.defaultScansPerPage': scansPerPage,
                    'preferences.defaultPagesPerPage': pagesPerPage,
                });
            } catch (err) {
                console.warn('Failed to save pagination preferences:', err);
            }
        };
        const timeout = setTimeout(savePrefs, 500); // debounce
        return () => clearTimeout(timeout);
    }, [scansPerPage, pagesPerPage, user]);



    // Hydrate chunked scan results when expanded
    useEffect(() => {
        if (!expandedScan || !user) return;

        const scan = scans.find(s => s.id === expandedScan);
        if (!scan || !scan.hasChunks || hydratedResults[expandedScan]) return;

        const fetchChunks = async () => {
            setIsLoadingChunks(true);
            try {
                console.log(`[Dashboard] Hydrating chunked scan: ${expandedScan}`);
                const chunksRef = collection(db, 'users', user.uid, 'queue', expandedScan, 'chunks');
                const q = query(chunksRef, orderBy('index', 'asc'));
                const snapshot = await getDocs(q);

                if (snapshot.empty) {
                    console.warn('[Dashboard] No chunks found for scan:', expandedScan);
                    return;
                }

                const fullData = snapshot.docs
                    .map(doc => doc.data().data)
                    .join('');

                let parsedResults;
                if (scan.dataIsCompressed) {
                    const decompressed = LZString.decompressFromUTF16(fullData);
                    parsedResults = JSON.parse(decompressed);
                } else {
                    parsedResults = JSON.parse(fullData);
                }

                setHydratedResults(prev => ({
                    ...prev,
                    [expandedScan]: parsedResults
                }));
            } catch (err) {
                console.error('[Dashboard] Failed to hydrate chunks:', err);
                setToast({ message: 'Failed to load full scan data.', type: 'error' });
            } finally {
                setIsLoadingChunks(false);
            }
        };

        fetchChunks();
    }, [expandedScan, scans, user, hydratedResults]);



    useEffect(() => {
        if (!user) return;

        const queueRef = collection(db, 'users', user.uid, 'queue');
        const q = query(queueRef, orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const scanDocs = snapshot.docs.map((d) => ({
                id: d.id,
                ...d.data(),
            }));
            setScans(scanDocs);
        }, (err) => {
            console.error("[Dashboard] Queue listener error:", err);
        });

        return () => unsubscribe();
    }, [user]);

    // Handle initial issues filter from navigation
    useEffect(() => {
        if (initialIssuesFilter && dashboardTab === 'issues') {
            const { site, ruleId } = initialIssuesFilter;
            console.log(`[Dashboard] Applying initial filter: ${site} / ${ruleId}`);
            if (site) setIssuesSiteFilter([site]);
            if (ruleId) setSelectedViolationRuleId(ruleId);
            onClearInitialFilter();
            
            // Scroll to the top or to the target if needed
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [initialIssuesFilter, dashboardTab, onClearInitialFilter]);

    // Handle initial URL population for Rescan Domain
    useEffect(() => {
        if (initialUrl && dashboardTab === 'new-scan') {
            console.log(`[Dashboard] Auto-populating URL for rescan: ${initialUrl}`);
            // Ensure URL starts with https:// if it looks like a domain
            const targetUrl = initialUrl.startsWith('http') ? initialUrl : `https://${initialUrl}`;
            setUrl(targetUrl);
            onClearInitialUrl();
        }
    }, [initialUrl, dashboardTab, onClearInitialUrl]);

    // Real-time listener on User Journeys
    useEffect(() => {
        if (!user) return;

        const journeysRef = collection(db, 'users', user.uid, 'journeys');
        const q = query(journeysRef, orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setJourneys(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        }, (err) => {
            console.error("[Dashboard] Journeys listener error:", err);
        });

        return () => unsubscribe();
    }, [user]);

    // Real-time listener on Monthly Page Usage
    useEffect(() => {
        if (!user) return;

        const currentMonthId = new Date().toISOString().slice(0, 7);
        const usageRef = doc(db, 'users', user.uid, 'usage', currentMonthId);

        const unsubscribe = onSnapshot(usageRef, (docSnap) => {
            if (docSnap.exists()) {
                setMonthlyPagesScanned(docSnap.data().pagesScanned || 0);
            } else {
                setMonthlyPagesScanned(0);
            }
        }, (err) => {
            console.error("[Dashboard] Usage listener error:", err);
        });

        return () => unsubscribe();
    }, [user]);

    // Auto-open Crawl Review Modal when a pending crawl completes
    useEffect(() => {
        if (!pendingCrawlId) return;
        const currentCrawl = scans.find(s => s.id === pendingCrawlId);
        if (currentCrawl && currentCrawl.status === 'completed' && currentCrawl.discoveredUrls) {
            handleReviewCrawl(currentCrawl);
            setPendingCrawlId(null);
            setDashboardTab('scans'); // Switch to scans tab to ensure modal is visible over relevant context
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [scans, pendingCrawlId]);

    // Real-time listener on Reports
    useEffect(() => {
        if (!user) return;

        const reportsRef = collection(db, 'users', user.uid, 'reports');
        const q = query(reportsRef, orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setReports(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        }, (err) => {
            console.error("[Dashboard] Reports listener error:", err);
        });

        return () => unsubscribe();
    }, [user]);

    const unviewedCount = useMemo(() => {
        return scans.filter(s => s.viewed === false && s.status === 'completed').length;
    }, [scans]);

    const handleMarkAllViewed = async () => {
        const unviewed = scans.filter(s => s.viewed === false);
        if (unviewed.length === 0) return;

        try {
            const batch = [];
            unviewed.forEach(scan => {
                batch.push(updateDoc(doc(db, 'users', user.uid, 'queue', scan.id), {
                    viewed: true,
                    updatedAt: serverTimestamp()
                }));
            });
            await Promise.all(batch);
        } catch (err) {
            console.error("Failed to mark scans as viewed:", err);
        }
    };

    // Auto-mark as viewed if user is already on the scans tab
    useEffect(() => {
        setUnviewedCount(prev => prev !== unviewedCount ? unviewedCount : prev);
        if (dashboardTab === 'scans' && unviewedCount > 0) {
            handleMarkAllViewed();
        }
    }, [dashboardTab, unviewedCount, setUnviewedCount]);

    const handleScan = async (e, scanType = 'scan') => {
        if (e) e.preventDefault();
        if (!complianceType) return;

        // Collect URLs from text input + any imported file URLs
        const allUrls = [];
        if (url.trim()) allUrls.push(url.trim());
        if (importedUrls.length > 0) allUrls.push(...importedUrls);

        if (allUrls.length === 0) return;
        if (selectedDevices.length === 0) {
            setToast({ message: 'Error: You must select at least one device profile.', type: 'error' });
            return;
        }

        // Check Domain Limits against planLimits
        const currentDomains = new Set(scans.filter(s => s.status !== 'failed' && s.url).map(s => {
            try { return new URL(s.url).hostname; } catch(e) { return s.url; }
        }));
        
        const newDomains = new Set(allUrls.map(u => {
            try { return new URL(u).hostname; } catch(e) { return u; }
        }));
        
        let domainsToAdd = 0;
        newDomains.forEach(d => {
            if (!currentDomains.has(d)) domainsToAdd++;
        });

        if (planLimits?.maxDomains !== -1 && (currentDomains.size + domainsToAdd) > (planLimits?.maxDomains || 1)) {
            setToast({ message: `Plan Limit Reached: You are currently limited to ${planLimits?.maxDomains || 1} domain${(planLimits?.maxDomains || 1) === 1 ? '' : 's'}. Please upgrade your plan in the Billing section to scan additional domains.`, type: 'error' });
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const queueRef = collection(db, 'users', user.uid, 'queue');
            let queued = 0;
            const ctx = COMPLIANCE_CONTEXTS.find(c => c.value === complianceType);
            const scanTags = ctx ? [...ctx.tags] : ['wcag2a', 'wcag2aa', 'wcag21a', 'wcag21aa'];
            if (includeBestPractices) {
                scanTags.push('best-practice');
            }

            // Check Monthly Page Limits
            const newPages = allUrls.length > 1 ? allUrls.length : (depth === 'single' ? 1 : pageCap);
            if (planLimits?.maxPagesPerMonth > 0 && (monthlyPagesScanned + newPages > planLimits.maxPagesPerMonth)) {
                setUpgradeModal({
                    isOpen: true,
                    title: 'Page Limit Reached',
                    message: `This scan requires ${newPages} pages, but you only have ${Math.max(0, planLimits.maxPagesPerMonth - monthlyPagesScanned)} pages remaining this month. Please upgrade your plan to scan more pages.`
                });
                setSubmitting(false);
                return;
            }

            let newDocId = null;
            let serverScanPayload;

            if (allUrls.length > 1) {
                // Batch/Bundle multiple URLs into a single scan
                const newDocRef = doc(queueRef);
                newDocId = newDocRef.id;
                serverScanPayload = {
                    url: allUrls[0], // First URL as primary reference
                    displayUrl: `Batch Scan: ${allUrls.length} pages`,
                    seedUrls: allUrls,
                    complianceType,
                    tags: scanTags,
                    depth: 'single',
                    maxPages: allUrls.length,
                    targetAgentId,
                    type: scanType,
                    includeRegex: scanIncludeRegex.trim() || null,
                    excludeRegex: scanExcludeRegex.trim() || null,
                    devices: selectedDevices,
                    includePrivacy,
                    status: 'pending',
                    viewed: false,
                    createdAt: serverTimestamp(),
                    scheduledAt: scheduledAt ? new Date(scheduledAt) : serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    results: null,
                    discoveredUrls: null,
                    error: null,
                    archived: false,
                    journeyId: selectedJourney?.id || null,
                    journeyName: selectedJourney?.name || null,
                    // New Scheduling & Failure Logic
                    isScheduled,
                    scheduleFrequency: isScheduled ? scheduleFrequency : null,
                    maxRetries,
                    failOnPageError,
                    retryCount: 0
                };
                queued = allUrls.length;
            } else {
                // Individual scan
                const journeyProps = selectedJourney ? {
                    journeyId: selectedJourney.id,
                    journeyName: selectedJourney.name,
                } : {};

                const newDocRef = doc(queueRef);
                newDocId = newDocRef.id;
                serverScanPayload = {
                    url: allUrls[0],
                    complianceType,
                    tags: scanTags,
                    depth,
                    maxPages: depth === 'single' ? 1 : pageCap,
                    targetAgentId,
                    type: scanType,
                    includeRegex: scanIncludeRegex.trim() || null,
                    excludeRegex: scanExcludeRegex.trim() || null,
                    devices: selectedDevices,
                    includePrivacy,
                    status: 'pending',
                    distributed: depth !== 'single' && distributed,
                    createdAt: serverTimestamp(),
                    scheduledAt: scheduledAt ? new Date(scheduledAt) : serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    results: null,
                    discoveredUrls: null,
                    error: null,
                    archived: false,
                    ...journeyProps,
                    // New Scheduling & Failure Logic
                    isScheduled,
                    scheduleFrequency: isScheduled ? scheduleFrequency : null,
                    maxRetries,
                    failOnPageError,
                    retryCount: 0
                };
                queued = 1;
            }

            const createScan = httpsCallable(functions, 'createScan');
            const created = await createScan({ scan: serverScanPayload, requestedPages: newPages });
            newDocId = created.data.scanId;

            if (scanType === 'crawl' && newDocId) {
                setPendingCrawlId(newDocId);
            }

            setUrl('');
            setImportedUrls([]);
            setToast({ message: `${queued} scan${queued > 1 ? 's' : ''} queued successfully!`, type: 'success' });
        } catch (err) {
            console.error('Error queueing scan:', err);
            setError('Failed to queue scan. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteScheduledScan = async (scanId) => {
        if (!user) return;
        setConfirmState({
            isOpen: true,
            title: 'Delete Scheduled Scan',
            message: 'Are you sure you want to delete this future scheduled scan? This action cannot be undone.',
            onConfirm: async () => {
                try {
                    await deleteDoc(doc(db, 'users', user.uid, 'queue', scanId));
                    setToast({ message: 'Scheduled scan deleted successfully.', type: 'success' });
                } catch (err) {
                    console.error("Failed to delete scheduled scan:", err);
                    setToast({ message: 'Failed to delete scheduled scan.', type: 'error' });
                }
            }
        });
    };

    const handleRetryScan = async (scanId) => {
        try {
            await updateDoc(doc(db, 'users', user.uid, 'queue', scanId), {
                status: 'pending',
                error: null,
                updatedAt: serverTimestamp()
            });
            setToast({ message: 'Scan queued for retry.', type: 'info' });
        } catch (err) {
            console.error('Error retrying scan:', err);
            setToast({ message: 'Failed to retry scan.', type: 'error' });
        }
    };

    const handleCancelScan = (scanId, isProcessing = false) => {
        setConfirmState({
            isOpen: true,
            title: isProcessing ? 'Stop active scan' : 'Cancel Scan',
            message: isProcessing
                ? 'Are you sure you want to stop this active scan? It will be marked as failed.'
                : 'Are you sure you want to permanently delete this pending scan?',
            onConfirm: async () => {
                try {
                    if (isProcessing) {
                        await updateDoc(doc(db, 'users', user.uid, 'queue', scanId), {
                            status: 'failed',
                            error: 'Scan stopped by user',
                            updatedAt: serverTimestamp()
                        });
                        setToast({ message: 'Stopping scan...', type: 'info' });
                    } else {
                        await deleteDoc(doc(db, 'users', user.uid, 'queue', scanId));
                        setToast({ message: 'Scan cancelled.', type: 'info' });
                    }
                } catch (err) {
                    console.error('Error cancelling scan:', err);
                }
                setConfirmState(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleStopAllPending = () => {
        const pendingCount = scans.filter(s => s.status === 'pending').length;
        if (pendingCount === 0) return;

        setConfirmState({
            isOpen: true,
            title: 'Stop All Pending',
            message: `Are you sure you want to permanently delete all ${pendingCount} pending scans from the queue?`,
            onConfirm: async () => {
                try {
                    const pendingScans = scans.filter(s => s.status === 'pending');
                    const batch = [];
                    pendingScans.forEach(scan => {
                        batch.push(deleteDoc(doc(db, 'users', user.uid, 'queue', scan.id)));
                    });
                    await Promise.all(batch);
                    setToast({ message: `Cleared ${pendingScans.length} pending scans.`, type: 'info' });
                } catch (err) {
                    console.error('Error stopping all pending scans:', err);
                    setToast({ message: 'Failed to clear some pending scans.', type: 'error' });
                }
                setConfirmState(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleVerifyFixes = async (parentScan) => {
        try {
            const newPagesVerify = parentScan.maxPages || (parentScan.depth === 'single' ? 1 : 10);
            if (planLimits?.maxPagesPerMonth > 0 && (monthlyPagesScanned + newPagesVerify > planLimits.maxPagesPerMonth)) {
                setUpgradeModal({
                    isOpen: true,
                    title: 'Page Limit Reached',
                    message: `This verification scan requires ${newPagesVerify} pages, but you only have ${Math.max(0, planLimits.maxPagesPerMonth - monthlyPagesScanned)} pages remaining this month. Please upgrade your plan to scan more pages.`
                });
                return;
            }

            const scanPayload = {
                url: parentScan.url,
                displayUrl: parentScan.displayUrl || null,
                seedUrls: parentScan.seedUrls || null,
                complianceType: parentScan.complianceType || 'federal',
                tags: parentScan.tags || ['wcag2a', 'wcag2aa', 'wcag21a', 'wcag21aa'],
                depth: parentScan.depth || 'single',
                maxPages: newPagesVerify,
                targetAgentId: parentScan.targetAgentId || 'auto',
                type: parentScan.type || 'scan',
                includeRegex: parentScan.includeRegex || null,
                excludeRegex: parentScan.excludeRegex || null,
                devices: parentScan.devices || ['desktop'],
                distributed: parentScan.distributed || false,
                status: 'pending',
                viewed: false,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                parentScanId: parentScan.id, // Link to previous scan for diffing
                isVerification: true,
                journeyId: parentScan.journeyId || null,
                journeyName: parentScan.journeyName || null,
                archived: false,
                results: null,
                discoveredUrls: null,
                error: null
            };

            await httpsCallable(functions, 'createScan')({ scan: scanPayload, requestedPages: newPagesVerify });
            setToast({ message: 'Verification scan queued successfully!', type: 'success' });
            // Optional: scroll up to let them know it queued
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            console.error('Error queueing verification scan:', err);
        }
    };

    const handleArchiveScan = async (scanId, archiveState) => {
        try {
            await updateDoc(doc(db, 'users', user.uid, 'queue', scanId), {
                archived: archiveState,
                updatedAt: serverTimestamp(),
            });
        } catch (err) {
            console.error('Error archiving scan:', err);
        }
    };

    const handlePermanentDelete = (scanId) => {
        setDeleteConfirmScanId(scanId);
    };

    const confirmPermanentDelete = async (scanId) => {
        try {
            await deleteDoc(doc(db, 'users', user.uid, 'queue', scanId));
            setToast({ message: 'Scan permanently deleted.', type: 'info' });
        } catch (err) {
            console.error('Error deleting scan:', err);
        }
        setDeleteConfirmScanId(null);
    };

    const handleBulkDelete = async () => {
        if (selectedScans.size === 0) return;
        const confirmResult = window.confirm(`Permanently delete ${selectedScans.size} selected scan(s)? This cannot be undone.`);
        if (!confirmResult) return;

        try {
            for (const id of selectedScans) {
                await deleteDoc(doc(db, 'users', user.uid, 'queue', id));
            }
            setSelectedScans(new Set());
            setToast({ message: `Successfully deleted ${selectedScans.size} scan(s).`, type: 'info' });
        } catch (err) {
            console.error('Error bulk deleting scans:', err);
            setToast({ message: 'Error deleting some scans.', type: 'error' });
        }
    };

    const cancelDelete = () => {
        setDeleteConfirmScanId(null);
    };

    const handleReviewCrawl = (scan) => {
        setActiveCrawlScan(scan);
        setCrawlSelectedUrls(new Set(scan.discoveredUrls || []));
        setCrawlUrlFilter('');
        setCrawlUrlExclude('');
    };

    const handleSubmitCrawlSelections = async () => {
        if (!activeCrawlScan || crawlSelectedUrls.size === 0) return;
        setSubmitting(true);
        try {
            const selectedArray = Array.from(crawlSelectedUrls);
            const newPages = selectedArray.length;

            if (planLimits?.maxPagesPerMonth > 0 && (monthlyPagesScanned + newPages > planLimits.maxPagesPerMonth)) {
                setUpgradeModal({
                    isOpen: true,
                    title: 'Page Limit Reached',
                    message: `This scan requires ${newPages} pages, but you only have ${Math.max(0, planLimits.maxPagesPerMonth - monthlyPagesScanned)} pages remaining this month. Please upgrade your plan to scan more pages.`
                });
                setSubmitting(false);
                return;
            }

            const scanPayload = {
                url: activeCrawlScan.url, // Keep root for grouping
                displayUrl: `Batch Scan: ${selectedArray.length} pages`,
                seedUrls: selectedArray,
                complianceType: activeCrawlScan.complianceType,
                tags: activeCrawlScan.tags,
                depth: 'single',
                maxPages: newPages,
                targetAgentId: activeCrawlScan.targetAgentId,
                type: 'scan',
                status: 'pending',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                results: null,
                error: null,
                archived: false,
                isBatch: true,
                parentScanId: activeCrawlScan.id
            };

            // Mark the crawl itself as archived
            const crawlRef = doc(db, 'users', user.uid, 'queue', activeCrawlScan.id);
            await updateDoc(crawlRef, {
                archived: true,
                status: 'archived_crawl',
                updatedAt: serverTimestamp()
            });

            await httpsCallable(functions, 'createScan')({ scan: scanPayload, requestedPages: newPages });

            setToast({ message: `Queued single scan for all ${newPages} selected pages!`, type: 'success' });
            setActiveCrawlScan(null);
        } catch (err) {
            console.error('Failed to queue selected crawl urls', err);
            setToast({ message: 'Failed to queue all scans. See console.', type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveJourneyName = async (journeyId, newName) => {
        try {
            await updateDoc(doc(db, 'users', user.uid, 'journeys', journeyId), {
                name: newName,
                updatedAt: serverTimestamp()
            });
            setToast({ message: 'Journey name updated.', type: 'success' });
            setEditingJourneyId(null);
        } catch (err) {
            console.error('Failed to update journey name:', err);
            setToast({ message: 'Failed to update name.', type: 'error' });
        }
    };

    const toggleJurisdiction = (value) => {
        setSelectedJurisdictions((prev) =>
            prev.includes(value)
                ? prev.filter((j) => j !== value)
                : [...prev, value]
        );
    };

    const handleQuickSelectDate = (range) => {
        const end = new Date();
        let start = new Date();

        switch (range) {
            case '7d':
                start.setDate(end.getDate() - 7);
                break;
            case '14d':
                start.setDate(end.getDate() - 14);
                break;
            case '30d':
                start.setDate(end.getDate() - 30);
                break;
            case 'lastMonth': {
                start = new Date(end.getFullYear(), end.getMonth() - 1, 1);
                const lastMonthEnd = new Date(end.getFullYear(), end.getMonth(), 0);
                setReportForm(prev => ({
                    ...prev,
                    startDate: start.toISOString().split('T')[0],
                    endDate: lastMonthEnd.toISOString().split('T')[0]
                }));
                return;
            }
            case 'thisMonth':
                start = new Date(end.getFullYear(), end.getMonth(), 1);
                break;
            case 'all':
                setReportForm(prev => ({ ...prev, startDate: '', endDate: '' }));
                return;
            default:
                break;
        }

        setReportForm(prev => ({
            ...prev,
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0]
        }));
    };

    const handleGenerateReport = async () => {
        let reportName = reportForm.name;
        if (!reportName) {
            reportName = `ANALYTICS-${new Date().toISOString().split('T')[0]}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
        }

        setSubmitting(true);
        try {
            // 1. Initial filter based on status, domain, and date
            let qualifyingScans = scans.filter(s => s.status === 'completed' && s.url);

            if (reportForm.domain !== 'all') {
                qualifyingScans = qualifyingScans.filter(s => {
                    try { return new URL(s.url).hostname === reportForm.domain; }
                    catch { return false; }
                });
            }

            const startTime = reportForm.startDate ? new Date(reportForm.startDate).getTime() : 0;
            let endTime = Infinity;
            if (reportForm.endDate) {
                const end = new Date(reportForm.endDate);
                end.setUTCHours(23, 59, 59, 999);
                endTime = end.getTime();
            }

            if (startTime || endTime !== Infinity) {
                qualifyingScans = qualifyingScans.filter(s => {
                    const scanTime = (s.createdAt?.seconds || 0) * 1000;
                    return scanTime >= startTime && scanTime <= endTime;
                });
            }

            // 2. Aggregate latest per URL from the qualifying set
            const urlMap = new Map();
            qualifyingScans.forEach(scan => {
                const existing = urlMap.get(scan.url);
                if (!existing || (scan.createdAt?.seconds || 0) > (existing.createdAt?.seconds || 0)) {
                    urlMap.set(scan.url, scan);
                }
            });

            const filteredPages = Array.from(urlMap.values());

            if (filteredPages.length === 0) {
                setToast({ message: 'No scan data found for the selected criteria.', type: 'error' });
                setSubmitting(false);
                return;
            }

            // Aggregate data for the snapshot
            const summary = {
                riskScore: 0,
                totalViolations: 0,
                totalPages: filteredPages.length,
                totalPasses: 0,
                totalIncomplete: 0,
                totalAffectedNodes: 0,
                totalRiskCost: 0,
                bySeverity: { critical: 0, serious: 0, moderate: 0, minor: 0 }
            };

            const ruleStats = {};

            filteredPages.forEach(page => {
                const s = page.summary;
                if (!s) return;

                summary.riskScore += s.riskScore || 0;
                summary.totalViolations += s.totalViolations || 0;
                summary.totalPasses += s.totalPasses || 0;
                summary.totalIncomplete += s.totalIncomplete || 0;
                summary.totalAffectedNodes += s.totalAffectedNodes || 0;
                summary.totalRiskCost += s.totalRiskCost || 0;

                if (s.ruleStats) {
                    Object.entries(s.ruleStats).forEach(([ruleId, data]) => {
                        if (!ruleStats[ruleId]) {
                            ruleStats[ruleId] = {
                                impact: data.impact,
                                description: data.description || '',
                                helpUrl: data.helpUrl || '',
                                count: 0,
                                pagesCount: 0,
                                pages: {}
                            };
                        }
                        ruleStats[ruleId].count += data.count;
                        ruleStats[ruleId].pagesCount += 1;
                        ruleStats[ruleId].pages[page.url] = data.count;
                    });
                }
            });

            // Average the risk score and finalize summary stats
            summary.riskScore = filteredPages.length > 0 ? Math.round(summary.riskScore / filteredPages.length) : 0;
            summary.bySeverity = {
                critical: Object.values(ruleStats).filter(r => r.impact === 'critical').length,
                serious: Object.values(ruleStats).filter(r => r.impact === 'serious').length,
                moderate: Object.values(ruleStats).filter(r => r.impact === 'moderate').length,
                minor: Object.values(ruleStats).filter(r => r.impact === 'minor').length,
            };

            // Save to Firestore
            await addDoc(collection(db, 'users', user.uid, 'reports'), {
                name: reportName,
                criteria: {
                    domain: reportForm.domain,
                    startDate: reportForm.startDate || null,
                    endDate: reportForm.endDate || null
                },
                summary,
                ruleStats,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });

            setToast({ message: 'Success. Analytics protocol finalized.', type: 'success' });
            setIsGenerateReportModalOpen(false);
            setReportForm({ name: '', domain: 'all', startDate: '', endDate: '' });
        } catch (err) {
            console.error('Error generating report:', err);
            setToast({ message: 'Analytics failure. Check system logs.', type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleReportArchive = async (reportId, currentStatus) => {
        try {
            const reportRef = doc(db, 'users', user.uid, 'reports', reportId);
            await updateDoc(reportRef, {
                archived: !currentStatus,
                updatedAt: serverTimestamp()
            });
            setToast({
                message: currentStatus ? 'Report restored to active.' : 'Report moved to archive.',
                type: 'success'
            });
        } catch (err) {
            setToast({ message: 'Failed to update report status.', type: 'error' });
        }
    };

    const handleDownloadPDF = async (report) => {
        setDownloadingPDF(report.id);
        try {
            const md = '# Accessibility Audit Report: ' + report.name + '\n\n' +
                '**Domain:** ' + (report.criteria?.domain || 'All') + '\n' +
                '**Generated:** ' + (report.createdAt ? (report.createdAt.toDate ? report.createdAt.toDate().toLocaleString() : new Date(report.createdAt.seconds * 1000).toLocaleString()) : new Date().toLocaleString()) + '\n' +
                '**Total Pages Scanned:** ' + (report.summary?.totalPages || 0) + '\n\n' +
                '## Executive Summary\n' +
                '**Risk Score:** ' + (report.summary?.riskScore || 0) + '\n' +
                '**Total Violations:** ' + (report.summary?.totalViolations || 0) + '\n\n' +
                '### Breakdown by Severity\n' +
                '- Critical: ' + (report.summary?.bySeverity?.critical || 0) + '\n' +
                '- Serious: ' + (report.summary?.bySeverity?.serious || 0) + '\n' +
                '- Moderate: ' + (report.summary?.bySeverity?.moderate || 0) + '\n' +
                '- Minor: ' + (report.summary?.bySeverity?.minor || 0) + '\n\n' +
                '## Rule Violations Details\n' +
                Object.entries(report.ruleStats || {}).map(([ruleId, stats]) => '- **' + ruleId + '** (Impact: ' + stats.impact + '): ' + stats.count + ' issues across ' + stats.pagesCount + ' pages').join('\n') + '\n\n' +
                '## ⚖️ Global Data Privacy & Cookie Law Guidance\n' +
                '> **Important**: If your organization operates globally and processes personal data—including through website tracking and analytics—you must comply with international privacy laws like the EU\'s ePrivacy Directive (ePD), GDPR, and other global state laws.\n\n' +
                '**Key Compliance Steps:**\n' +
                '1. **Conduct a Cookie Audit**: Identify and categorize all trackers used on your site.\n' +
                '2. **Maintain Accessible Policies**: Ensure a clear Cookie Policy and Privacy Policy are available, linked to your consent banner.\n' +
                '3. **Implement a Compliant Choice Mechanism**: Provide clear options to accept or reject non-essential cookies. Do not block access with cookie walls.\n' +
                '4. **Document Consent Records**: Keep logs of users\' consent choices to demonstrate compliance.\n\n' +
                '*This guidance is based on best practices for major frameworks (such as Cookiebot guidelines) and is not intended as formal legal advice.*\n\n' +
                '---\n*Report generated via Accessibility Scout Enterprise*\n';

            const token = await auth.currentUser.getIdToken();

            const response = await fetch('https://us-central1-a11y-scanner-c052f.cloudfunctions.net/generatePDFReportAuth', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ markdown: md })
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error || `HTTP ${response.status}`);
            }

            const result = await response.json();
            if (result.success) {
                const linkSource = 'data:application/pdf;base64,' + result.pdfBase64;
                const downloadLink = document.createElement('a');
                downloadLink.href = linkSource;
                downloadLink.download = report.name.replace(/\s+/g, '_') + '_Report.pdf';
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
            } else {
                throw new Error('Failed to generate payload');
            }
        } catch (error) {
            console.error('[PDF-Error] Full error object:', error);

            // Extract code and message with extreme caution
            let errorCode = error?.code ||
                (error?.details?.status) ||
                (error?.status) ||
                'unknown';
            let errorMessage = error?.message || 'Check browser console for details.';

            // Specific handling for fetch/network/CORS errors which often show up as TypeErrors
            if (error instanceof TypeError || errorMessage.includes('fetch') || errorMessage.includes('NetworkError')) {
                errorCode = 'network-fail';
                errorMessage = 'CORS or Network Block. Ensure the new "generatePDFReportAuth" function is deployed.';
            }

            console.error(`[PDF-Error] Normalized Code: ${errorCode}, Message: ${errorMessage}`);

            if (errorCode === 'permission-denied' || errorCode === 'unauthenticated') {
                setToast({ message: 'Access denied. Please re-login or check permissions.', type: 'error' });
            } else if (errorCode === 'internal' && errorMessage.includes('CORS')) {
                setToast({ message: 'CORS Block. Ensure Functions are deployed with public access.', type: 'error' });
            } else {
                setToast({ message: `Download failed: ${errorMessage.substring(0, 50)}...`, type: 'error' });
            }
        } finally {
            setDownloadingPDF(null);
        }
    };

    const handleSignOut = async () => {
        await signOut(auth);
    };

    const handleExportAnalytics = useCallback(async (format = 'png') => {
        if (!analyticsRef.current) return;
        try {
            setToast({ message: 'Generating export...', type: 'success' });
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(analyticsRef.current, {
                backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--surface').trim() || '#111',
                scale: 2,
                useCORS: true,
            });
            if (format === 'png') {
                const link = document.createElement('a');
                link.download = `analytics-${new Date().toISOString().split('T')[0]}.png`;
                link.href = canvas.toDataURL('image/png');
                link.click();
                setToast({ message: 'PNG exported successfully.', type: 'success' });
            } else {
                // PDF via print
                const win = window.open('', '_blank');
                win.document.write('<html><head><title>Analytics Export</title><style>body{margin:0;display:flex;justify-content:center;background:#111;}img{max-width:100%;height:auto;}</style></head><body><img src="' + canvas.toDataURL('image/png') + '" /><script>setTimeout(function(){window.print();},500);</' + 'script></body></html>');
                win.document.close();
                setToast({ message: 'Print dialog opened.', type: 'success' });
            }
        } catch (err) {
            console.error('Export failed:', err);
            setToast({ message: 'Export failed. Check console.', type: 'error' });
        }
    }, []);


    // Extract unique domains for the domain filter (only from active or archived based on viewMode)
    const uniqueDomains = [...new Set(
        scans
            .filter(s => (viewMode === 'archived' ? !!s.archived : !s.archived))
            .map(s => {
                try { return new URL(s.url).hostname; } catch { return 'unknown'; }
            })
    )].sort();

    const allUniqueDomains = [...new Set(
        scans.map(s => {
            try { return new URL(s.url).hostname; } catch { return 'unknown'; }
        })
    )].sort();

    // Compute Audit list — every unique URL ever scanned, with its latest status
    const auditScans = [];
    if (viewMode === 'audit') {
        const urlMap = new Map();
        scans.forEach(scan => {
            const url = scan.url;
            const existing = urlMap.get(url);
            if (!existing || (scan.createdAt?.seconds || 0) > (existing.createdAt?.seconds || 0)) {
                urlMap.set(url, scan);
            }
        });
        auditScans.push(...Array.from(urlMap.values()).sort((a, b) => {
            const timeA = a.createdAt?.seconds || 0;
            const timeB = b.createdAt?.seconds || 0;
            return scansSort.direction === 'desc' ? timeB - timeA : timeA - timeB;
        }));
    }

    // Compute filtered and sorted scans
    const filteredScans = scans
        .filter(s => {
            // View Mode Match (Active vs Archived vs Scheduled)
            const isArchived = !!s.archived;
            const now = new Date();
            const isFuture = s.status === 'pending' && s.scheduledAt && (s.scheduledAt.seconds * 1000 > now.getTime());

            if (viewMode === 'active') {
                if (isArchived || isFuture) return false;
            }
            if (viewMode === 'archived' && !isArchived) return false;
            if (viewMode === 'scheduled' && !isFuture) return false;

            // Search Match
            if (searchQuery && !s.url.toLowerCase().includes(searchQuery.toLowerCase())) return false;

            // Status Match
            if (statusFilter !== 'all' && s.status !== statusFilter) return false;

            // Hide HA sub-scans (scans with a parentScanId) – always hide them from primary view
            // BUT show if it is an explicit verification scan or a batch scan from crawl
            if ((s.parentScanId && !s.isVerification && !s.isBatch) || s.isDelegated) return false;

            // Domain Match
            if (domainFilter !== 'all') {
                try {
                    if (new URL(s.url).hostname !== domainFilter) return false;
                } catch {
                    return false;
                }
            }

            return true;
        })
        .sort((a, b) => {
            if (scansSort.key === 'date') {
                const timeA = a.createdAt?.seconds || 0;
                const timeB = b.createdAt?.seconds || 0;
                return scansSort.direction === 'desc' ? timeB - timeA : timeA - timeB;
            }
            if (scansSort.key === 'riskScore') {
                const scoreA = a.summary?.riskScore || 0;
                const scoreB = b.summary?.riskScore || 0;
                return scansSort.direction === 'desc' ? scoreB - scoreA : scoreA - scoreB;
            }
            if (scansSort.key === 'status') {
                const statusA = a.status || '';
                const statusB = b.status || '';
                return scansSort.direction === 'desc' ? statusB.localeCompare(statusA) : statusA.localeCompare(statusB);
            }
            return 0;
        });

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
        setSelectedScans(new Set());
    }, [viewMode, searchQuery, statusFilter, domainFilter, scansSort, scansPerPage]);

    // Apply pagination slice
    const paginatedScans = useMemo(() => {
        if (scansPerPage === 'All') return filteredScans;
        const startIdx = (currentPage - 1) * scansPerPage;
        return filteredScans.slice(startIdx, startIdx + scansPerPage);
    }, [filteredScans, currentPage, scansPerPage]);

    const groupedScans = useMemo(() => {
        if (!groupByDomain) return null;
        const groups = {};
        paginatedScans.forEach(scan => {
            let domain = 'unknown';
            try { domain = new URL(scan.url).hostname; } catch (e) { }
            if (!groups[domain]) groups[domain] = [];
            groups[domain].push(scan);
        });
        return groups;
    }, [paginatedScans, groupByDomain]);

    // --- NEW: Pages Data Aggregation ---
    const pagesData = useMemo(() => {
        // Extract ALL unique page URLs from scan results (not just entry URLs)
        const pageStats = new Map(); // URL -> { totalViolations, bySeverity, riskScore, scanId, createdAt }

        scans.forEach(scan => {
            if (scan.status !== 'completed' || !scan.url) return;

            // Filter by view mode
            if (viewMode === 'active' && !!scan.archived) return;
            if (viewMode === 'archived' && !scan.archived) return;

            const summary = scan.summary;

            // Try to extract page-level data from ruleStats.pages (new agent format)
            let pagesFromRuleStats = new Set();
            if (summary?.ruleStats) {
                Object.values(summary.ruleStats).forEach(rule => {
                    if (rule.pages && typeof rule.pages === 'object') {
                        Object.keys(rule.pages).forEach(url => pagesFromRuleStats.add(url));
                    }
                });
            }

            // If we have page-level data from ruleStats, use it
            if (pagesFromRuleStats.size > 0) {
                pagesFromRuleStats.forEach(pageUrl => {
                    // Compute stats for this page from ruleStats
                    let totalViolations = 0;
                    const bySeverity = { critical: 0, serious: 0, moderate: 0, minor: 0 };

                    Object.values(summary.ruleStats).forEach(rule => {
                        const count = rule.pages?.[pageUrl] || 0;
                        if (count > 0) {
                            totalViolations += count;
                            if (rule.impact && Object.prototype.hasOwnProperty.call(bySeverity, rule.impact)) {
                                bySeverity[rule.impact] += count;
                            }
                        }
                    });

                    const riskScore = Math.min(100, bySeverity.critical * 10 + bySeverity.serious * 5 + bySeverity.moderate * 2 + bySeverity.minor);
                    const totalRiskCost = bySeverity.critical * VIOLATION_COSTS.critical + bySeverity.serious * VIOLATION_COSTS.serious + bySeverity.moderate * VIOLATION_COSTS.moderate + bySeverity.minor * VIOLATION_COSTS.minor;

                    const existing = pageStats.get(pageUrl);
                    if (!existing || (scan.createdAt?.seconds || 0) > (existing.createdAt?.seconds || 0)) {
                        pageStats.set(pageUrl, {
                            url: pageUrl,
                            totalViolations,
                            bySeverity,
                            riskScore,
                            scanId: scan.id,
                            createdAt: scan.createdAt,
                            scanUrl: scan.url, // Entry point URL for reference
                            summary: { totalViolations, bySeverity, riskScore, totalRiskCost }
                        });
                    }
                });
            }
            // Fallback: Parse results to extract page URLs (for old scans or non-ruleStats format)
            else if (scan.results && !scan.hasChunks) {
                try {
                    let resultsData = scan.results;
                    if (typeof resultsData === 'string' && scan.dataIsCompressed) {
                        resultsData = JSON.parse(LZString.decompressFromUTF16(resultsData));
                    }

                    if (resultsData?.violations) {
                        // Extract all unique pages from violation nodes
                        const pageViolations = {}; // URL -> { total, bySeverity }

                        resultsData.violations.forEach(v => {
                            const impact = v.impact || 'minor';
                            (v.nodes || []).forEach(node => {
                                const url = node.pageUrl || scan.url;
                                if (!pageViolations[url]) {
                                    pageViolations[url] = { total: 0, bySeverity: { critical: 0, serious: 0, moderate: 0, minor: 0 } };
                                }
                                pageViolations[url].total += 1;
                                if (Object.prototype.hasOwnProperty.call(pageViolations[url].bySeverity, impact)) {
                                    pageViolations[url].bySeverity[impact] += 1;
                                }
                            });

                            // If no pageUrl in nodes, check violation.pageUrls
                            if ((v.nodes || []).every(n => !n.pageUrl) && v.pageUrls) {
                                v.pageUrls.forEach(url => {
                                    if (!pageViolations[url]) {
                                        pageViolations[url] = { total: 0, bySeverity: { critical: 0, serious: 0, moderate: 0, minor: 0 } };
                                    }
                                    const countPerPage = Math.ceil((v.nodes?.length || 1) / v.pageUrls.length);
                                    pageViolations[url].total += countPerPage;
                                    if (Object.prototype.hasOwnProperty.call(pageViolations[url].bySeverity, impact)) {
                                        pageViolations[url].bySeverity[impact] += countPerPage;
                                    }
                                });
                            }
                        });

                        // Add each extracted page to pageStats
                        Object.entries(pageViolations).forEach(([pageUrl, stats]) => {
                            const riskScore = Math.min(100, stats.bySeverity.critical * 10 + stats.bySeverity.serious * 5 + stats.bySeverity.moderate * 2 + stats.bySeverity.minor);
                            const existing = pageStats.get(pageUrl);
                            if (!existing || (scan.createdAt?.seconds || 0) > (existing.createdAt?.seconds || 0)) {
                                pageStats.set(pageUrl, {
                                    url: pageUrl,
                                    totalViolations: stats.total,
                                    bySeverity: stats.bySeverity,
                                    riskScore,
                                    scanId: scan.id,
                                    createdAt: scan.createdAt,
                                    scanUrl: scan.url,
                                    summary: { totalViolations: stats.total, bySeverity: stats.bySeverity, riskScore }
                                });
                            }
                        });
                    }
                } catch (e) {
                    console.warn('[Pages] Failed to parse results for page extraction:', e);
                    // Fallback to entry URL only
                    const existing = pageStats.get(scan.url);
                    if (!existing || (scan.createdAt?.seconds || 0) > (existing.createdAt?.seconds || 0)) {
                        pageStats.set(scan.url, {
                            url: scan.url,
                            totalViolations: summary?.totalViolations || 0,
                            bySeverity: summary?.bySeverity || { critical: 0, serious: 0, moderate: 0, minor: 0 },
                            riskScore: summary?.riskScore || 0,
                            scanId: scan.id,
                            id: scan.id, // For compatibility
                            createdAt: scan.createdAt,
                            summary: summary || {}
                        });
                    }
                }
            }
            // Final fallback: Just use scan.url with summary stats
            else {
                const existing = pageStats.get(scan.url);
                if (!existing || (scan.createdAt?.seconds || 0) > (existing.createdAt?.seconds || 0)) {
                    pageStats.set(scan.url, {
                        url: scan.url,
                        totalViolations: summary?.totalViolations || 0,
                        bySeverity: summary?.bySeverity || { critical: 0, serious: 0, moderate: 0, minor: 0 },
                        riskScore: summary?.riskScore || 0,
                        scanId: scan.id,
                        id: scan.id,
                        createdAt: scan.createdAt,
                        summary: summary || {}
                    });
                }
            }
        });

        let result = Array.from(pageStats.values());

        // Apply Search Filter
        if (pagesSearchQuery) {
            const q = pagesSearchQuery.toLowerCase();
            result = result.filter(p => p.url.toLowerCase().includes(q));
        }

        // Apply Severity Filter
        if (pagesSeverityFilter !== 'all') {
            result = result.filter(p => {
                const bySeverity = p.bySeverity || p.summary?.bySeverity;
                if (!bySeverity) return false;
                if (pagesSeverityFilter === 'critical') return bySeverity.critical > 0;
                if (pagesSeverityFilter === 'serious') return bySeverity.serious > 0 || bySeverity.critical > 0;
                return true;
            });
        }

        // Apply Compliance Node (Site) Filter
        if (pagesSiteFilter !== 'all') {
            result = result.filter(p => {
                try { return new URL(p.url).hostname === pagesSiteFilter; } catch { return false; }
            });
        }

        return result.sort((a, b) => {
            let valA, valB;
            if (pagesSort.key === 'lastScan') {
                valA = a.createdAt?.seconds || 0;
                valB = b.createdAt?.seconds || 0;
            } else if (pagesSort.key === 'riskScore') {
                valA = a.riskScore || a.summary?.riskScore || 0;
                valB = b.riskScore || b.summary?.riskScore || 0;
            } else if (pagesSort.key === 'totalViolations') {
                valA = a.totalViolations || a.summary?.totalViolations || 0;
                valB = b.totalViolations || b.summary?.totalViolations || 0;
            } else if (pagesSort.key === 'url') {
                valA = a.url || '';
                valB = b.url || '';
                return pagesSort.direction === 'desc' ? valB.localeCompare(valA) : valA.localeCompare(valB);
            } else if (pagesSort.key === 'hostname') {
                try { valA = new URL(a.url).hostname; } catch { valA = a.url || ''; }
                try { valB = new URL(b.url).hostname; } catch { valB = b.url || ''; }
                return pagesSort.direction === 'desc' ? valB.localeCompare(valA) : valA.localeCompare(valB);
            }

            return pagesSort.direction === 'desc' ? valB - valA : valA - valB;
        });
    }, [scans, pagesSearchQuery, pagesSeverityFilter, pagesSiteFilter, pagesSort, viewMode]);

    // Reset pages pagination when filters change
    useEffect(() => {
        setPagesCurrentPage(1);
    }, [pagesSearchQuery, pagesSeverityFilter, pagesSiteFilter, pagesSort, viewMode, pagesPerPage]);

    // Apply pages pagination slice
    const paginatedPages = useMemo(() => {
        if (pagesPerPage === 'All') return pagesData;
        const startIdx = (pagesCurrentPage - 1) * pagesPerPage;
        return pagesData.slice(startIdx, startIdx + pagesPerPage);
    }, [pagesData, pagesCurrentPage, pagesPerPage]);

    // --- NEW: Global Issues Data Aggregation ---
    const issuesData = useMemo(() => {
        const allRules = {}; // RuleID -> { impact, totalCount, pages: {} }
        const affectedPages = new Set();

        // Process each scan to aggregate all rules
        scans.forEach(scan => {
            if (scan.status !== 'completed') return;

            // Filter by view mode
            if (viewMode === 'active' && !!scan.archived) return;
            if (viewMode === 'archived' && !scan.archived) return;

            const summary = scan.summary;

            // First try: Use ruleStats.pages if populated (new agent format)
            if (summary?.ruleStats) {
                let hasPageLevelData = false;

                Object.entries(summary.ruleStats).forEach(([ruleId, data]) => {
                    if (!allRules[ruleId]) {
                        allRules[ruleId] = {
                            impact: data.impact,
                            description: data.description || '',
                            helpUrl: data.helpUrl || '',
                            tags: data.tags || [],
                            totalCount: 0,
                            pages: {} // URL -> count
                        };
                    }
                    allRules[ruleId].totalCount += data.count;

                    // Check if page-level data exists
                    if (data.pages && Object.keys(data.pages).length > 0) {
                        hasPageLevelData = true;
                        Object.entries(data.pages).forEach(([url, count]) => {
                            allRules[ruleId].pages[url] = (allRules[ruleId].pages[url] || 0) + count;
                            affectedPages.add(url);
                        });
                    }

                    // Preserve best metadata
                    if (data.description && !allRules[ruleId].description) {
                        allRules[ruleId].description = data.description;
                    }
                    if (data.helpUrl && !allRules[ruleId].helpUrl) {
                        allRules[ruleId].helpUrl = data.helpUrl;
                    }
                    if (data.tags && data.tags.length > 0 && allRules[ruleId].tags.length === 0) {
                        allRules[ruleId].tags = data.tags;
                    }
                });

                // Fallback: Parse violations from results if no page-level data and results available
                if (!hasPageLevelData && scan.results && !scan.hasChunks) {
                    try {
                        let resultsData = scan.results;
                        if (typeof resultsData === 'string' && scan.dataIsCompressed) {
                            resultsData = JSON.parse(LZString.decompressFromUTF16(resultsData));
                        }

                        // Agent 1.0.4+ wraps single pages in an array: [{ url, results: { violations: [] } }]
                        let targetViolations = resultsData?.violations;
                        if (!targetViolations && Array.isArray(resultsData) && resultsData.length > 0) {
                            targetViolations = resultsData[0].results?.violations;
                        }

                        if (targetViolations) {
                            targetViolations.forEach(v => {
                                const ruleId = v.id;
                                if (!allRules[ruleId]) return; // Skip if not in ruleStats

                                // Fallback tag parsing for legacy scans without node extraction
                                if (v.tags && v.tags.length > 0 && allRules[ruleId].tags.length === 0) {
                                    allRules[ruleId].tags = v.tags;
                                }

                                // Extract page-level data from nodes
                                const pageNodeCounts = {};
                                (v.nodes || []).forEach(node => {
                                    const url = node.pageUrl || scan.url;
                                    pageNodeCounts[url] = (pageNodeCounts[url] || 0) + 1;
                                    affectedPages.add(url);
                                });

                                // If no pageUrl in nodes, use violation.pageUrls
                                if (Object.keys(pageNodeCounts).length === 0 && v.pageUrls) {
                                    v.pageUrls.forEach(url => {
                                        pageNodeCounts[url] = Math.ceil((v.nodes?.length || v.totalNodes || 1) / v.pageUrls.length);
                                        affectedPages.add(url);
                                    });
                                }

                                // Fallback to scan URL
                                if (Object.keys(pageNodeCounts).length === 0) {
                                    pageNodeCounts[scan.url] = v.nodes?.length || v.totalNodes || 1;
                                    affectedPages.add(scan.url);
                                }

                                Object.entries(pageNodeCounts).forEach(([url, count]) => {
                                    allRules[ruleId].pages[url] = (allRules[ruleId].pages[url] || 0) + count;
                                });
                            });
                        }
                    } catch (e) {
                        console.warn('[Issues] Failed to parse results for page-level data:', e);
                        // Final fallback: use scan URL
                        Object.keys(summary.ruleStats).forEach(ruleId => {
                            if (allRules[ruleId]) {
                                allRules[ruleId].pages[scan.url] = (allRules[ruleId].pages[scan.url] || 0) + summary.ruleStats[ruleId].count;
                                affectedPages.add(scan.url);
                            }
                        });
                    }
                } else if (!hasPageLevelData) {
                    // No page-level data extracted (chunked or no results), use scan URL as fallback
                    Object.entries(summary.ruleStats).forEach(([ruleId, data]) => {
                        if (allRules[ruleId]) {
                            allRules[ruleId].pages[scan.url] = (allRules[ruleId].pages[scan.url] || 0) + data.count;
                            affectedPages.add(scan.url);
                        }
                    });
                }
            } else if (scan.results || scan.hasChunks) {
                // Fallback for scans WITHOUT ruleStats: parse violations from results directly
                try {
                    let resultsData = scan.results;
                    if (!resultsData) return; // Chunked without inline results — cannot parse here

                    if (typeof resultsData === 'string' && scan.dataIsCompressed) {
                        resultsData = JSON.parse(LZString.decompressFromUTF16(resultsData));
                    }

                    // Support both flat { violations: [] } and array [{ url, results: { violations: [] } }]
                    let targetViolations = resultsData?.violations;
                    const pageResults = [];
                    if (Array.isArray(resultsData)) {
                        resultsData.forEach(entry => {
                            const v = entry.results?.violations || entry.violations;
                            if (v) pageResults.push({ url: entry.url || scan.url, violations: v });
                        });
                    } else if (targetViolations) {
                        pageResults.push({ url: scan.url, violations: targetViolations });
                    }

                    pageResults.forEach(({ url: pageUrl, violations }) => {
                        violations.forEach(v => {
                            const ruleId = v.id;
                            if (!ruleId) return;
                            const nodeCount = v.nodes?.length || v.totalNodes || 1;

                            if (!allRules[ruleId]) {
                                allRules[ruleId] = {
                                    impact: v.impact || 'minor',
                                    description: v.description || v.help || '',
                                    helpUrl: v.helpUrl || '',
                                    tags: v.tags || [],
                                    totalCount: 0,
                                    pages: {}
                                };
                            }
                            allRules[ruleId].totalCount += nodeCount;
                            allRules[ruleId].pages[pageUrl] = (allRules[ruleId].pages[pageUrl] || 0) + nodeCount;
                            affectedPages.add(pageUrl);

                            // Preserve metadata
                            if (v.tags && v.tags.length > 0 && allRules[ruleId].tags.length === 0) {
                                allRules[ruleId].tags = v.tags;
                            }
                            if (v.description && !allRules[ruleId].description) {
                                allRules[ruleId].description = v.description;
                            }
                            if (v.helpUrl && !allRules[ruleId].helpUrl) {
                                allRules[ruleId].helpUrl = v.helpUrl;
                            }
                        });
                    });
                } catch (e) {
                    console.warn('[Issues] Fallback results parsing failed:', e);
                }
            }
        });

        // Apply deep filters to allRules and aggregate global metrics
        let critical = 0, serious = 0, moderate = 0, minor = 0, totalInstances = 0;
        const filteredPages = new Set();
        const searchLower = issuesSearchQuery.toLowerCase();

        // Compute raw affectedSites just for the dropdown filter options (must not be filtered)
        const affectedSites = new Set();
        affectedPages.forEach(url => {
            try { affectedSites.add(new URL(url).hostname); } catch { }
        });
        // Also include sites from scan URLs directly (mirrors Analytics logic)
        scans.forEach(scan => {
            if (scan.status === 'completed' && scan.url && (!scan.archived || viewMode !== 'active')) {
                if (viewMode === 'archived' && !scan.archived) return;
                try { affectedSites.add(new URL(scan.url).hostname); } catch { }
            }
        });

        const filteredRules = Object.entries(allRules).reduce((acc, [ruleId, data]) => {
            // 1. Apply Severity Filter
            if (issuesSeverityFilter !== 'all' && data.impact !== issuesSeverityFilter) return acc;

            // 2. Apply Search Filter
            if (searchLower) {
                const ruleText = ruleId.replace(/-/g, ' ').toLowerCase();
                const descText = (data.description || '').toLowerCase();
                if (!ruleText.includes(searchLower) && !descText.includes(searchLower)) return acc;
            }

            // 3. Apply Deep Site and Page Filters to page map
            let filteredTotalCount = 0;
            const originalPages = data.pages || {};
            const finalPages = {};

            Object.entries(originalPages).forEach(([url, count]) => {
                // Page Filter (Array)
                if (issuesPageFilter.length > 0 && !issuesPageFilter.includes(url)) return;

                // Site Filter (Array)
                if (issuesSiteFilter.length > 0) {
                    try {
                        const hostname = new URL(url).hostname;
                        if (!issuesSiteFilter.includes(hostname)) return;
                    } catch { return; }
                }

                finalPages[url] = count;
                filteredTotalCount += count;
            });

            // If the rule has no occurrences after deep filtering, drop it entirely
            if (filteredTotalCount === 0) return acc;

            // 4. Record the surviving subset
            const finalRule = {
                ...data,
                totalCount: filteredTotalCount,
                pages: finalPages
            };

            // Recompute global KPIs
            totalInstances += filteredTotalCount;
            switch (finalRule.impact) {
                case 'critical': critical += filteredTotalCount; break;
                case 'serious': serious += filteredTotalCount; break;
                case 'moderate': moderate += filteredTotalCount; break;
                case 'minor': minor += filteredTotalCount; break;
            }
            Object.keys(finalPages).forEach(url => filteredPages.add(url));

            acc.push([ruleId, finalRule]);
            return acc;
        }, []);

        // Sort filtered rules
        const sortedRules = {};
        filteredRules
            .sort((a, b) => {
                const ruleA = a[0];
                const dataA = a[1];
                const ruleB = b[0];
                const dataB = b[1];

                let valA, valB;
                if (issuesSort.key === 'totalCount') {
                    valA = dataA.totalCount;
                    valB = dataB.totalCount;
                } else if (issuesSort.key === 'impact') {
                    const impactWeight = { 'critical': 4, 'serious': 3, 'moderate': 2, 'minor': 1 };
                    valA = impactWeight[dataA.impact] || 0;
                    valB = impactWeight[dataB.impact] || 0;
                } else if (issuesSort.key === 'ruleId') {
                    valA = ruleA;
                    valB = ruleB;
                    return issuesSort.direction === 'desc' ? valB.localeCompare(valA) : valA.localeCompare(valB);
                } else if (issuesSort.key === 'sites') {
                    valA = new Set(Object.keys(dataA.pages).map(u => { try { return new URL(u).hostname; } catch { return u; } })).size;
                    valB = new Set(Object.keys(dataB.pages).map(u => { try { return new URL(u).hostname; } catch { return u; } })).size;
                } else if (issuesSort.key === 'pages') {
                    valA = Object.keys(dataA.pages).length;
                    valB = Object.keys(dataB.pages).length;
                }

                return issuesSort.direction === 'desc' ? valB - valA : valA - valB;
            })
            .forEach(([id, data]) => {
                sortedRules[id] = data;
            });

        const avgWeightedImpact = (critical * 10 + serious * 5 + moderate * 2 + minor * 1) / (filteredPages.size || 1);
        const riskScore = Math.min(100, Math.round(100 * (1 - Math.exp(-avgWeightedImpact / 30))));
        const { grade, pass } = getGradeFromRisk(riskScore);

        return {
            critical,
            serious,
            moderate,
            minor,
            totalInstances,
            totalRules: Object.keys(sortedRules).length,
            affectedPages: filteredPages,
            affectedSites, // Preserved unfiltered for dropdown menus
            rules: sortedRules,
            riskScore,
            grade,
            pass
        };
    }, [scans, viewMode, issuesSort, issuesSearchQuery, issuesSeverityFilter, issuesPageFilter, issuesSiteFilter]);

    // --- Analytics Dashboard Data ---

    // Memoize the extraction and parsing of expanded scan data to prevent massive
    // main thread freezes every 15s (when agent count tick triggers app rerender).
    const activeExpandedScanData = useMemo(() => {
        if (!expandedScan || !user) return null;
        const scan = scans.find(s => s.id === expandedScan);
        if (!scan) return null;

        const raw = scan.results || hydratedResults[scan.id];
        if (!raw) return null;

        if (typeof raw === 'string' && scan.dataIsCompressed) {
            try {
                return JSON.parse(LZString.decompressFromUTF16(raw));
            } catch (e) {
                console.error("Failed to decompress inline results", e);
                return null;
            }
        }
        return raw;
    }, [expandedScan, scans, user, hydratedResults]);

    const analyticsData = useMemo(() => {
        // Calculate date range filter
        const now = new Date();
        let startDate = null;
        let endDate = now;

        if (analyticsTimeRange === '7d') {
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        } else if (analyticsTimeRange === '30d') {
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        } else if (analyticsTimeRange === '90d') {
            startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        } else if (analyticsTimeRange === 'custom' && analyticsCustomStart) {
            startDate = new Date(analyticsCustomStart);
            if (analyticsCustomEnd) endDate = new Date(analyticsCustomEnd);
        }
        // 'all' means no date filter

        const filteredScans = scans.filter(scan => {
            if (scan.status !== 'completed') return false;
            if (scan.archived) return false; // Only active scans

            // Date filter
            if (startDate) {
                const scanDate = scan.createdAt?.seconds ? new Date(scan.createdAt.seconds * 1000) : null;
                if (!scanDate || scanDate < startDate || scanDate > endDate) return false;
            }

            // Site filter
            if (analyticsSiteFilter !== 'all') {
                try {
                    const hostname = new URL(scan.url).hostname;
                    if (hostname !== analyticsSiteFilter) return false;
                } catch { return false; }
            }

            return true;
        });

        // Get unique sites - include archived scans for the site list
        const uniqueSites = new Set();
        scans.forEach(scan => {
            if (scan.status === 'completed' && scan.url) {
                try { uniqueSites.add(new URL(scan.url).hostname); } catch { }
            }
        });

        // Calculate metrics
        let totalViolations = 0;
        let totalRiskCost = 0;
        const bySeverity = { critical: 0, serious: 0, moderate: 0, minor: 0 };
        const nodeBySeverity = { critical: 0, serious: 0, moderate: 0, minor: 0 };
        const byRule = {}; // ruleId -> count
        const byDate = {}; // YYYY-MM-DD -> { scans, violations, risk }
        const bySite = {}; // hostname -> { scans, violations, risk, pages, nodeBySeverity }
        const uniquePages = new Set();

        // Deduplicate results by URL: only take the LATEST scan for each unique URL
        // across the entire filtered scan list. This implements the "Merge/Update/Deduplicate" logic.
        const latestScanMap = new Map();
        [...filteredScans].forEach(scan => {
            if (scan.status === 'completed' && scan.url) {
                const existing = latestScanMap.get(scan.url);
                if (!existing || (scan.createdAt?.seconds || 0) > (existing.createdAt?.seconds || 0)) {
                    latestScanMap.set(scan.url, scan);
                }
            }
        });

        const deduplicatedScans = Array.from(latestScanMap.values());

        deduplicatedScans.forEach(scan => {
            let summary = scan.summary;
            
            // Fallback: If summary is missing, thin (no node data), or empty, try to derive it from scan.results
            const isThinSummary = !summary || 
                (summary.totalViolations > 0 && (!summary.nodeCountsBySeverity || Object.keys(summary.nodeCountsBySeverity).length === 0));

            if (isThinSummary && (scan.results || hydratedResults[scan.id])) {
                const raw = scan.results || hydratedResults[scan.id];
                let results = null;
                if (typeof raw === 'string' && scan.dataIsCompressed) {
                    try {
                        results = JSON.parse(LZString.decompressFromUTF16(raw));
                    } catch (e) {
                        console.error("[Dashboard] Failed to decompress fallback results", e);
                    }
                } else {
                    results = raw;
                }

                if (results) {
                    // Simple on-the-fly summary calculation for analytics
                    const violations = results.violations || [];
                    const bySev = { critical: 0, serious: 0, moderate: 0, minor: 0 };
                    const nodeBySev = { critical: 0, serious: 0, moderate: 0, minor: 0 };
                    let totalRisk = 0;
                    
                    violations.forEach(v => {
                        const imp = v.impact || 'minor';
                        const n = v.nodes?.length || 0;
                        bySev[imp]++;
                        nodeBySev[imp] += n;
                        totalRisk += (VIOLATION_COSTS[imp] || 0) * n;
                    });

                    summary = {
                        totalViolations: violations.length,
                        totalRiskCost: totalRisk,
                        bySeverity: bySev,
                        nodeCountsBySeverity: nodeBySev,
                        ruleStats: results.ruleStats // Might be missing but that's okay
                    };
                }
            }

            if (!summary) return;

            totalViolations += summary.totalViolations || 0;
            totalRiskCost += summary.totalRiskCost || 0;

            // Severity breakdown
            if (summary.bySeverity) {
                bySeverity.critical += summary.bySeverity.critical || 0;
                bySeverity.serious += summary.bySeverity.serious || 0;
                bySeverity.moderate += summary.bySeverity.moderate || 0;
                bySeverity.minor += summary.bySeverity.minor || 0;
            }

            // Node counts by severity (for more accurate scoring)
            const hasNodeData = summary.nodeCountsBySeverity &&
                (summary.nodeCountsBySeverity.critical || summary.nodeCountsBySeverity.serious ||
                    summary.nodeCountsBySeverity.moderate || summary.nodeCountsBySeverity.minor);

            if (hasNodeData) {
                nodeBySeverity.critical += summary.nodeCountsBySeverity.critical || 0;
                nodeBySeverity.serious += summary.nodeCountsBySeverity.serious || 0;
                nodeBySeverity.moderate += summary.nodeCountsBySeverity.moderate || 0;
                nodeBySeverity.minor += summary.nodeCountsBySeverity.minor || 0;
            } else if (summary.ruleStats) {
                // Secondary fallback: Extract node counts from ruleStats
                Object.values(summary.ruleStats).forEach(data => {
                    const impact = data.impact || 'minor';
                    if (nodeBySeverity[impact] !== undefined) {
                        nodeBySeverity[impact] += data.count || 0;
                    }
                });
            } else {
                // Final fallback: Use base violation counts
                nodeBySeverity.critical += (summary.bySeverity?.critical || 0) * 4;
                nodeBySeverity.serious += (summary.bySeverity?.serious || 0) * 3;
                nodeBySeverity.moderate += (summary.bySeverity?.moderate || 0) * 2;
                nodeBySeverity.minor += (summary.bySeverity?.minor || 0) * 1;
            }

            // Track unique pages scanned (even with zero violations)
            uniquePages.add(scan.url);
            if (scan.seedUrls) {
                scan.seedUrls.forEach(u => uniquePages.add(u));
            }

            // Rule breakdown and unique page extraction from violations
            if (summary.ruleStats) {
                Object.entries(summary.ruleStats).forEach(([ruleId, data]) => {
                    if (!byRule[ruleId]) byRule[ruleId] = { count: 0, impact: data.impact, description: data.description };
                    byRule[ruleId].count += data.count || 0;

                    if (data.pages) {
                        Object.keys(data.pages).forEach(url => uniquePages.add(url));
                    }
                });
            }

            // By date
            const scanDate = scan.createdAt?.seconds ? new Date(scan.createdAt.seconds * 1000).toISOString().split('T')[0] : null;
            if (scanDate) {
                if (!byDate[scanDate]) {
                    byDate[scanDate] = { 
                        scans: 0, 
                        violations: 0, 
                        risk: 0,
                        nodeBySeverity: { critical: 0, serious: 0, moderate: 0, minor: 0 },
                        pages: new Set()
                    };
                }
                byDate[scanDate].scans += 1;
                byDate[scanDate].violations += summary.totalViolations || 0;
                byDate[scanDate].risk += summary.totalRiskCost || 0;
                byDate[scanDate].pages.add(scan.url);
                if (scan.seedUrls) {
                    scan.seedUrls.forEach(u => byDate[scanDate].pages.add(u));
                }

                // Add node counts for this date
                if (summary.nodeCountsBySeverity) {
                    byDate[scanDate].nodeBySeverity.critical += summary.nodeCountsBySeverity.critical || 0;
                    byDate[scanDate].nodeBySeverity.serious += summary.nodeCountsBySeverity.serious || 0;
                    byDate[scanDate].nodeBySeverity.moderate += summary.nodeCountsBySeverity.moderate || 0;
                    byDate[scanDate].nodeBySeverity.minor += summary.nodeCountsBySeverity.minor || 0;
                } else if (summary.ruleStats) {
                    Object.values(summary.ruleStats).forEach(data => {
                        const impact = data.impact || 'minor';
                        if (byDate[scanDate].nodeBySeverity[impact] !== undefined) {
                            byDate[scanDate].nodeBySeverity[impact] += data.count || 0;
                        }
                    });
                }
            }

            // By site
            try {
                const hostname = new URL(scan.url).hostname;
                if (!bySite[hostname]) bySite[hostname] = { scans: 0, violations: 0, risk: 0, pages: new Set(), nodeBySeverity: { critical: 0, serious: 0, moderate: 0, minor: 0 } };
                bySite[hostname].scans += 1;
                bySite[hostname].violations += summary.totalViolations || 0;
                bySite[hostname].risk += summary.totalRiskCost || 0;
                bySite[hostname].pages.add(scan.url);
                if (scan.seedUrls) {
                    scan.seedUrls.forEach(u => bySite[hostname].pages.add(u));
                }

                // Per-site node severity accumulation
                const hasSiteNodeData = summary.nodeCountsBySeverity &&
                    (summary.nodeCountsBySeverity.critical || summary.nodeCountsBySeverity.serious ||
                        summary.nodeCountsBySeverity.moderate || summary.nodeCountsBySeverity.minor);

                if (hasSiteNodeData) {
                    bySite[hostname].nodeBySeverity.critical += summary.nodeCountsBySeverity.critical || 0;
                    bySite[hostname].nodeBySeverity.serious += summary.nodeCountsBySeverity.serious || 0;
                    bySite[hostname].nodeBySeverity.moderate += summary.nodeCountsBySeverity.moderate || 0;
                    bySite[hostname].nodeBySeverity.minor += summary.nodeCountsBySeverity.minor || 0;
                } else if (summary.ruleStats) {
                    // Fallback: derive from ruleStats for this scan
                    Object.values(summary.ruleStats).forEach(data => {
                        const impact = data.impact || 'minor';
                        if (bySite[hostname].nodeBySeverity[impact] !== undefined) {
                            bySite[hostname].nodeBySeverity[impact] += data.count || 0;
                        }
                    });
                } else {
                    bySite[hostname].nodeBySeverity.critical += (summary.bySeverity?.critical || 0) * 4;
                    bySite[hostname].nodeBySeverity.serious += (summary.bySeverity?.serious || 0) * 3;
                    bySite[hostname].nodeBySeverity.moderate += (summary.bySeverity?.moderate || 0) * 2;
                    bySite[hostname].nodeBySeverity.minor += (summary.bySeverity?.minor || 0) * 1;
                }

                // Add unique pages from ruleStats for this site
                if (summary.ruleStats) {
                    Object.values(summary.ruleStats).forEach(rule => {
                        if (rule.pages) {
                            Object.keys(rule.pages).forEach(pageUrl => bySite[hostname].pages.add(pageUrl));
                        }
                    });
                }
            } catch { }
        });

        // Sort dates for trend chart
        const sortedDates = Object.keys(byDate).sort();
        const trendData = sortedDates.map(date => {
            const dateData = byDate[date];
            const dateWeightedSum = dateData.nodeBySeverity.critical * 10 + dateData.nodeBySeverity.serious * 5 + dateData.nodeBySeverity.moderate * 2 + dateData.nodeBySeverity.minor * 1;
            const datePageCount = dateData.pages.size || 1;
            const dateAvgWeighted = dateWeightedSum / datePageCount;
            const dateRiskScore = Math.min(100, Math.round(100 * (1 - Math.exp(-dateAvgWeighted / 30))));
            
            return {
                date,
                ...dateData,
                riskScore: dateRiskScore
            };
        });

        // Top violations
        const topViolations = Object.entries(byRule)
            .sort((a, b) => a[1].count - b[1].count)
            .slice(-10)
            .map(([ruleId, data]) => ({ ruleId, ...data }));

        // Site breakdown with per-site compliance score and grade
        const siteBreakdown = Object.entries(bySite)
            .map(([site, data]) => {
                const siteWeighted = data.nodeBySeverity.critical * 10 + data.nodeBySeverity.serious * 5 + data.nodeBySeverity.moderate * 2 + data.nodeBySeverity.minor * 1;
                const sitePageCount = data.pages.size || 1;
                const siteAvgWeighted = siteWeighted / sitePageCount;
                // Risk Score: 0 is perfect, 100 is critical risk
                const siteRiskScore = Math.min(100, Math.round(100 * (1 - Math.exp(-siteAvgWeighted / 30))));
                const siteGrade = getGradeFromRisk(siteRiskScore);
                return { site, ...data, pages: data.pages.size, riskScore: siteRiskScore, grade: siteGrade.grade, pass: siteGrade.pass };
            });

        // Add sites that only have archived scans
        uniqueSites.forEach(hostname => {
            if (!bySite[hostname]) {
                siteBreakdown.push({
                    site: hostname,
                    scans: 0,
                    violations: 0,
                    risk: 0,
                    pages: 0,
                    nodeBySeverity: { critical: 0, serious: 0, moderate: 0, minor: 0 },
                    riskScore: 0,
                    grade: 'N/A',
                    pass: true,
                    isArchivedSite: true
                });
            }
        });

        siteBreakdown.sort((a, b) => (b.violations || 0) - (a.violations || 0));

        // Executive Grade (A-F) - Align with individual scan risk thresholds
        // 1. Calculate weighted impact sum (10/5/2/1 weights)
        const weightedImpactSum = nodeBySeverity.critical * 10 + nodeBySeverity.serious * 5 + nodeBySeverity.moderate * 2 + nodeBySeverity.minor * 1;
        // 2. Get average weighted impact per page
        const scannedPageCount = uniquePages.size || 1;
        const avgWeightedImpact = weightedImpactSum / scannedPageCount;
        // 3. Map to 0-100 risk score where 0 is A+ (0 impact) and 100 is F (massive impact)
        const riskScore = Math.min(100, Math.round(100 * (1 - Math.exp(-avgWeightedImpact / 30))));
        // 4. Get grade from thresholds
        const { grade } = getGradeFromRisk(riskScore);



        const severityDistribution = [
            { id: 'critical', label: 'Critical', value: bySeverity.critical || 0, color: '#ef4444' },
            { id: 'serious', label: 'Serious', value: bySeverity.serious || 0, color: '#f97316' },
            { id: 'moderate', label: 'Moderate', value: bySeverity.moderate || 0, color: '#eab308' },
            { id: 'minor', label: 'Minor', value: bySeverity.minor || 0, color: '#14b8a6' },
        ];

        return {
            totalScans: filteredScans.length,
            totalViolations,
            totalRiskCost,
            bySeverity,
            severityDistribution,
            uniqueSites: Array.from(uniqueSites),
            uniquePages: uniquePages.size,
            trendData,
            topViolations,
            siteBreakdown,
            riskScore,
            grade,
            dateRange: { start: startDate, end: endDate }
        };
    }, [scans, analyticsTimeRange, analyticsCustomStart, analyticsCustomEnd, analyticsSiteFilter]);

    const journeysData = useMemo(() => {
        return [...journeys].sort((a, b) => {
            let valA, valB;
            if (journeysSort.key === 'createdAt') {
                valA = a.createdAt?.seconds || 0;
                valB = b.createdAt?.seconds || 0;
            } else if (journeysSort.key === 'name') {
                valA = a.name || '';
                valB = b.name || '';
                return journeysSort.direction === 'desc' ? valB.localeCompare(valA) : valA.localeCompare(valB);
            } else if (journeysSort.key === 'domain') {
                valA = a.domain || '';
                valB = b.domain || '';
                return journeysSort.direction === 'desc' ? valB.localeCompare(valA) : valA.localeCompare(valB);
            }
            return journeysSort.direction === 'desc' ? valB - valA : valA - valB;
        });
    }, [journeys, journeysSort]);

    const applyCrawlFilters = (allUrls, filterRegexStr, excludeRegexStr) => {
        if (!allUrls) return;
        let filtered = [...allUrls];
        if (filterRegexStr) {
            try {
                const regex = new RegExp(filterRegexStr, 'i');
                filtered = filtered.filter(u => regex.test(u));
            } catch (e) { }
        }
        if (excludeRegexStr) {
            try {
                const regex = new RegExp(excludeRegexStr, 'i');
                filtered = filtered.filter(u => !regex.test(u));
            } catch (e) { }
        }
        setCrawlSelectedUrls(new Set(filtered));
    };

    const handleToggleSelect = (e, scanId) => {
        e.stopPropagation();
        const next = new Set(selectedScans);
        if (next.has(scanId)) {
            next.delete(scanId);
        } else {
            next.add(scanId);
        }
        setSelectedScans(next);
    };

    const handleSelectAll = () => {
        if (selectedScans.size === paginatedScans.length) {
            setSelectedScans(new Set());
        } else {
            const next = new Set(selectedScans);
            paginatedScans.forEach(s => next.add(s.id));
            setSelectedScans(next);
        }
    };

    const renderScanCard = (scan) => {
        const isExpanded = expandedScan === scan.id;
        return (
            <div key={scan.id} className="bg-white border border-(--border) rounded-2xl shadow-sm relative group hover:border-(--accent)/50 transition-all">
                <div className={`absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl ${scan.status === 'completed' ? 'bg-(--success)' : (scan.status === 'failed' ? 'bg-(--error)' : 'bg-(--accent)')}`}></div>

                {/* Scan Header */}
                <div className="p-6 flex items-center gap-5">
                    <input
                        type="checkbox"
                        checked={selectedScans.has(scan.id)}
                        onChange={(e) => handleToggleSelect(e, scan.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-5 h-5 cursor-pointer accent-(--accent) shrink-0 bg-white border border-(--border) rounded"
                    />
                    <button
                        onClick={() =>
                            setExpandedScan(isExpanded ? null : scan.id)
                        }
                        className="min-w-0 flex-1 text-left cursor-pointer transition"
                    >
                        <p className="text-(--text) font-extrabold text-sm truncate flex items-center gap-3">
                            <span className="truncate group-hover:text-(--accent) transition-colors">
                                {(() => {
                                    if (scan.seedUrls) {
                                        try {
                                            return `${new URL(scan.url).hostname} [Cluster: ${scan.seedUrls.length}]`;
                                        } catch (e) {
                                            return scan.displayUrl || scan.url;
                                        }
                                    }
                                    return scan.displayUrl || scan.url;
                                })()}
                            </span>
                            <span
                                onClick={(e) => {
                                    e.stopPropagation();
                                    let textToCopy = scan.url;
                                    if (scan.seedUrls) {
                                        try { textToCopy = new URL(scan.url).hostname; } catch (e) { }
                                    }
                                    navigator.clipboard.writeText(textToCopy);
                                    const target = e.currentTarget;
                                    const originalText = target.innerText;
                                    target.innerText = '✓';
                                    setTimeout(() => { target.innerText = originalText; }, 1000);
                                }}
                                className="text-[0.5625rem] font-black bg-(--surface-stone) border border-(--border) px-2 py-0.5 rounded-md uppercase tracking-tighter cursor-copy hover:border-(--accent) hover:text-(--accent) transition-all"
                                title="Copy Endpoint"
                            >
                                Copy
                            </span>
                        </p>
                        <p className="text-(--text-muted) font-black text-[0.625rem] mt-1.5 uppercase tracking-widest opacity-80">
                            {getComplianceLabel(scan.complianceType)}
                            {scan.createdAt &&
                                ` • ${new Date(scan.createdAt.seconds * 1000).toLocaleString()} `}
                        </p>
                        {/* Activity Metadata (Simplified Analytics) */}
                        {scan.status === 'completed' && (
                            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 bg-(--surface-stone)/30 p-3 rounded-lg border border-(--border)/50">
                                <div className="flex flex-col">
                                    <span className="text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-tighter opacity-60">Node ID</span>
                                    <span className="text-xs font-black uppercase tracking-wider text-(--accent)">
                                        {scan.claimedAgentInfo?.agentId || 'AUTO_PROVISION'}
                                    </span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-tighter opacity-60">Geolocation</span>
                                    <span className="text-xs font-black uppercase tracking-wider text-(--text)">
                                        {scan.claimedAgentInfo?.ip || 'GLOBAL_EDGE'}
                                    </span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-tighter opacity-60">Dimensions</span>
                                    <span className="text-xs font-black uppercase tracking-wider text-(--text)">
                                        {scan.depth === 'single' ? 'Single Page' : `Cluster (${scan.maxPages}p)`} • {scan.complianceType?.toUpperCase()}
                                    </span>
                                </div>
                            </div>
                        )}
                        {/* Live scanning progress */}
                        {scan.status === 'processing' && scan.progress && (
                            <div className="mt-4 space-y-2">
                                <div className="w-full bg-(--surface-stone) rounded-full h-1.5 overflow-hidden border border-(--border)">
                                    <div
                                        className="h-full bg-(--accent) rounded-full transition-all duration-500"
                                        style={{ width: `${Math.min(100, ((scan.progress.crawled || 0) / Math.max(1, scan.progress.total || 1)) * 100)}% ` }}
                                    />
                                </div>
                                <p className="text-(--accent) text-[0.625rem] font-black font-mono truncate flex items-center gap-2 uppercase tracking-widest">
                                    <span className="animate-pulse">🔄</span> Scanning: {scan.progress.crawled}/{scan.progress.total}
                                    {scan.progress.currentUrl && (
                                        <span className="text-(--text-muted) truncate opacity-50"> // {scan.progress.currentUrl}</span>
                                    )}
                                </p>
                            </div>
                        )}
                    </button>
                    <div className="flex items-center gap-3 shrink-0 ml-auto">
                        {(() => {
                            const now = new Date();
                            const isFuture = scan.status === 'pending' && scan.scheduledAt && (scan.scheduledAt.seconds * 1000 > now.getTime());
                            const status = isFuture ? STATUS_STYLES.scheduled : (STATUS_STYLES[scan.status] || STATUS_STYLES.pending);
                            return (
                                <span
                                    className={`px-3 py-1 rounded-full text-[0.625rem] font-bold border-2 flex items-center gap-1 ${status.bg} ${status.text} border-(--border)`}
                                >
                                    {status.icon} {status.label}
                                </span>
                            );
                        })()}
                        {(scan.distributed || scan.isHA) && (
                            <span className="px-3 py-1 rounded-full text-[0.625rem] font-bold border-2 flex items-center gap-1 bg-(--accent)/10 text-(--accent) border-(--accent)/30 animate-pulse">
                                🛸 HA_CLUSTER
                            </span>
                        )}
                        {scan.status === 'processing' && (
                            <button
                                onClick={(e) => { e.stopPropagation(); handleCancelScan(scan.id, true); }}
                                className="p-2 text-(--text-muted) hover:text-(--fail-red) hover:bg-(--fail-red)/10 rounded-full transition-colors flex items-center justify-center shrink-0"
                                title="Stop active scan"
                            >
                                🛑
                            </button>
                        )}
                        {scan.status === 'pending' && (
                            <div className="flex gap-2">
                                {(() => {
                                    const now = new Date();
                                    const isFuture = scan.scheduledAt && (scan.scheduledAt.seconds * 1000 > now.getTime());
                                    if (isFuture) {
                                        return (
                                            <>
                                                <button
                                                    onClick={(e) => { 
                                                        e.stopPropagation(); 
                                                        // Pre-populate "New Scan" form and switch tabs
                                                        setUrl(scan.url);
                                                        setComplianceType(scan.complianceType);
                                                        setDepth(scan.depth);
                                                        setPageCap(scan.maxPages);
                                                        setSelectedDevices(scan.devices);
                                                        setTargetAgentId(scan.targetAgentId);
                                                        setDistributed(scan.distributed);
                                                        setScanIncludeRegex(scan.includeRegex || '');
                                                        setScanExcludeRegex(scan.excludeRegex || '');
                                                        setIsScheduled(scan.isScheduled);
                                                        setScheduleFrequency(scan.scheduleFrequency || 'daily');
                                                        setMaxRetries(scan.maxRetries || 0);
                                                        setFailOnPageError(scan.failOnPageError || false);
                                                        if (scan.scheduledAt) {
                                                            const d = new Date(scan.scheduledAt.seconds * 1000);
                                                            // Format as YYYY-MM-DDThh:mm for datetime-local
                                                            const offset = d.getTimezoneOffset() * 60000;
                                                            const localIso = new Date(d.getTime() - offset).toISOString().slice(0, 16);
                                                            setScheduledAt(localIso);
                                                        }
                                                        setDashboardTab('new-scan');
                                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                                        // Important: We'll delete the old one when they submit the "new" one to "edit" it?
                                                        // Or just treat it as a template? User said "edit".
                                                        // Simplest "edit" is template + delete old one. 
                                                        // But maybe we should just keep it simple for now: navigate to form.
                                                    }}
                                                    className="p-2 text-(--text-muted) hover:text-(--accent) hover:bg-(--accent)/10 rounded-full transition-colors flex items-center justify-center shrink-0"
                                                    title="Edit scheduled scan"
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleDeleteScheduledScan(scan.id); }}
                                                    className="p-2 text-(--text-muted) hover:text-(--fail-red) hover:bg-(--fail-red)/10 rounded-full transition-colors flex items-center justify-center shrink-0"
                                                    title="Delete scheduled scan"
                                                >
                                                    🗑️
                                                </button>
                                            </>
                                        );
                                    }
                                    return (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleCancelScan(scan.id); }}
                                            className="p-2 text-(--text-muted) hover:text-(--fail-red) hover:bg-(--fail-red)/10 rounded-full transition-colors flex items-center justify-center shrink-0"
                                            title="Cancel pending scan"
                                        >
                                            🚫
                                        </button>
                                    );
                                })()}
                            </div>
                        )}
                        {scan.status === 'failed' && (
                            <button
                                onClick={(e) => { e.stopPropagation(); handleRetryScan(scan.id); }}
                                className="p-2 text-(--text-muted) hover:text-(--warning) hover:bg-(--warning)/10 rounded-full transition-colors flex items-center justify-center shrink-0"
                                title="Retry failed scan"
                            >
                                🔄
                            </button>
                        )}

                        {/* Action buttons (Archive vs Restore/Delete) */}
                        {scan.status !== 'pending' && scan.status !== 'processing' && viewMode === 'active' && (
                            <div className="flex gap-2 min-w-max">
                                {scan.type === 'crawl' && scan.status === 'completed' && scan.discoveredUrls && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleReviewCrawl(scan);
                                        }}
                                        className="px-4 py-2 bg-(--accent)/10 text-(--accent) border border-(--accent)/30 font-bold text-xs rounded-full hover:bg-(--accent)/20 transition-colors animate-pulse"
                                        title="Review and select discovered URLs"
                                    >
                                        Review & Select
                                    </button>
                                )}
                                {scan.type !== 'crawl' && scan.status === 'completed' && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleVerifyFixes(scan);
                                        }}
                                        className="px-4 py-2 bg-(--surface-elevated) text-(--text) font-bold text-xs rounded-full hover:bg-(--surface-stone) border border-(--border) transition-colors"
                                        title="Establish new verification link"
                                    >
                                        Verify
                                    </button>
                                )}
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleArchiveScan(scan.id, true); }}
                                    className="px-4 py-2 text-(--text-muted) font-bold text-xs rounded-full hover:bg-(--surface-stone) hover:text-(--text) transition-colors"
                                    title="Move to history"
                                >
                                    Archive
                                </button>
                            </div>
                        )}

                        {scan.status !== 'pending' && scan.status !== 'processing' && viewMode === 'archived' && (
                            <div className="flex gap-2 min-w-max">
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleArchiveScan(scan.id, false); }}
                                    className="px-4 py-2 bg-(--surface-elevated) text-(--text) font-bold text-xs rounded-full hover:bg-(--surface-stone) border border-(--border) transition-colors"
                                    title="Restore to active list"
                                >
                                    Restore
                                </button>
                                <div className="relative">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handlePermanentDelete(scan.id); }}
                                        className="px-4 py-2 bg-(--fail-red)/10 text-(--fail-red) font-bold text-xs rounded-full hover:bg-(--fail-red)/20 transition-colors"
                                        title="Delete record permanently"
                                    >
                                        Delete
                                    </button>

                                    {/* Inline Delete Confirmation Popover */}
                                    {deleteConfirmScanId === scan.id && (
                                        <div
                                            className="absolute bottom-full right-0 mb-3 w-64 p-5 bg-white border border-(--border) rounded-2xl shadow-lg z-50 animate-in slide-in-from-bottom-2 duration-150"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <p className="text-sm font-bold text-(--text) mb-4 text-center">
                                                Confirm permanent deletion?
                                            </p>
                                            <div className="flex gap-3">
                                                <button
                                                    onClick={cancelDelete}
                                                    className="flex-1 px-4 py-2 text-(--text-muted) font-bold text-xs rounded-full hover:bg-(--surface-stone) hover:text-(--text) transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={() => confirmPermanentDelete(scan.id)}
                                                    className="flex-1 px-4 py-2 bg-(--fail-red) text-white font-bold text-xs rounded-full hover:opacity-90 transition-colors"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                            {/* Pointer triangle */}
                                            <div className="absolute top-full right-5 w-0 h-0 border-l-[0.5em] border-r-[0.5em] border-t-[0.625em] border-l-transparent border-r-transparent border-t-white"></div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Simplified Activity Audit Log */}
                {isExpanded && scan.status === 'completed' && (
                    <div className="border-t border-(--border) p-8 bg-(--surface-stone)/50 rounded-b-2xl">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-lg font-black text-(--text) tracking-tight uppercase">Scan Activity Audit</h3>
                                <p className="text-xs text-(--text-muted) font-bold mt-1 uppercase tracking-widest">Activity log for validation and forensic tracking</p>
                            </div>
                            <button
                                onClick={() => setExpandedScan(null)}
                                className="px-4 py-2 bg-white border border-(--border) text-(--text-muted) hover:text-(--text) font-bold text-[0.625rem] rounded-full uppercase tracking-widest transition-all shadow-sm"
                            >
                                Close Log
                            </button>
                        </div>

                        <div className="mb-6 flex gap-4">
                            <div className="flex-1 bg-white border border-(--border) rounded-2xl p-6 shadow-sm">
                                <p className="text-[0.625rem] font-black text-(--text-muted) uppercase tracking-widest mb-3">Scan Configuration</p>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <span className="text-[0.5625rem] font-black text-(--text-muted) uppercase opacity-60 block">Engine</span>
                                        <span className="text-xs font-bold text-(--text)">Playwright + Axe-Core</span>
                                    </div>
                                    <div>
                                        <span className="text-[0.5625rem] font-black text-(--text-muted) uppercase opacity-60 block">Standard</span>
                                        <span className="text-xs font-bold text-(--text)">{scan.complianceType?.toUpperCase() || 'WCAG 2.1 AA'}</span>
                                    </div>
                                    <div>
                                        <span className="text-[0.5625rem] font-black text-(--text-muted) uppercase opacity-60 block">Device</span>
                                        <span className="text-xs font-bold text-(--text)">{scan.devices?.join(', ') || 'Desktop Chrome'}</span>
                                    </div>
                                    <div>
                                        <span className="text-[0.5625rem] font-black text-(--text-muted) uppercase opacity-60 block">Identity</span>
                                        <span className="text-xs font-bold text-(--text)">{scan.claimedAgentInfo?.agentId || 'ANONYMOUS_RUN'}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="w-1/3 bg-(--accent) text-white rounded-2xl p-6 shadow-lg relative overflow-hidden flex flex-col justify-center text-left">
                                <div className="absolute top-0 right-0 p-4 opacity-20 text-4xl">💎</div>
                                <h4 className="text-xl font-black uppercase tracking-tighter leading-none mb-1">Activity Log</h4>
                                <p className="text-[0.625rem] font-black opacity-80 uppercase tracking-widest mb-4">Integrity Verified</p>
                                <div className="text-xs font-bold opacity-90 leading-relaxed border-t border-white/20 pt-4">
                                    This audit log provides strictly activity-based tracking of the scan execution for transparency and compliance record-keeping.
                                </div>
                            </div>
                        </div>

                        <div className="bg-white border border-(--border) rounded-2xl overflow-hidden shadow-sm">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-(--surface-stone) border-b border-(--border)">
                                    <tr>
                                        <th className="px-6 py-4 text-[0.625rem] font-black text-(--text-muted) uppercase tracking-[0.2em]">Target URL / Page Segment</th>
                                        <th className="px-6 py-4 text-[0.625rem] font-black text-(--text-muted) uppercase tracking-[0.2em] w-32">Status</th>
                                        <th className="px-6 py-4 text-[0.625rem] font-black text-(--text-muted) uppercase tracking-[0.2em] w-32 text-right">Activity</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-(--border)">
                                    {(() => {
                                        const urlsToRender = [];
                                        const seenUrls = new Set();
                                        
                                        // 1. Root Entry Point
                                        if (scan.url) {
                                            urlsToRender.push({ url: scan.url, label: '🎯 TARGET (ROOT)', color: 'text-(--accent)' });
                                            seenUrls.add(scan.url);
                                        }
                                        
                                        // 2. Seed URLs (Targets)
                                        if (scan.seedUrls && scan.seedUrls.length > 0) {
                                            scan.seedUrls.forEach((u, i) => {
                                                if (!seenUrls.has(u)) {
                                                    urlsToRender.push({ url: u, label: `🎯 TARGET (CLUSTER NODE ${i+1})`, color: 'text-(--text-muted)' });
                                                    seenUrls.add(u);
                                                }
                                            });
                                        }
                                        
                                        // 3. Discovered / Extracted URLs
                                        let foundUrls = [];
                                        if (scan.discoveredUrls) {
                                            foundUrls = scan.discoveredUrls;
                                        } else if (scan.summary?.ruleStats) {
                                            const urlSet = new Set();
                                            Object.values(scan.summary.ruleStats).forEach(r => {
                                                if (r.pages) Object.keys(r.pages).forEach(u => urlSet.add(u));
                                            });
                                            foundUrls = Array.from(urlSet);
                                        } else if (scan.summary?.pages) {
                                            foundUrls = scan.summary.pages.map(p => p.url);
                                        }

                                        foundUrls.forEach((u) => {
                                            if (u && !seenUrls.has(u)) {
                                                urlsToRender.push({ url: u, label: '🔍 FOUND PAGE', color: 'text-(--success)' });
                                                seenUrls.add(u);
                                            }
                                        });

                                        return urlsToRender.map((item, idx) => (
                                            <tr key={idx} className="hover:bg-(--surface-stone)/30 transition-colors">
                                                <td className="px-6 py-4">
                                                    <span className="text-sm font-bold text-(--text) block truncate max-w-md" title={item.url}>{item.url}</span>
                                                    <span className={`text-[0.5625rem] font-black ${item.color} uppercase tracking-widest mt-1 block`}>{item.label}</span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="px-3 py-1 bg-(--success)/10 text-(--success) border border-(--success)/20 rounded-full text-[0.5625rem] font-black uppercase tracking-widest">VALIDATED</span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <span className="text-[0.625rem] font-mono font-black text-(--text-muted)">SUCCESS</span>
                                                </td>
                                            </tr>
                                        ));
                                    })()}
                                </tbody>
                            </table>
                        </div>


                    </div>
                )}

                {isExpanded && scan.status === 'completed' && scan.hasChunks && isLoadingChunks && !hydratedResults[scan.id] && (
                    <div className="border-t border-(--border) p-12 text-center bg-(--bg)/50">
                        <div className="animate-spin w-10 h-10 border-4 border-(--accent) border-t-transparent rounded-none mx-auto mb-4 shadow-[0.25em_0.25em_0em_var(--border)]" />
                        <p className="text-(--accent) font-black uppercase tracking-[0.2em] text-xs">Loading Scan Results...</p>
                    </div>
                )}

                {isExpanded && scan.status === 'failed' && scan.error && (
                    <div className="border-t border-(--border) p-6 bg-(--error)/5">
                        <div className="bg-(--error)/10 border border-(--error)/20 rounded-none p-5">
                            <p className="text-(--error) text-xs font-black uppercase tracking-widest">Scan Error</p>
                            <p className="text-(--text-muted) text-sm mt-2 font-mono">{scan.error}</p>
                        </div>
                    </div>
                )}

                {isExpanded && scan.status === 'processing' && (
                    <div className="border-t border-(--border) p-8 bg-(--surface-stone)">
                        <div className="flex items-center gap-6 bg-white rounded-xl shadow-sm border border-(--border) p-6">
                            <div className="animate-spin shrink-0 w-8 h-8 border-4 border-(--accent)/30 border-t-(--accent) rounded-full" />
                            <div className="flex-1">
                                <p className="text-(--accent) font-bold text-sm tracking-wide">
                                    Agent Processing
                                </p>
                                {scan.progress && (
                                    <div className="mt-4 text-xs text-(--text-muted) font-bold flex items-center gap-4">
                                        <span className="shrink-0">{scan.progress.crawled} / {scan.progress.total} Segments</span>
                                        <div className="flex-1 bg-(--surface-stone) rounded-full h-2 overflow-hidden border border-(--border)">
                                            <div
                                                className="bg-(--accent) h-full rounded-full transition-all duration-300"
                                                style={{ width: `${(scan.progress.crawled / scan.progress.total) * 100}% ` }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="p-8 space-y-10 w-full animate-in fade-in duration-500">

            {/* Analytics Dashboard */}
            {dashboardTab === 'analytics' && (
                <section ref={analyticsRef} className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 border-b border-(--border) pb-8">
                        <div className="space-y-3">
                            <h2 className="text-4xl font-black text-(--text) tracking-tight">
                                Executive Overview
                            </h2>
                            <p className="text-lg text-(--text-muted) max-w-2xl">
                                A high-level view of your portfolio's accessibility health and risk management progress.
                            </p>
                        </div>
                            <div className="flex items-center gap-4">
                                {/* Export Buttons */}
                                <div className="flex items-center gap-2 bg-(--surface-stone) p-1 rounded-full border border-(--border)">
                                    <button
                                        onClick={() => {
                                            if (!planLimits?.allowSharing) {
                                                setUpgradeModal({
                                                    isOpen: true,
                                                    title: 'Sharing Locked',
                                                    message: 'Exporting analytics as PNG is only available on premium plans. Upgrade to share beautiful reports with your team.'
                                                });
                                                return;
                                            }
                                            handleExportAnalytics('png');
                                        }}
                                        className={`pill-button px-4 py-2 text-[0.625rem] bg-transparent hover:bg-white/50 transition-all ${!planLimits?.allowSharing ? 'text-(--text-muted) opacity-50' : 'text-(--text)'}`}
                                        title={!planLimits?.allowSharing ? "Export as PNG (Locked)" : "Export as PNG"}
                                    >
                                        {!planLimits?.allowSharing ? '🔒 PNG' : '📸 PNG'}
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (!planLimits?.allowPdfExport) {
                                                setUpgradeModal({
                                                    isOpen: true,
                                                    title: 'PDF Export Locked',
                                                    message: 'Detailed PDF reports are an enterprise feature. Upgrade your plan to export professional accessibility reports.'
                                                });
                                                return;
                                            }
                                            handleExportAnalytics('pdf');
                                        }}
                                        className={`pill-button px-4 py-2 text-[0.625rem] bg-transparent hover:bg-white/50 transition-all ${!planLimits?.allowPdfExport ? 'text-(--text-muted) opacity-50' : 'text-(--text)'}`}
                                        title={!planLimits?.allowPdfExport ? "Export as PDF (Locked)" : "Export as PDF"}
                                    >
                                        {!planLimits?.allowPdfExport ? '🔒 PDF' : '📄 PDF'}
                                    </button>
                                </div>
                                <div className="flex items-center gap-3 bg-(--surface-stone) border border-(--border) rounded-2xl px-5 py-3">
                                <span className="text-xs font-bold text-(--text-muted) uppercase tracking-wider">Time Range</span>
                                <select
                                    value={analyticsTimeRange}
                                    onChange={(e) => setAnalyticsTimeRange(e.target.value)}
                                    className="bg-transparent text-sm font-bold outline-none cursor-pointer text-(--accent)"
                                    aria-label="Filter analytics by time range"
                                >
                                    <option value="all">All Time</option>
                                    <option value="7d">Last 7 Days</option>
                                    <option value="30d">Last 30 Days</option>
                                    <option value="90d">Last 90 Days</option>
                                    <option value="custom">Custom Range</option>
                                </select>
                                {analyticsTimeRange === 'custom' && (
                                    <div className="flex items-center gap-2 ml-2">
                                        <input
                                            type="date"
                                            value={analyticsCustomStart || ''}
                                            onChange={e => setAnalyticsCustomStart(e.target.value)}
                                            className="bg-white border border-(--border) rounded-2xl px-3 py-1 text-xs font-mono text-(--accent)"
                                        />
                                        <input
                                            type="date"
                                            value={analyticsCustomEnd || ''}
                                            onChange={e => setAnalyticsCustomEnd(e.target.value)}
                                            className="bg-white border border-(--border) rounded-2xl px-3 py-1 text-xs font-mono text-(--accent)"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* Bento Box Metrics Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-6 auto-rows-fr">
                        {/* Massive Compliance Score Block */}
                        <Card className="col-span-1 md:col-span-2 md:row-span-2 flex flex-col justify-center items-center text-center bg-white border-2 border-(--accent)/20 relative overflow-hidden group rounded-2xl shadow-sm">
                            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                                <span className="text-9xl">🛡️</span>
                            </div>
                            <Text className="text-sm font-bold text-(--text-muted) uppercase tracking-wider mb-2 relative z-10">Overall Compliance Grade</Text>
                            <Metric className={`text-8xl font-black mb-4 relative z-10 ${analyticsData.grade === 'A' || analyticsData.grade === 'B' ? 'text-(--pass-green)' : analyticsData.grade === 'C' ? 'text-(--warn-amber)' : 'text-(--fail-red)'}`}>
                                {analyticsData.grade}
                            </Metric>
                            <Flex justifyContent="center" alignItems="center" className="gap-3 relative z-10">
                                <span className="text-2xl">
                                    {analyticsData.grade === 'A' || analyticsData.grade === 'B' ? '✅' : analyticsData.grade === 'C' ? '⚠️' : '🚨'}
                                </span>
                                <Text className="text-xl font-bold text-(--text)">
                                    {analyticsData.grade === 'A' || analyticsData.grade === 'B' ? 'COMPLIANT' : analyticsData.grade === 'C' ? 'WARNING' : 'ACTION REQUIRED'}
                                </Text>
                            </Flex>
                            <Text className="mt-6 text-sm text-(--text-muted) font-medium max-w-[15em] relative z-10">
                                Based on {analyticsData.totalScans} scans across {analyticsData.uniqueSites.length} verified domains.
                            </Text>
                        </Card>

                        {/* Financial Risk Block */}
                        <Card
                            onClick={() => setDashboardTab('issues')}
                            className="bg-white border border-(--border) rounded-2xl p-6 hover:shadow-md transition-all cursor-pointer group shadow-sm"
                        >
                            <Flex justifyContent="between" alignItems="start">
                                <Text className="text-xs font-bold text-(--text-muted) uppercase tracking-wider">Est. Legal Risk</Text>
                                <span className="text-2xl opacity-40 group-hover:scale-110 transition-transform">⚖️</span>
                            </Flex>
                            <div className="mt-4">
                                <Metric className={`font-bold text-(--fail-red) ${('$' + analyticsData.totalRiskCost.toLocaleString()).length > 10 ? 'text-3xl' : 'text-4xl'}`}>
                                    ${analyticsData.totalRiskCost.toLocaleString()}
                                </Metric>
                                <Flex justifyContent="start" alignItems="center" className="gap-2 mt-2">
                                    <BadgeDelta deltaType="moderateIncrease" className="text-xs font-bold">
                                        High Exposure
                                    </BadgeDelta>
                                </Flex>
                            </div>
                        </Card>

                        {/* Violation Count Block */}
                        <Card
                            onClick={() => setDashboardTab('issues')}
                            className="bg-white border border-(--border) rounded-2xl p-6 hover:shadow-md transition-all cursor-pointer group shadow-sm"
                        >
                            <Flex justifyContent="between" alignItems="start">
                                <Text className="text-xs font-bold text-(--text-muted) uppercase tracking-wider">Roadblocks Found</Text>
                                <span className="text-2xl opacity-40 group-hover:scale-110 transition-transform">🚧</span>
                            </Flex>
                            <div className="mt-4">
                                <Metric className="text-4xl font-bold text-(--text)">{analyticsData.totalViolations}</Metric>
                                <Text className="text-xs font-medium text-(--text-muted) mt-2 opacity-80">Critical accessibility barriers</Text>
                            </div>
                        </Card>

                        {/* Portfolio Pulse Block */}
                        <div className="bento-card bg-white border border-(--border) rounded-2xl p-6 flex flex-col justify-between col-span-1 md:col-span-2 shadow-sm">
                            <div className="flex justify-between items-center mb-4">
                                <p className="text-xs font-bold text-(--text-muted) uppercase tracking-wider">Portfolio Pulse</p>
                                <div className="flex items-center gap-4">
                                    <div className="text-center">
                                        <p className="text-[0.625rem] font-bold text-(--text-muted) uppercase">Sites</p>
                                        <p className="text-xl font-bold">{analyticsData.uniqueSites.length}</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-[0.625rem] font-bold text-(--text-muted) uppercase">Pages</p>
                                        <p className="text-xl font-bold">{analyticsData.uniquePages}</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-[0.625rem] font-bold text-(--text-muted) uppercase">Risk Score</p>
                                        <p className="text-xl font-bold text-(--accent)">{analyticsData.riskScore}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="h-2 w-full bg-white/50 rounded-full overflow-hidden border border-(--border)">
                                <div
                                    className="h-full bg-(--accent) rounded-full shadow-sm transition-all duration-1000"
                                    style={{ width: `${Math.max(10, 100 - (analyticsData.riskScore / 10))}%` }}
                                ></div>
                            </div>
                            <p className="text-[0.625rem] text-(--text-muted) mt-4 font-bold uppercase tracking-widest flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-(--accent) animate-pulse"></span>
                                System Health:
                                <span className="text-(--accent)"> {Math.max(10, 100 - (analyticsData.riskScore / 10))}% Stabilized</span>
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Trends area */}
                        <div className="bg-white border border-(--border) rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-black text-(--text) tracking-tight border-b border-(--border) pb-4 mb-6">
                                Portfolio Risk Trend
                            </h3>
                            <div className="h-64">
                                <RiskScoreTrendTremor 
                                    data={analyticsData.trendData.map(d => ({ date: d.date, riskScore: d.riskScore }))} 
                                    height={256} 
                                />
                            </div>
                        </div>
                        <div className="bg-white border border-(--border) rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-black text-(--text) tracking-tight border-b border-(--border) pb-4 mb-6">
                                Severity Breakdown
                            </h3>
                            <div className="h-64 flex items-center justify-center">
                                <ImpactPieChartNivo data={analyticsData.severityDistribution} />
                            </div>
                        </div>

                        {/* Top Violations */}
                        <div className="bento-card bg-white">
                            <h3 className="text-lg font-black text-(--text) tracking-tight border-b border-(--border) pb-4 mb-6">
                                Top Violation Types
                            </h3>
                            <div className="h-[25em]">
                                <WcagCriteriaBreakdownNivo 
                                    data={analyticsData.topViolations.map(v => ({ criteria: v.ruleId.replace(/-/g, ' ').toUpperCase(), count: v.count }))} 
                                />
                            </div>
                        </div>

                        {/* Site Breakdown */}
                        <div className="bento-card bg-white">
                            <div className="flex items-center justify-between border-b border-(--border) pb-4 mb-6">
                                <h3 className="text-lg font-black text-(--text) tracking-tight">
                                    Health by Site
                                </h3>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Search sites..."
                                        value={analyticsSiteSearch}
                                        onChange={(e) => setAnalyticsSiteSearch(e.target.value)}
                                        className="w-44 bg-(--surface-stone) border border-(--border) rounded-full px-4 py-2 pl-10 text-xs font-bold text-(--text) placeholder-(--text-muted)/50 focus:border-(--accent) focus:outline-none transition-all"
                                    />
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm opacity-40">🔍</span>
                                </div>
                            </div>
                            {(() => {
                                const filteredSites = analyticsData.siteBreakdown.filter(site =>
                                    !analyticsSiteSearch || site.site.toLowerCase().includes(analyticsSiteSearch.toLowerCase())
                                );
                                return filteredSites.length > 0 ? (
                                    <ul className="space-y-4 max-h-[25em] overflow-y-auto pr-2 custom-scrollbar" tabIndex="0">
                                        {filteredSites.map(site => (
                                            <li
                                                key={site.site}
                                                onClick={() => onSiteSelect?.(site.site)}
                                                className="flex justify-between items-center p-4 bg-(--surface-stone) border border-(--border) rounded-xl hover:border-(--accent) transition-all cursor-pointer group">

                                                <div className="flex items-center gap-4 min-w-0">
                                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center border-2 font-black text-lg shrink-0 ${site.pass ? 'border-(--pass-green) text-(--pass-green) bg-(--pass-green)/5' : 'border-(--fail-red) text-(--fail-red) bg-(--fail-red)/5'}`}>
                                                        {site.grade}
                                                    </div>
                                                    <div className="flex flex-col gap-1 min-w-0">
                                                        <span className="text-sm font-bold text-(--text) truncate group-hover:text-(--accent) transition-colors">
                                                            {site.site}
                                                        </span>
                                                        <span className="text-[0.625rem] font-bold text-(--text-muted) uppercase opacity-70 truncate">
                                                            {site.scans} Scans • Risk: {site.riskScore}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4 shrink-0 ml-4">
                                                    <div className="flex flex-col items-end gap-1">
                                                        <span className={`text-xs font-black ${site.violations > 0 ? 'text-(--fail-red)' : 'text-(--pass-green)'}`}>
                                                            {site.violations} {site.violations === 1 ? 'Violation' : 'Violations'}
                                                        </span>
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-[0.625rem] font-bold text-(--text-muted) uppercase">
                                                                {site.pass ? '✅ PASS' : '🚨 FAIL'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="text-center py-12">
                                        <span className="text-4xl block mb-4">🔍</span>
                                        <p className="text-sm text-(--text-muted) font-bold uppercase tracking-widest opacity-50">
                                            {analyticsSiteSearch ? 'No sites match search' : 'No site data found'}
                                        </p>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </section>
            )}


            {dashboardTab === 'new-scan' && (
                <section className="bg-white border border-(--border) rounded-2xl shadow-sm p-8 sm:p-12 animate-in fade-in zoom-in-95 duration-500 relative overflow-hidden">
                    <div className="relative z-10">
                        <div className="flex items-center gap-4 mb-10">
                            <span className="text-4xl">⚡</span>
                            <div>
                                <h2 className="text-3xl font-bold text-(--text) tracking-tight">Run New Scan</h2>
                                <p className="text-sm text-(--text-muted) mt-1 font-medium">Configure and launch a new accessibility audit across your domain.</p>
                            </div>
                        </div>

                        {/* Monthly Usage Indicator */}
                        <div className="bg-(--surface-stone) border border-(--border) rounded-xl p-5 xl:p-6 mb-8 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-white rounded-xl shadow-sm border border-(--border) flex items-center justify-center text-xl">
                                    📊
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-(--text)">Monthly Page Usage</h4>
                                    <p className="text-xs text-(--text-muted) font-medium mt-0.5">Scanned pages reset at the end of the month.</p>
                                </div>
                            </div>
                            <div className="flex flex-col items-end">
                                <span className="text-lg font-black text-(--text) tracking-tight">
                                    {monthlyPagesScanned} / {planLimits?.maxPagesPerMonth > 0 ? planLimits.maxPagesPerMonth : 'Unlimited'}
                                </span>
                                <span className="text-[0.625rem] font-bold text-(--text-muted) uppercase tracking-wider">
                                    Pages Used
                                </span>
                            </div>
                        </div>

                        <form onSubmit={handleScan} className="space-y-10">
                            {error && (
                                <div className="bg-(--error)/10 border border-(--error)/20 rounded-xl p-5 text-(--error) font-bold text-sm">
                                    {error}
                                </div>
                            )}

                            {/* URL Input Cluster */}
                            <div className="space-y-4">
                                <label className="text-xs font-bold text-(--text-muted) uppercase tracking-wider pl-1" htmlFor="scan-url">
                                    Target URL(s)
                                </label>
                                <div className="flex gap-4">
                                    <input
                                        id="scan-url"
                                        type="url"
                                        value={url}
                                        onChange={(e) => setUrl(e.target.value)}
                                        className="flex-1 bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-4 text-sm font-mono text-(--text) focus:border-(--accent) focus:outline-none transition-all placeholder:text-(--text-muted)/50"
                                        placeholder="https://example.com"
                                    />
                                    <label className="bg-(--surface-stone) border border-(--border) hover:border-(--accent) px-6 py-4 rounded-2xl text-sm font-bold flex items-center gap-2 shrink-0 cursor-pointer transition-all text-(--text)">
                                        <span>📁 Import List</span>
                                        <input
                                            type="file"
                                            accept=".txt,.json,.xml"
                                            className="hidden"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (!file) return;
                                                const reader = new FileReader();
                                                reader.onload = (ev) => {
                                                    const text = ev.target.result;
                                                    let urls = [];
                                                    const ext = file.name.split('.').pop().toLowerCase();
                                                    if (ext === 'json') {
                                                        try {
                                                            const parsed = JSON.parse(text);
                                                            urls = Array.isArray(parsed) ? parsed : [];
                                                        } catch { urls = []; }
                                                    } else if (ext === 'xml') {
                                                        const matches = text.match(/https?:\/\/[^\s<>"']+/gi);
                                                        urls = matches || [];
                                                    } else {
                                                        urls = text.split(/\r?\n/).map(l => l.trim());
                                                    }
                                                    const valid = urls.filter(u => /^https?:\/\//i.test(u));
                                                    setImportedUrls(valid);
                                                    setToast({ message: `Successfully imported ${valid.length} URLs from ${file.name} `, type: 'success' });
                                                };
                                                reader.readAsText(file);
                                                e.target.value = ''; // Reset for re-upload
                                            }}
                                        />
                                    </label>
                                </div>
                                {importedUrls.length > 0 && (
                                    <div className="mt-4 border border-(--border) bg-(--surface-stone) p-4 rounded-xl shadow-sm">
                                        <div className="flex items-center justify-between mb-3">
                                            <p className="text-xs text-(--text) font-bold">{importedUrls.length} URLs Imported</p>
                                            <button type="button" onClick={() => setImportedUrls([])} className="text-[0.625rem] bg-(--fail-red)/10 text-(--fail-red) px-3 py-1 rounded-full font-bold hover:bg-(--fail-red)/20 transition-all cursor-pointer">Clear Buffer</button>
                                        </div>
                                        <div className="max-h-32 overflow-y-auto space-y-1 custom-scrollbar pr-2">
                                            {importedUrls.map((u, i) => (
                                                <p key={i} className="text-[0.6875rem] text-(--text-muted) font-mono truncate py-0.5 border-b border-(--border)">{u}</p>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* User Journey Selection */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between pl-1">
                                    <label className="text-xs font-bold text-(--text-muted) uppercase tracking-wider">
                                        User Journey (Beta)
                                    </label>
                                    {(planLimits?.maxJourneys || 0) > 0 ? (
                                        <span className={`text-[0.625rem] font-black uppercase tracking-widest px-2 py-1 rounded-sm ${journeys.length >= planLimits.maxJourneys ? 'bg-(--fail-red)/20 text-(--fail-red)' : 'bg-(--surface-stone) text-(--text-muted)'}`}>
                                            {journeys.length} / {planLimits.maxJourneys} Journeys
                                        </span>
                                    ) : (
                                        <span className="text-[0.625rem] font-black uppercase tracking-widest px-2 py-1 rounded-sm bg-amber-500/10 text-amber-500">
                                            0 Journeys (Locked)
                                        </span>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <select
                                        value={selectedJourney?.id || ''}
                                        onChange={(e) => {
                                            const j = journeys.find(jw => jw.id === e.target.value);
                                            setSelectedJourney(j || null);
                                            if (j && !url) {
                                                setUrl(`https://${j.domain}`);
                                            }
                                        }}
                                        aria-label="Select User Journey"
                                        className="bg-(--surface-stone) border border-(--border) rounded-xl px-4 py-3 text-sm font-medium text-(--text) focus:border-(--accent) focus:outline-none transition-all cursor-pointer"
                                    >
                                        <option value="">-- Direct Scan (No Journey) --</option>
                                        {
                                            journeys.map(j => (
                                                <option key={j.id} value={j.id}>{j.name} ({j.domain})</option>
                                            ))
                                        }
                                    </select >
                                    <div className="flex items-center gap-3 text-xs text-(--text-muted) bg-(--surface-stone) p-4 rounded-xl border border-(--border)">
                                        <span className="text-xl">🛣️</span>
                                        <span>Authenticated journeys require a Master Key in your Local Agent settings to replay.</span>
                                    </div>
                                </div >
                            </div >

                            {/* Simulation Profiles */}
                            < div className="space-y-4" >
                                <label className="text-xs font-bold text-(--text-muted) uppercase tracking-wider pl-1">
                                    Select Device Profiles
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    {DEVICE_PROFILES.map((device) => {
                                        const isSelected = selectedDevices.includes(device.id);
                                        return (
                                            <label
                                                key={device.id}
                                                className={`relative border rounded-2xl p-5 flex items-center gap-4 cursor-pointer transition-all duration-300 ${isSelected ? 'border-(--accent) bg-[color-mix(in_srgb,var(--accent)_10%,transparent)] shadow-sm' : 'border-(--border) bg-(--surface-stone) hover:border-(--accent)/50'}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => {
                                                        setSelectedDevices(prev => {
                                                            const next = new Set(prev);
                                                            if (next.has(device.id)) next.delete(device.id);
                                                            else next.add(device.id);
                                                            if (next.size === 0) return ['desktop'];
                                                            return Array.from(next);
                                                        });
                                                    }}
                                                    className="w-5 h-5 rounded bg-white border border-(--border) accent-(--accent) shrink-0 cursor-pointer"
                                                />
                                                <div>
                                                    <span className={`text-sm font-bold ${isSelected ? 'text-(--accent)' : 'text-(--text)'}`}>{device.label}</span>
                                                    <p className="text-xs text-(--text-muted)">Profile v2.4.0</p>
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div >

                            {/* Penetration Depth */}
                            < div className="space-y-4" >
                                <label className="text-xs font-bold text-(--text-muted) uppercase tracking-wider pl-1">
                                    Scan Depth Configuration
                                </label>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {[
                                        { value: 'single', label: 'Single', desc: 'Current page only' },
                                        { value: 'depth1', label: 'Depth 1', desc: 'Follow internal links' },
                                        { value: 'depth2', label: 'Depth 2', desc: 'Follow two levels' },
                                        { value: 'full', label: 'Full Site', desc: 'Thorough crawl' },
                                    ].map((d) => (
                                        <button
                                            key={d.value}
                                            type="button"
                                            onClick={() => setDepth(d.value)}
                                            className={`p-4 rounded-2xl text-center border transition-all duration-300 cursor-pointer flex flex-col items-center justify-center min-h-[5.625em] ${depth === d.value
                                                ? 'border-(--accent) bg-[color-mix(in_srgb,var(--accent)_10%,transparent)] shadow-sm'
                                                : 'border-(--border) bg-(--surface-stone) hover:border-(--accent)/50'
                                                }`}
                                        >
                                            <p className={`font-bold text-sm ${depth === d.value ? 'text-(--accent)' : 'text-(--text)'}`}>{d.label}</p>
                                            <p className="text-xs mt-1 text-(--text-muted)">{d.desc}</p>
                                        </button>
                                    ))}
                                </div>
                                {
                                    depth !== 'single' && (
                                        <div className="mt-6 p-6 border border-(--border) bg-(--surface-stone) rounded-2xl space-y-6 animate-in slide-in-from-top-4 duration-300 shadow-sm">
                                            <div className="flex flex-wrap items-center gap-8 border-b border-(--border) pb-5">
                                                <div className="flex items-center gap-3">
                                                    <label className="text-xs font-bold text-(--text-muted) uppercase tracking-wider">Page Limit</label>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max={planLimits?.maxPagesPerMonth > 0 ? planLimits.maxPagesPerMonth : undefined}
                                                            value={pageCap}
                                                            onChange={(e) => {
                                                                const maxAllowed = planLimits?.maxPagesPerMonth > 0 ? planLimits.maxPagesPerMonth : Infinity;
                                                                setPageCap(Math.max(1, Math.min(maxAllowed, parseInt(e.target.value) || 10)));
                                                            }}
                                                            className="w-24 bg-white border border-(--border) rounded-lg text-(--text) font-mono text-center py-2 px-3 focus:outline-none focus:border-(--accent)"
                                                        />
                                                        <span className="text-[0.625rem] text-(--text-muted) font-bold bg-(--surface-stone) border border-(--border) px-2 py-1 rounded-lg">
                                                            Max {planLimits?.maxPagesPerMonth > 0 ? planLimits.maxPagesPerMonth : 'Unlimited'}
                                                        </span>
                                                    </div>
                                                </div>

                                                <label className="flex items-center gap-3 cursor-pointer group">
                                                    <div className="relative">
                                                        <input
                                                            type="checkbox"
                                                            checked={distributed}
                                                            onChange={(e) => setDistributed(e.target.checked)}
                                                            className="sr-only peer"
                                                        />
                                                        <div className="w-11 h-6 bg-(--border) rounded-2xl peer-checked:bg-(--accent) transition-all"></div>
                                                        <div className="absolute top-1 left-1 w-4 h-4 bg-white rounded-2xl peer-checked:translate-x-5 transition-all"></div>
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-bold text-(--text) transition-colors">Distributed Scanning (HA)</span>
                                                        <span className="text-[0.625rem] text-(--text-muted)">Leverage all available agents for faster crawling</span>
                                                    </div>
                                                </label>
                                            </div>
                                            <div className="space-y-3">
                                                <label className="text-xs font-bold text-(--text-muted) uppercase tracking-wider block">URL Filtering (Regex)</label>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                    <input
                                                        type="text"
                                                        placeholder="Include Pattern (e.g. \/blog\/)"
                                                        value={scanIncludeRegex}
                                                        onChange={e => setScanIncludeRegex(e.target.value)}
                                                        className="bg-white border border-(--border) rounded-xl px-4 py-2 text-sm font-mono text-(--text) placeholder:text-(--text-muted)/50 focus:border-(--accent) transition-all"
                                                    />
                                                    <input
                                                        type="text"
                                                        placeholder="Exclude Pattern (e.g. \/admin|\/login)"
                                                        value={scanExcludeRegex}
                                                        onChange={e => setScanExcludeRegex(e.target.value)}
                                                        className="bg-white border border-(--border) rounded-xl px-4 py-2 text-sm font-mono text-(--fail-red) placeholder:text-(--text-muted)/50 focus:border-(--fail-red) transition-all"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }
                            </div >

                            {/* Compliance Layer */}
                            < div className="space-y-4" >
                                <label className="text-xs font-bold text-(--text-muted) uppercase tracking-wider pl-1">
                                    Accessibility Standard
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {COMPLIANCE_CONTEXTS.map((ctx) => (
                                        <button
                                            key={ctx.value}
                                            type="button"
                                            onClick={() => setComplianceType(ctx.value)}
                                            className={`p-6 rounded-2xl border transition-all duration-300 cursor-pointer flex flex-col items-center shadow-sm ${complianceType === ctx.value
                                                ? 'border-(--accent) bg-[color-mix(in_srgb,var(--accent)_10%,transparent)]'
                                                : 'border-(--border) bg-(--surface-stone) hover:border-(--accent)/50'
                                                }`}
                                        >
                                            <span className="text-3xl mb-3">{ctx.icon}</span>
                                            <p className={`font-bold text-sm ${complianceType === ctx.value ? 'text-(--accent)' : 'text-(--text)'}`}>{ctx.label}</p>
                                            <p className="text-xs mt-1 text-(--text-muted) text-center">{ctx.description}</p>
                                            <div className="w-full mt-4 pt-4 border-t border-(--border)/50">
                                                <p className="text-[0.625rem] text-(--text-muted) font-mono">Rules: {ctx.examples}</p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div >

                            {/* Advanced Automation & Scheduling */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 border-t border-(--border) pt-8">
                                <div className="space-y-6">
                                        <div className="flex items-center justify-between">
                                            <label className="text-xs font-bold text-(--text-muted) uppercase tracking-wider block">Scheduling</label>
                                            
                                            {!planLimits?.allowScheduling ? (
                                                <div className="flex items-center gap-3">
                                                    <span className="text-[0.625rem] font-bold text-(--text-muted) uppercase px-2 py-0.5 bg-gray-100 rounded-lg border border-gray-200">Locked</span>
                                                    <button type="button" onClick={() => window.location.href = '/'} className="text-[0.625rem] font-bold text-(--accent) hover:underline">Upgrade Plan</button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-6">
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={scheduledAt !== ''} 
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    const now = new Date();
                                                                    const iso = new Date(now.getTime() - (now.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
                                                                    setScheduledAt(iso);
                                                                } else {
                                                                    setScheduledAt('');
                                                                }
                                                            }}
                                                            className="w-4 h-4 rounded border-(--border) accent-(--accent)"
                                                        />
                                                        <span className="text-xs font-bold text-(--text)">Schedule for Later</span>
                                                    </label>
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={isScheduled} 
                                                            onChange={(e) => setIsScheduled(e.target.checked)}
                                                            className="w-4 h-4 rounded border-(--border) accent-(--accent)"
                                                        />
                                                        <span className="text-xs font-bold text-(--text)">Enable Recurring</span>
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {scheduledAt !== '' && (
                                            <div className="p-4 bg-(--surface-stone) border border-(--border) rounded-2xl space-y-4 animate-in fade-in zoom-in-95 duration-200">
                                                <div className="space-y-2">
                                                    <label className="text-[0.625rem] font-bold text-(--text-muted) uppercase tracking-wider pl-1">Run at Specific Time</label>
                                                    <input
                                                        type="datetime-local"
                                                        value={scheduledAt}
                                                        onChange={(e) => setScheduledAt(e.target.value)}
                                                        className="w-full bg-white border border-(--border) rounded-xl px-4 py-2 text-sm font-bold text-(--text) focus:outline-none focus:border-(--accent) transition-all"
                                                    />
                                                </div>
                                                <p className="text-[0.625rem] text-(--text-muted) flex items-center gap-2 bg-white/50 p-2 rounded-lg border border-(--border)/50">
                                                    <span>📍</span>
                                                    <span>One-time scan will trigger at the designated moment.</span>
                                                </p>
                                            </div>
                                        )}

                                        {isScheduled && (
                                            <div className="p-4 bg-(--surface-stone) border border-(--border) rounded-2xl space-y-4 animate-in fade-in zoom-in-95 duration-200">
                                                <div className="space-y-2">
                                                    <label className="text-[0.625rem] font-bold text-(--text-muted) uppercase tracking-wider pl-1">Frequency</label>
                                                    <div className="flex gap-2">
                                                        {['daily', 'weekly', 'monthly'].map((freq) => (
                                                            <button
                                                                key={freq}
                                                                type="button"
                                                                onClick={() => setScheduleFrequency(freq)}
                                                                className={`flex-1 py-2 rounded-xl text-[0.6875rem] font-bold border transition-all uppercase tracking-tight ${scheduleFrequency === freq 
                                                                    ? 'bg-(--accent) text-white border-(--accent)' 
                                                                    : 'bg-white text-(--text-muted) border-(--border) hover:border-(--accent)/50'}`}
                                                            >
                                                                {freq}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                                <p className="text-[0.625rem] text-(--text-muted) flex items-center gap-2 bg-white/50 p-2 rounded-lg border border-(--border)/50">
                                                    <span>📅</span>
                                                    <span>Next scan will be triggered automatically based on this schedule.</span>
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                <div className="space-y-6">
                                    <label className="text-xs font-bold text-(--text-muted) uppercase tracking-wider block">Failure Handling</label>
                                    <div className="bg-(--surface-stone) border border-(--border) rounded-2xl p-5 space-y-5 shadow-sm">
                                        <div className="flex items-center justify-between">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-(--text)">Max Retries</span>
                                                <span className="text-[0.625rem] text-(--text-muted)">Automatic retries on transient errors</span>
                                            </div>
                                            <select 
                                                value={maxRetries}
                                                onChange={(e) => setMaxRetries(parseInt(e.target.value))}
                                                className="bg-white border border-(--border) rounded-lg px-3 py-1 text-sm font-bold text-(--accent) focus:outline-none focus:border-(--accent) cursor-pointer"
                                            >
                                                {[0, 1, 2, 3, 5].map(n => <option key={n} value={n}>{n} {n === 1 ? 'Retry' : 'Retries'}</option>)}
                                            </select>
                                        </div>

                                        <label className="flex items-center justify-between cursor-pointer group">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-(--text)">Strict Mode</span>
                                                <span className="text-[0.625rem] text-(--text-muted)">Fail job completely if any single page hits an error</span>
                                            </div>
                                            <div className="relative">
                                                <input
                                                    type="checkbox"
                                                    className="sr-only peer"
                                                    checked={failOnPageError}
                                                    onChange={(e) => setFailOnPageError(e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-(--border) rounded-2xl peer-checked:bg-(--fail-red) transition-all"></div>
                                                <div className="absolute top-1 left-1 w-4 h-4 bg-white rounded-2xl peer-checked:translate-x-5 transition-all"></div>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Scan Localhost Card Configuration */}
                            <div className="border border-(--border) bg-white p-6 rounded-2xl shadow-sm mb-8 space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <span className="text-2xl p-2 bg-(--surface-stone) rounded-xl border border-(--border)">🔐</span>
                                        <div>
                                            <h3 className="text-sm font-bold text-(--text)">Scan Localhost / Staging</h3>
                                            <p className="text-xs text-(--text-muted)">Target local development environments directly bypassing public DNS mapping.</p>
                                        </div>
                                    </div>
                                    {!planLimits?.allowLocalAgent && (
                                        <div className="shrink-0 flex items-center gap-4">
                                            <span className="text-xs font-bold text-(--text-muted) uppercase px-3 py-1 bg-gray-100 rounded-full">Locked</span>
                                            <button 
                                                type="button" 
                                                onClick={() => window.location.href = '/'} // Redirect to Plans/Upgrade
                                                className="text-xs font-bold bg-(--accent) text-white px-4 py-2 rounded-xl shadow-sm hover:opacity-90 transition-opacity"
                                            >
                                                Upgrade to Premium
                                            </button>
                                        </div>
                                    )}
                                </div>
                                
                                {planLimits?.allowLocalAgent && (
                                    <div className="pt-4 border-t border-(--border) flex flex-col gap-3">
                                        <label className="flex items-center gap-3 cursor-pointer group w-fit">
                                            <div className="relative">
                                                <input
                                                    type="checkbox"
                                                    className="sr-only peer"
                                                    checked={url.includes('localhost') || url.includes('127.0.0.1')}
                                                    onChange={(e) => {
                                                        if (e.target.checked) setUrl('http://localhost:3000');
                                                        else setUrl('');
                                                    }}
                                                />
                                                <div className="w-11 h-6 bg-(--border) rounded-2xl peer-checked:bg-(--accent) transition-all"></div>
                                                <div className="absolute top-1 left-1 w-4 h-4 bg-white rounded-2xl peer-checked:translate-x-5 transition-all"></div>
                                            </div>
                                            <span className="text-sm font-bold text-(--text)">Enable Localhost Targeting Mode</span>
                                        </label>
                                        <p className="text-[0.625rem] text-(--text-muted) italic">Note: Requires a Local Agent running on the target machine.</p>
                                    </div>
                                )}
                            </div>

                            {/* System Toggles */}
                            < div className="grid grid-cols-1 sm:grid-cols-2 gap-8 border-t border-(--border) pt-8" >
                                <div className="space-y-4">
                                    <label className="text-xs font-bold text-(--text-muted) uppercase tracking-wider block">Analysis Mode</label>
                                    <label className="flex items-center gap-4 cursor-pointer group w-fit">
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                className="sr-only peer"
                                                checked={includeBestPractices}
                                                onChange={(e) => setIncludeBestPractices(e.target.checked)}
                                            />
                                            <div className="w-11 h-6 bg-(--border) rounded-2xl peer-checked:bg-(--accent) transition-all"></div>
                                            <div className="absolute top-1 left-1 w-4 h-4 bg-white rounded-2xl peer-checked:translate-x-5 transition-all"></div>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-sm font-bold text-(--text)">Include Best Practices</span>
                                            <span className="text-xs text-(--text-muted)">Recommended for thorough audits</span>
                                        </div>
                                    </label>

                                    <label className="flex items-center gap-4 cursor-pointer group w-fit mt-4">
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                className="sr-only peer"
                                                checked={includePrivacy}
                                                onChange={(e) => setIncludePrivacy(e.target.checked)}
                                            />
                                            <div className="w-11 h-6 bg-(--border) rounded-2xl peer-checked:bg-(--accent) transition-all"></div>
                                            <div className="absolute top-1 left-1 w-4 h-4 bg-white rounded-2xl peer-checked:translate-x-5 transition-all"></div>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-sm font-bold text-(--text)">Privacy & Cookie Checks</span>
                                            <span className="text-xs text-(--text-muted)">Scan for CMPs and generic cookie banners</span>
                                        </div>
                                    </label>
                                </div>

                                <div className="space-y-4">
                                    <label className="text-xs font-bold text-(--text-muted) uppercase tracking-wider block">Agent Assignment</label>
                                    <div className="flex flex-col gap-3">
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setTargetAgentId('auto')}
                                                className={`px-4 py-2 rounded-2xl border transition-all flex items-center gap-2 ${targetAgentId === 'auto'
                                                    ? 'border-(--accent) bg-[color-mix(in_srgb,var(--accent)_10%,transparent)] text-(--accent)'
                                                    : 'border-(--border) bg-(--surface-stone) text-(--text-muted) hover:border-(--text)'
                                                    }`}
                                            >
                                                <span>🤖</span>
                                                <span className="text-xs font-bold">Auto-Assign</span>
                                            </button>

                                            {/* Region Selectors (Derived from online agents) */}
                                            {Array.from(new Set(onlineAgents.map(a => a.region).filter(Boolean))).map(region => (
                                                <button
                                                    key={region}
                                                    type="button"
                                                    onClick={() => setTargetAgentId(`region:${region}`)}
                                                    className={`px-4 py-2 rounded-2xl border transition-all flex items-center gap-2 ${targetAgentId === `region:${region}`
                                                        ? 'border-(--accent) bg-[color-mix(in_srgb,var(--accent)_10%,transparent)] text-(--accent)'
                                                        : 'border-(--border) bg-(--surface-stone) text-(--text-muted) hover:border-(--text)'
                                                        }`}
                                                >
                                                    <span>🌐</span>
                                                    <span className="text-xs font-bold uppercase">{region} Region</span>
                                                </button>
                                            ))}
                                        </div>
                                        
                                        <div className="bg-(--surface-stone) p-4 rounded-2xl border border-(--border) space-y-3">
                                            <p className="text-[0.625rem] font-bold text-(--text-muted) uppercase tracking-widest px-1">Specific Agents</p>
                                            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto custom-scrollbar pr-2">
                                                {onlineAgents.filter(a => a.type !== 'browser-extension').map(agent => (
                                                    <button
                                                        key={agent.id}
                                                        type="button"
                                                        onClick={() => {
                                                            const currentIds = targetAgentId.startsWith('ids:') ? targetAgentId.replace('ids:', '').split(',') : [];
                                                            let nextIds;
                                                            if (currentIds.includes(agent.agentId)) {
                                                                nextIds = currentIds.filter(id => id !== agent.agentId);
                                                            } else {
                                                                nextIds = [...currentIds, agent.agentId];
                                                            }
                                                            
                                                            if (nextIds.length === 0) setTargetAgentId('auto');
                                                            else setTargetAgentId(`ids:${nextIds.join(',')}`);
                                                        }}
                                                        className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all flex items-center gap-2 ${targetAgentId.includes(agent.agentId)
                                                            ? 'border-(--accent) bg-(--accent) text-white'
                                                            : 'border-(--border) bg-white text-(--text-muted) hover:border-(--text)'
                                                            }`}
                                                    >
                                                        <span>💻</span>
                                                        <span className="truncate max-w-[7.5em]">
                                                            {agent.customName || agent.hostname || 'AGENT-X'}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div >

                            {/* Execute Button container */}
                            <div className="pt-8 flex flex-col sm:flex-row gap-4">
                                <button
                                    type="button"
                                    onClick={(e) => handleScan(e, 'scan')}
                                    disabled={submitting || !complianceType}
                                    className="flex-1 bg-(--accent) text-white py-4 rounded-2xl font-bold text-lg transition-all duration-300 hover:opacity-90 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3 shadow-md"
                                >
                                    {submitting ? (
                                        <>
                                            <div className="animate-spin w-6 h-6 border-2 border-white border-t-transparent rounded-full" />
                                            <span>Starting...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Run Direct Scan</span>
                                            <span className="text-xl">⚡</span>
                                        </>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => handleScan(e, 'crawl')}
                                    disabled={submitting || !complianceType}
                                    className="flex-1 bg-white text-(--accent) border-2 border-(--accent) py-4 rounded-2xl font-bold text-lg transition-all duration-300 hover:bg-(--accent)/5 active:scale-95 disabled:opacity-50 disabled:border-opacity-50 flex items-center justify-center gap-3 shadow-sm"
                                >
                                    {submitting ? (
                                        <>
                                            <div className="animate-spin w-6 h-6 border-2 border-(--accent) border-t-transparent rounded-full" />
                                            <span>Crawling...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Crawl & Discover</span>
                                            <span className="text-xl">🔍</span>
                                        </>
                                    )}
                                </button>
                            </div>
                            {submitting && <p className="text-center text-xs text-(--text-muted) mt-4 font-bold animate-pulse">Connecting to Agent...</p>}
                        </form>
                    </div>
                </section>
            )}

            {
                dashboardTab === 'scans' && (
                    <section className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="space-y-6">
                            <h2 className="text-3xl font-black text-(--text) tracking-tight flex items-center gap-4">
                                Scan Analytics
                                {unviewedCount > 0 && (
                                    <span className="text-[0.625rem] bg-(--surface-elevated) border border-(--accent) text-(--accent) px-3 py-1 rounded-full animate-pulse shadow-sm">
                                        {unviewedCount} NEW REPORTS
                                    </span>
                                )}
                            </h2>

                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                                <div className="flex bg-(--surface-stone) border border-(--border) p-1 relative rounded-full w-fit shadow-sm overflow-hidden flex-wrap sm:flex-nowrap">
                                    {/* Sliding background indicator */}
                                    <div
                                        className={`absolute top-1 bottom-1 left-1 w-[8.25rem] bg-(--accent) rounded-full shadow-sm transition-transform duration-300 ease-out ${viewMode === 'archived' ? 'translate-x-[8.25rem]' : viewMode === 'scheduled' ? 'translate-x-[16.5rem]' : viewMode === 'audit' ? 'translate-x-[24.75rem]' : ''}`}
                                    />
                                    <button
                                        onClick={() => setViewMode('active')}
                                        className={`relative z-10 px-6 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors w-[8.25rem] cursor-pointer whitespace-nowrap ${viewMode === 'active' ? 'text-white' : 'text-(--text-muted) hover:text-(--text)'}`}
                                    >
                                        Active
                                    </button>
                                    <button
                                        onClick={() => setViewMode('archived')}
                                        className={`relative z-10 px-6 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors w-[8.25rem] cursor-pointer whitespace-nowrap ${viewMode === 'archived' ? 'text-white' : 'text-(--text-muted) hover:text-(--text)'}`}
                                    >
                                        Archived
                                    </button>
                                    <button
                                        onClick={() => setViewMode('scheduled')}
                                        className={`relative z-10 px-6 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors w-[8.25rem] cursor-pointer whitespace-nowrap ${viewMode === 'scheduled' ? 'text-white' : 'text-(--text-muted) hover:text-(--text)'}`}
                                    >
                                        Scheduled
                                    </button>
                                    <button
                                        onClick={() => setViewMode('audit')}
                                        className={`relative z-10 px-6 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors w-[8.25rem] cursor-pointer whitespace-nowrap ${viewMode === 'audit' ? 'text-white' : 'text-(--text-muted) hover:text-(--text)'}`}
                                    >
                                        Audit Logs
                                    </button>
                                </div>

                                {/* Top Control Bar */}
                                <div className={`flex flex-wrap items-center gap-4 ${viewMode === 'audit' ? 'hidden' : ''}`}>
                                    {/* Sort Controls */}
                                    <div className="flex items-center gap-3 bg-(--surface-stone) border border-(--border) rounded-2xl px-5 py-3 shadow-sm">
                                        <span className="text-xs font-bold text-(--text-muted) uppercase tracking-wider">Sort By</span>
                                        <select
                                            value={scansSort.key}
                                            onChange={(e) => setScansSort(prev => ({ ...prev, key: e.target.value }))}
                                            className="bg-transparent text-sm font-bold outline-none cursor-pointer text-(--accent)"
                                            aria-label="Sort scans by"
                                        >
                                            <option value="date">Date Created</option>
                                            <option value="riskScore">Risk Score</option>
                                            <option value="status">Scan Status</option>
                                        </select>
                                        <button
                                            onClick={() => setScansSort(prev => ({ ...prev, direction: prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                            className="text-sm font-bold hover:text-(--accent) transition-colors flex items-center justify-center w-6 h-6 rounded-full hover:bg-white/50"
                                            title={scansSort.direction === 'desc' ? 'Descending' : 'Ascending'}
                                        >
                                            {scansSort.direction === 'desc' ? '↓' : '↑'}
                                        </button>
                                    </div>
                                    <div className="relative group">
                                        <input
                                            type="text"
                                            placeholder="Search scans..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="w-full sm:w-64 bg-(--surface-stone) border border-(--border) focus:border-(--accent) focus:ring-2 focus:ring-(--accent)/5 rounded-full py-3 px-6 pl-12 text-sm font-medium text-(--text) placeholder-(--text-muted)/50 focus:outline-none transition-all shadow-sm"
                                        />
                                        <span className="absolute left-5 top-1/2 -translate-y-1/2 text-sm opacity-40 group-focus-within:opacity-100 transition-opacity">🔍</span>
                                    </div>

                                    <div className="flex bg-(--surface-stone) border border-(--border) rounded-2xl overflow-hidden shadow-sm">
                                        <select
                                            value={domainFilter}
                                            onChange={(e) => setDomainFilter(e.target.value)}
                                            className="bg-transparent py-3 px-5 text-sm font-bold text-(--text) cursor-pointer focus:outline-none hover:bg-(--accent)/5 transition-colors border-r border-(--border)"
                                            aria-label="Filter scans by cluster"
                                        >
                                            <option value="all">All Domains</option>
                                            {uniqueDomains.map(domain => (
                                                <option key={domain} value={domain}>{domain}</option>
                                            ))}
                                        </select>

                                        <select
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            className="bg-transparent py-3 px-5 text-sm font-bold text-(--text) cursor-pointer focus:outline-none hover:bg-(--accent)/5 transition-colors"
                                            aria-label="Filter scans by status"
                                        >
                                            <option value="all">All Status</option>
                                            <option value="pending">Queued</option>
                                            <option value="scheduled">Pending Run</option>
                                            <option value="processing">Scanning</option>
                                            <option value="completed">Analyzed</option>
                                            <option value="failed">Error</option>
                                        </select>
                                    </div>

                                    {/* Group by Domain Toggle */}
                                    <div className="flex items-center gap-3 bg-(--surface-stone) border border-(--border) rounded-2xl px-5 py-3 shadow-sm cursor-pointer" onClick={() => setGroupByDomain(!groupByDomain)}>
                                        <span className="text-xs font-bold text-(--text-muted) uppercase tracking-wider">Group by Domain</span>
                                        <div className={`w-11 h-6 rounded-full transition-all relative ${groupByDomain ? 'bg-(--accent)' : 'bg-(--border)'}`}>
                                            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${groupByDomain ? 'left-6' : 'left-1'}`}></div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>

                        {viewMode === 'audit' ? (
                            <div className="space-y-4 animate-in fade-in zoom-in-95 duration-300">
                                {auditScans.length === 0 ? (
                                    <div className="border border-(--border) border-dashed rounded-2xl p-16 text-center bg-white shadow-sm">
                                        <p className="text-sm text-(--text-muted) font-bold">No audit logs detected in current sector.</p>
                                    </div>
                                ) : (
                                    auditScans.map((scan) => {
                                        const now = new Date();
                                        const isFuture = scan.status === 'pending' && scan.scheduledAt && (scan.scheduledAt.seconds * 1000 > now.getTime());
                                        const status = isFuture ? STATUS_STYLES.scheduled : (STATUS_STYLES[scan.status] || STATUS_STYLES.pending);

                                        return (
                                            <div key={scan.id} className="bg-white border border-(--border) p-6 rounded-2xl flex items-center justify-between gap-6 hover:border-(--accent)/50 transition-all shadow-sm group">
                                                <div className="min-w-0 flex-1 text-left">
                                                    <p className="text-(--text) font-bold text-sm truncate flex items-center gap-4">
                                                        <span className="text-xl group-hover:scale-125 transition-transform">🌐</span>
                                                        <span className="tracking-widest group-hover:text-(--accent) transition-colors">{scan.url}</span>
                                                    </p>
                                                    <p className="text-(--text-muted) font-bold text-xs mt-2 flex items-center gap-3">
                                                        <span className="bg-(--surface-stone) px-3 py-1 rounded-full border border-(--border)">
                                                            {scan.createdAt ? new Date(scan.createdAt.seconds * 1000).toLocaleString() : 'N/A'}
                                                        </span>
                                                        <span className="opacity-40">|</span>
                                                        <span className="text-(--accent)">{getComplianceLabel(scan.complianceType)}</span>
                                                        {scan.status === 'completed' && scan.results && (
                                                            <>
                                                                <span className="opacity-40">|</span>
                                                                {(() => {
                                                                    const { summary } = parseAxeResults(scan.results);
                                                                    const gradeInfo = getGradeFromRisk(summary.riskScore || 0);
                                                                    return (
                                                                        <span className="flex items-center gap-4">
                                                                            <span className={`px-2 py-0.5 border-2 rounded text-[0.625rem] font-bold ${gradeInfo.pass ? 'border-(--success) text-(--success)' : 'border-(--error) text-(--error)'}`}>{gradeInfo.grade}</span>
                                                                            <span className={`text-xs font-bold ${gradeInfo.pass ? 'text-(--success)' : 'text-(--error)'}`}>{gradeInfo.pass ? 'PASS' : 'FAIL'}</span>
                                                                            <span className="text-(--error)">RISK: {summary.riskScore}</span>
                                                                            <span className="text-(--success)">${summary.totalRiskCost.toLocaleString()}</span>
                                                                            <span>{summary.totalViolations} VIOLATIONS</span>
                                                                        </span>
                                                                    );
                                                                })()}
                                                            </>
                                                        )}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-4 shrink-0">
                                                    <span className={`px-4 py-1.5 rounded-full border text-xs font-bold flex items-center gap-2 ${status.bg} ${status.text} bg-transparent`}>
                                                        {status.icon} {status.label}
                                                    </span>
                                                    <button
                                                        onClick={() => handleVerifyFixes(scan)}
                                                        className="px-4 py-2 bg-(--surface-elevated) text-(--text) font-bold text-xs rounded-full hover:bg-(--surface-stone) border border-(--border) transition-colors"
                                                        title="RE-QUEUE"
                                                    >
                                                        Retry
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        ) : paginatedScans.length === 0 ? (
                            <div className="border border-(--border) border-dashed rounded-2xl p-24 text-center bg-white shadow-sm">
                                <p className="text-sm text-(--text-muted) font-bold">
                                    {scans.length === 0
                                        ? 'No scan history found. Start a new scan to see results here.'
                                        : 'No matching scans found. Try adjusting your filters.'}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {/* Master Controls & Pagination Header */}
                                {paginatedScans.length > 0 && (
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 bg-white p-5 rounded-2xl border border-(--border) shadow-sm mb-8">
                                        <div className="flex items-center gap-6">
                                            <label className="flex items-center gap-4 cursor-pointer group">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedScans.size === paginatedScans.length && paginatedScans.length > 0}
                                                    onChange={handleSelectAll}
                                                    className="w-5 h-5 cursor-pointer accent-(--accent) bg-white border border-(--border) rounded"
                                                />
                                                <span className="text-xs font-bold text-(--text-muted) group-hover:text-(--text) transition-colors">Select Page</span>
                                            </label>

                                            <div className="w-px h-6 bg-(--border) mx-2" />

                                            <div className="flex items-center gap-3">
                                                <button
                                                    onClick={() => setShowAgentsModal(true)}
                                                    className="px-4 py-2 bg-(--surface-elevated) text-(--text) font-bold text-xs rounded-full hover:bg-(--surface-stone) border border-(--border) transition-colors"
                                                >
                                                    Agent Status
                                                </button>

                                                <button
                                                    onClick={handleStopAllPending}
                                                    className="px-4 py-2 bg-(--fail-red)/10 text-(--fail-red) font-bold text-xs rounded-full hover:bg-(--fail-red)/20 transition-colors"
                                                    title="Remove all pending scans from the queue"
                                                >
                                                    Stop All Pending
                                                </button>

                                                {selectedScans.size > 0 && (
                                                    <button
                                                        onClick={handleBulkDelete}
                                                        className="px-4 py-2 bg-(--fail-red) text-white font-bold text-xs rounded-full hover:opacity-90 transition-colors shadow-[0_0_0.9375em_color-mix(in_srgb,var(--fail-red)_30%,transparent)]"
                                                    >
                                                        Delete Selected ({selectedScans.size})
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4 text-xs font-bold text-(--text-muted)">
                                            <span>Scans Per Page:</span>
                                            <select
                                                value={scansPerPage}
                                                onChange={(e) => {
                                                    setScansPerPage(e.target.value === 'All' ? 'All' : Number(e.target.value));
                                                }}
                                                className="bg-(--surface-stone) border border-(--border) py-2 px-4 cursor-pointer text-(--accent) focus:outline-none focus:border-(--accent) rounded-2xl shadow-sm"
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                                <option value="All">Show All</option>
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {groupByDomain && groupedScans ? (
                                    Object.entries(groupedScans).map(([domain, scans]) => {
                                        // Drawer State Persistence
                                        const drawerKey = `drawer_state_${domain}`;
                                        const isDrawerOpen = localStorage.getItem(drawerKey) !== 'closed';

                                        const handleToggle = (e) => {
                                            const isOpen = e.target.open;
                                            localStorage.setItem(drawerKey, isOpen ? 'open' : 'closed');
                                        };

                                        // Aggregate Domain Metrics
                                        const latestScanDate = scans.reduce((latest, scan) => {
                                            if (!scan.createdAt) return latest;
                                            const scanDate = scan.createdAt.toDate();
                                            return scanDate > latest ? scanDate : latest;
                                        }, new Date(0));

                                        // Safely format date, fallback if no valid date found (0 epoch)
                                        const dateStr = latestScanDate.getTime() > 0
                                            ? latestScanDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                                            : 'Unknown';

                                        // Aggregate Domain Metrics
                                        const domainResults = scans.map(scan => {
                                            let resultsData = scan.results || hydratedResults[scan.id];
                                            if (resultsData && typeof resultsData === 'string' && scan.dataIsCompressed) {
                                                try {
                                                    resultsData = JSON.parse(LZString.decompressFromUTF16(resultsData));
                                                } catch (e) {
                                                    console.warn('Failed to parse inner results for risk scoring', e);
                                                    return null;
                                                }
                                            }
                                            return resultsData;
                                        }).filter(Boolean);

                                        let highestRiskScore = 0;
                                        let totalRiskCost = 0;

                                        if (domainResults.length > 0) {
                                            const deduppedSummary = deduplicateViolations(domainResults);
                                            highestRiskScore = deduppedSummary.riskScore;
                                            totalRiskCost = deduppedSummary.totalRiskCost;
                                        } else {
                                            // Fallback to summing summaries if no results are hydrated
                                            scans.forEach(scan => {
                                                if (scan.summary) {
                                                    if (scan.summary.riskScore > highestRiskScore) highestRiskScore = scan.summary.riskScore;
                                                    if (scan.summary.totalRiskCost) totalRiskCost += scan.summary.totalRiskCost;
                                                }
                                            });
                                        }

                                        // Determine overall risk color
                                        let riskColor = 'text-green-500';
                                        if (highestRiskScore >= 80) riskColor = 'text-red-500';
                                        else if (highestRiskScore >= 40) riskColor = 'text-amber-500';
                                        else if (highestRiskScore > 0) riskColor = 'text-blue-500';

                                        return (
                                            <details
                                                key={domain}
                                                className="group relative bg-(--surface-stone) border border-(--border) rounded-2xl transition-all open:border-(--accent)/30 shadow-sm"
                                                open={isDrawerOpen}
                                                onToggle={handleToggle}
                                            >
                                                <summary className="sticky top-0 z-20 px-8 py-5 bg-white border-b border-(--border) cursor-pointer select-none list-none marker:hidden [&::-webkit-details-marker]:hidden flex flex-col sm:flex-row sm:items-center justify-between gap-5 hover:bg-(--accent)/5 transition-all">
                                                    <div className="flex items-center gap-5">
                                                        <div className="relative">
                                                            <span className="text-2xl transition-transform group-open:rotate-90 block">📂</span>
                                                        </div>
                                                        <div>
                                                            <span className="block text-sm font-bold text-(--text) group-hover:text-(--accent) transition-colors" title={domain}>{domain}</span>
                                                            <div className="flex items-center gap-3 mt-1">
                                                                <span className="text-xs font-bold text-(--accent) bg-(--accent)/10 px-3 py-1 rounded-full border border-(--accent)/20">
                                                                    {scans.length} Reports
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-8 text-xs font-bold text-(--text-muted)">
                                                        <div className="flex items-center gap-2 pr-6 border-r border-(--border)">
                                                            <span className="opacity-40 uppercase">Last Sync:</span>
                                                            <span className="text-(--text)">{dateStr}</span>
                                                        </div>
                                                        <div className="flex items-center gap-6">
                                                            <div className="flex items-center gap-2" title="MAX RISK LEVEL">
                                                                <span className="opacity-40 uppercase">Risk:</span>
                                                                <span className={`${highestRiskScore >= 80 ? 'text-(--error)' : 'text-(--success)'} `}>{highestRiskScore.toFixed(1)}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2" title="TOTAL EXPOSURE">
                                                                <span className="opacity-40 uppercase">Exp:</span>
                                                                <span className="text-(--text)">${totalRiskCost.toLocaleString()}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </summary>
                                                <div className="p-8 space-y-6 animate-in fade-in slide-in-from-top-2 duration-500">
                                                    {scans.map(scan => renderScanCard(scan))}
                                                </div>
                                            </details>
                                        );
                                    })
                                ) : (
                                    paginatedScans.map(scan => renderScanCard(scan))
                                )}

                                {/* Footer Pagination Controls */}
                                {scansPerPage !== 'All' && filteredScans.length > scansPerPage && (
                                    <div className="flex items-center justify-center gap-8 mt-12 pt-8 border-t border-(--border)">
                                        <button
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            className="bg-white border border-(--border) hover:border-(--accent) hover:text-(--accent) px-6 py-3 text-sm font-bold disabled:opacity-50 disabled:hover:border-(--border) disabled:hover:text-(--text) transition-all rounded-full flex items-center gap-2 shadow-sm"
                                        >
                                            <span className="text-lg">←</span> Previous
                                        </button>
                                        <div className="flex items-center gap-3 bg-(--surface-stone) px-6 py-3 rounded-full border border-(--border)">
                                            <span className="text-sm font-bold text-(--text-muted)">Page</span>
                                            <span className="text-sm font-bold text-(--accent)">
                                                {currentPage} <span className="text-(--text-muted) mx-1">of</span> {Math.ceil(filteredScans.length / scansPerPage)}
                                            </span>
                                        </div>
                                        <button
                                            disabled={currentPage >= Math.ceil(filteredScans.length / scansPerPage)}
                                            onClick={() => setCurrentPage(p => p + 1)}
                                            className="bg-white border border-(--border) hover:border-(--accent) hover:text-(--accent) px-6 py-3 text-sm font-bold disabled:opacity-50 disabled:hover:border-(--border) disabled:hover:text-(--text) transition-all rounded-full flex items-center gap-2 shadow-sm"
                                        >
                                            Next <span className="text-lg">→</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )
                        }
                    </section >
                )
            }


            {
                dashboardTab === 'journeys' && (
                    <section className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                            <div className="space-y-2">
                                <h2 className="text-2xl font-black uppercase tracking-[0.3em] text-(--accent) pl-2 border-l-4 border-(--accent) ">
                                    User Journeys
                                </h2>
                                <p className="text-[0.625rem] text-(--text-muted) font-bold uppercase tracking-widest opacity-60 ml-3">
                                    Manage recorded sequences for authenticated or complex session replays.
                                </p>
                            </div>

                            <div className="flex items-center gap-3 bg-(--surface-elevated)/40 border-2 border-(--border) rounded-none px-4 py-2">
                                <span className="text-[0.5625rem] font-black uppercase tracking-widest opacity-80">Sort By</span>
                                <select
                                    value={journeysSort.key}
                                    onChange={(e) => setJourneysSort(prev => ({ ...prev, key: e.target.value }))}
                                    className="bg-transparent text-[0.625rem] font-black uppercase tracking-widest outline-none cursor-pointer text-(--accent)"
                                    aria-label="Sort journeys by"
                                >
                                    <option value="createdAt">Date Created</option>
                                    <option value="name">Journey Name</option>
                                    <option value="domain">Target Domain</option>
                                </select>
                                <button
                                    onClick={() => setJourneysSort(prev => ({ ...prev, direction: prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                    className="text-[0.625rem] font-black hover:text-(--accent) transition-colors"
                                >
                                    {journeysSort.direction === 'desc' ? '↓' : '↑'}
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-8">
                            {journeysData.length === 0 ? (
                                <div className="neu-card p-24 text-center">
                                    <span className="text-4xl mb-6 block opacity-20 grayscale">🛣️</span>
                                    <p className="text-[0.625rem] text-(--text-muted) font-black uppercase tracking-[0.4em] opacity-40">
                                        No recorded journeys found. Use the companion extension to record a session.
                                    </p>
                                </div>
                            ) : (
                                journeysData.map((j) => (
                                    <div key={j.id} className="neu-card p-8 group hover:border-(--accent) transition-all">
                                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-10">
                                            <div className="space-y-3">
                                                <h3 className="text-lg font-black uppercase tracking-widest text-(--accent) flex items-center gap-3">
                                                    <span className="text-xl">🛣️</span>
                                                    {editingJourneyId === j.id ? (
                                                        <input
                                                            autoFocus
                                                            type="text"
                                                            value={tempJourneyName}
                                                            onChange={(e) => setTempJourneyName(e.target.value)}
                                                            onBlur={() => handleSaveJourneyName(j.id, tempJourneyName)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') handleSaveJourneyName(j.id, tempJourneyName);
                                                                if (e.key === 'Escape') setEditingJourneyId(null);
                                                            }}
                                                            className="bg-(--surface-elevated) border border-(--accent) rounded px-2 py-1 text-sm text-(--accent) outline-none"
                                                        />
                                                    ) : (
                                                        <div className="flex items-center gap-3 group/jname">
                                                            {j.name}
                                                            <button
                                                                onClick={() => {
                                                                    setEditingJourneyId(j.id);
                                                                    setTempJourneyName(j.name);
                                                                }}
                                                                className="opacity-0 group-hover/jname:opacity-100 transition-opacity text-[0.625rem]"
                                                                title="Rename Journey"
                                                            >
                                                                ✏️
                                                            </button>
                                                        </div>
                                                    )}
                                                </h3>
                                                <div className="flex flex-wrap items-center gap-5 text-[0.5625rem] font-black uppercase tracking-tighter text-(--text-muted)">
                                                    <span className="bg-(--surface-elevated) border border-(--border) px-2 py-0.5 rounded">URL: https://{j.domain}</span>
                                                    <span className="bg-(--surface-elevated) border border-(--border) px-2 py-0.5 rounded">Steps: {j.steps?.length || 0}</span>
                                                    <span className="bg-(--surface-elevated) border border-(--border) px-2 py-0.5 rounded">Created: {j.createdAt ? new Date(j.createdAt.seconds * 1000).toLocaleString().toUpperCase() : 'N/A'}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <button
                                                    onClick={() => {
                                                        setSelectedJourney(j);
                                                        setDashboardTab('new-scan');
                                                        setUrl(`https://${j.domain}`);
                                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                                    }}
                                                    className="bg-(--accent) text-black px-6 py-3 rounded-none text-[0.625rem] font-black uppercase tracking-widest hover:scale-[1.05] transition-all flex items-center gap-2 shadow-[0.25em_0.25em_0em_var(--border)]"
                                                >
                                                    ⚡ Use in New Scan
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setConfirmState({
                                                            isOpen: true,
                                                            title: 'Delete Journey',
                                                            message: `Are you sure you want to permanently delete the journey "${j.name}"?`,
                                                            onConfirm: async () => {
                                                                try {
                                                                    await deleteDoc(doc(db, 'users', user.uid, 'journeys', j.id));
                                                                    setToast({ message: 'Journey deleted.', type: 'info' });
                                                                } catch (err) {
                                                                    console.error("Failed to delete journey:", err);
                                                                }
                                                                setConfirmState(prev => ({ ...prev, isOpen: false }));
                                                            }
                                                        });
                                                    }}
                                                    className="bg-(--surface) border-2 border-(--error)/30 text-(--error)/50 px-6 py-3 rounded-none text-[0.625rem] font-black uppercase tracking-widest hover:bg-(--error)/10 hover:border-(--error) hover:text-(--error) transition-all"
                                                >
                                                    🗑️ Delete
                                                </button>
                                            </div>
                                        </div>

                                        {/* Steps visualization */}
                                        <div className="space-y-4">
                                            <p className="text-[0.625rem] font-black text-(--text-muted) uppercase tracking-widest opacity-40 ml-1">Interaction Sequence</p>
                                            <div className="bg-(--surface-elevated) rounded-none border border-(--border) p-1 flex flex-wrap gap-2">
                                                {j.steps?.map((step, idx) => {
                                                    const isSensitive = typeof step.value === 'string' && step.value.startsWith('enc:');
                                                    return (
                                                        <div key={idx} className="bg-(--surface-elevated) border border-(--border) rounded-none px-4 py-3 flex items-center gap-3 min-w-[7.5em]">
                                                            <span className="text-[0.5625rem] font-mono text-(--accent) opacity-40">#{idx + 1}</span>
                                                            <div className="flex flex-col">
                                                                <span className={`text-[0.625rem] font-black uppercase tracking-widest ${step.type === 'CLICK' ? 'text-(--accent)' : step.type === 'TYPE' ? 'text-(--secondary)' : 'text-(--text-muted)'}`}>
                                                                    {step.type}
                                                                </span>
                                                                <span className="text-[0.5rem] font-bold text-(--text-muted) truncate max-w-[9.375em]">
                                                                    {isSensitive ? '•••••••• (ENCRYPTED)' : (step.value || step.selector?.substring(0, 20) || 'ACTION')}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </section>
                )
            }



            {dashboardTab === 'pages' && (
                <section className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 px-2 border-b border-(--border) pb-8">
                        <div className="space-y-3">
                            <h2 className="text-4xl font-black text-(--text) tracking-tight">
                                Pages
                            </h2>
                            <p className="text-lg text-(--text-muted) max-w-2xl">
                                Manage and monitor accessibility health across all tracked domains and individual pages.
                            </p>
                            <div className="flex bg-(--surface-stone) p-1 rounded-full border border-(--border) w-fit mt-4">
                                <button
                                    onClick={() => setViewMode('active')}
                                    className={`relative z-10 px-6 py-2 text-xs font-bold uppercase tracking-widest transition-all rounded-full cursor-pointer ${viewMode === 'active' ? 'bg-(--accent) text-white shadow-sm' : 'text-(--text-muted) hover:text-(--text)'}`}
                                >
                                    Active
                                </button>
                                <button
                                    onClick={() => setViewMode('archived')}
                                    className={`relative z-10 px-6 py-2 text-xs font-bold uppercase tracking-widest transition-all rounded-full cursor-pointer ${viewMode === 'archived' ? 'bg-(--accent) text-white shadow-sm' : 'text-(--text-muted) hover:text-(--text)'}`}
                                >
                                    Archived
                                </button>
                            </div>
                        </div>


                    </div>
                    <div className="flex flex-wrap items-center gap-6 mt-4 px-2">
                        <div className="flex items-center gap-4 bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-3">
                            <span className="text-xs font-bold text-(--text-muted) uppercase tracking-wider">Sort By</span>
                            <select
                                value={pagesSort.key}
                                onChange={(e) => setPagesSort(prev => ({ ...prev, key: e.target.value }))}
                                className="bg-transparent text-sm font-bold outline-none cursor-pointer text-(--accent)"
                                aria-label="Sort pages by"
                            >
                                <option value="lastScan">Last Scan</option>
                                <option value="riskScore">Risk Score</option>
                                <option value="totalViolations">Violations Count</option>
                                <option value="url">URL Alphabetical</option>
                            </select>
                            <button
                                onClick={() => setPagesSort(prev => ({ ...prev, direction: prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                className="text-sm font-bold hover:text-(--accent) transition-colors flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/50"
                                title={pagesSort.direction === 'desc' ? 'Descending' : 'Ascending'}
                            >
                                {pagesSort.direction === 'desc' ? '↓' : '↑'}
                            </button>
                        </div>

                        <div className="relative group">
                            <input
                                type="text"
                                placeholder="Filter by URL or Domain..."
                                value={pagesSearchQuery}
                                onChange={(e) => setPagesSearchQuery(e.target.value)}
                                className="w-72 bg-(--surface-stone) border border-(--border) rounded-full px-6 py-3 pl-12 text-sm font-medium text-(--text) placeholder-(--text-muted)/50 focus:border-(--accent) focus:ring-2 focus:ring-(--accent)/5 focus:outline-none transition-all"
                            />
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg opacity-40 group-focus-within:opacity-100 transition-opacity">🔍</span>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => { setPagesSearchQuery(''); setPagesSort({ key: 'lastScan', direction: 'desc' }); }}
                                className="text-xs font-bold text-(--text-muted) hover:text-(--fail-red) uppercase tracking-widest px-4 py-2"
                            >
                                Reset Filters
                            </button>
                        </div>

                        {/* More Filters */}
                        <div className="flex items-center gap-4 bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-3">
                            <span className="text-xs font-bold text-(--text-muted) uppercase tracking-wider">Health Level</span>
                            <select
                                value={pagesSeverityFilter}
                                onChange={(e) => setPagesSeverityFilter(e.target.value)}
                                className="bg-transparent text-sm font-bold outline-none cursor-pointer text-(--accent)"
                            >
                                <option value="all">Balanced (All)</option>
                                <option value="critical">Critical Risk Only</option>
                                <option value="serious">Serious & Above</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-4 bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-3">
                            <span className="text-xs font-bold text-(--text-muted) uppercase tracking-wider">Domains</span>
                            <select
                                value={pagesSiteFilter}
                                onChange={(e) => setPagesSiteFilter(e.target.value)}
                                className="bg-transparent text-sm font-bold outline-none cursor-pointer text-(--accent)"
                            >
                                <option value="all">All Domains</option>
                                {(() => {
                                    const sites = new Set();
                                    scans.forEach(scan => {
                                        if (scan.status === 'completed' && scan.url) {
                                            if (viewMode === 'active' && !!scan.archived) return;
                                            if (viewMode === 'archived' && !scan.archived) return;
                                            try { sites.add(new URL(scan.url).hostname); } catch { }
                                        }
                                    });
                                    return Array.from(sites).sort().map(site => (
                                        <option key={site} value={site}>{site}</option>
                                    ));
                                })()}
                            </select>
                        </div>

                        <div className="flex items-center gap-3 bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-3">
                            <span className="text-xs font-bold text-(--text-muted) uppercase tracking-wider">Group by Domain</span>
                            <button
                                onClick={() => setGroupByDomain(!groupByDomain)}
                                className={`w-12 h-6 rounded-full transition-all relative ${groupByDomain ? 'bg-(--accent)' : 'bg-gray-300'}`}
                                aria-label={groupByDomain ? 'Ungroup domains' : 'Group by domain'}
                            >
                                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${groupByDomain ? 'left-7' : 'left-1'}`}></div>
                            </button>
                        </div>
                    </div>

                    {/* Bulk Action Bar (Overlay) */}
                    {
                        selectedPages.size > 0 && (
                            <div className="sticky top-4 z-[100] bg-(--accent) text-black rounded-none shadow-[0_0.625em_2.5em_rgba(var(--accent-rgb),0.3)] p-4 flex items-center justify-between border-2 border-white/20 animate-in slide-in-from-top-4 duration-300">
                                <div className="flex items-center gap-6 pl-4">
                                    <span className="text-xl">🛠️</span>
                                    <div>
                                        <p className="text-[0.625rem] font-black uppercase tracking-widest">Bulk Actions Active</p>
                                        <p className="text-[0.5625rem] font-bold opacity-70">[{selectedPages.size}] URLs selected in current fleet</p>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => {
                                            setConfirmState({
                                                isOpen: true,
                                                title: 'RESCAN SELECTION',
                                                message: `Initialize investigative audit for ${selectedPages.size} selected targets?`,
                                                onConfirm: async () => {
                                                    for (const url of selectedPages) {
                                                        await handleScan(url);
                                                    }
                                                    setSelectedPages(new Set());
                                                    setToast({ message: `Queued ${selectedPages.size} scans.`, type: 'success' });
                                                }
                                            });
                                        }}
                                        className="bg-(--surface) text-(--accent) border border-black px-6 py-2 rounded-none text-[0.5625rem] font-black uppercase tracking-widest hover:bg-(--surface-elevated) transition-all"
                                    >
                                        🔄 Start Audits
                                    </button>
                                    <button
                                        onClick={() => {
                                            setConfirmState({
                                                isOpen: true,
                                                title: 'PURGE RECORDS',
                                                message: `Permanently delete all data for ${selectedPages.size} selected URLs?`,
                                                onConfirm: async () => {
                                                    try {
                                                        // Find all scans associated with these URLs to purge them completely
                                                        for (const targetUrl of selectedPages) {
                                                            const matchingScans = scans.filter(s => s.url === targetUrl);
                                                            for (const scan of matchingScans) {
                                                                await deleteDoc(doc(db, 'users', user.uid, 'queue', scan.id));
                                                            }
                                                        }
                                                        setSelectedPages(new Set());
                                                        setToast({ message: `Purged all data for ${selectedPages.size} URLs.`, type: 'info' });
                                                    } catch (err) {
                                                        console.error("Failed to purge pages:", err);
                                                        setToast({ message: 'Error during system purge.', type: 'error' });
                                                    }
                                                    setConfirmState(prev => ({ ...prev, isOpen: false }));
                                                }
                                            });
                                        }}
                                        className="bg-transparent border border-black/20 text-black px-6 py-2 rounded-none text-[0.5625rem] font-black uppercase tracking-widest hover:bg-(--surface-elevated) transition-all"
                                    >
                                        🗑️ Purge
                                    </button>
                                    <button
                                        onClick={() => setSelectedPages(new Set())}
                                        className="text-[0.5625rem] font-black uppercase tracking-widest px-4 hover:opacity-100 opacity-60 transition-all"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )
                    }

                    <div className="neu-card border-(--border) overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-(--surface-elevated) border-b-2 border-(--border)">
                                    <tr>
                                        <th className="p-5 w-fit text-left">
                                            <label className="flex items-center gap-4 cursor-pointer group">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedPages.size > 0 && selectedPages.size === paginatedPages.length}
                                                    onChange={() => {
                                                        if (selectedPages.size === paginatedPages.length) setSelectedPages(new Set());
                                                        else setSelectedPages(new Set(paginatedPages.map(p => p.url)));
                                                    }}
                                                    className="w-5 h-5 accent-(--accent) cursor-pointer bg-(--surface-elevated) border-2 border-(--border) rounded"
                                                />
                                                <span className="text-[0.625rem] font-black text-(--text-muted) group-hover:text-(--text) uppercase tracking-widest transition-colors">Select Page</span>
                                            </label>
                                        </th>
                                        {[
                                            { label: 'Health Status', key: 'riskScore', align: 'text-left' },
                                            { label: 'Page URI', key: 'url', align: 'text-left' },
                                            { label: 'Compliance Node', key: 'hostname', align: 'text-left' },
                                            { label: 'Violations', key: 'totalViolations', align: 'text-center' },
                                            { label: 'Risk Cost', key: 'riskScore', align: 'text-center' },
                                            { label: 'Last Scan', key: 'lastScan', align: 'text-right' },
                                        ].map(col => (
                                            <th
                                                key={col.label}
                                                onClick={col.key ? () => setPagesSort(prev => prev.key === col.key ? { ...prev, direction: prev.direction === 'desc' ? 'asc' : 'desc' } : { key: col.key, direction: 'desc' }) : undefined}
                                                className={`p-5 text-[0.5625rem] font-black uppercase tracking-widest opacity-80 ${col.align} ${col.key ? 'cursor-pointer hover:opacity-100 hover:text-(--accent) transition-all select-none' : ''}`}
                                            >
                                                {col.label}
                                                {col.key && pagesSort.key === col.key && (
                                                    <span className="ml-1 text-(--accent)">{pagesSort.direction === 'desc' ? '▼' : '▲'}</span>
                                                )}
                                            </th>
                                        ))}
                                        <th className="p-5 w-24"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-(--border)/30">
                                    {paginatedPages.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" className="p-20 text-center">
                                                <span className="text-4xl mb-4 block opacity-10 grayscale">📄</span>
                                                <p className="text-[0.625rem] text-(--text-muted) font-black uppercase tracking-[0.4em] opacity-40">
                                                    No deployment signals found matching active filters.
                                                </p>
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedPages.map((page) => {
                                            const score = page.summary?.riskScore || 0;
                                            const isClean = score === 0;
                                            let pageUrl;
                                            try {
                                                pageUrl = new URL(page.url);
                                            } catch (e) {
                                                pageUrl = { hostname: 'Invalid URL', pathname: page.url };
                                            }

                                            return (
                                                <tr key={page.id} className="group hover:bg-(--accent)/5 transition-colors">
                                                    <td className="p-5 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedPages.has(page.url)}
                                                            onChange={() => {
                                                                const next = new Set(selectedPages);
                                                                if (next.has(page.url)) next.delete(page.url);
                                                                else next.add(page.url);
                                                                setSelectedPages(next);
                                                            }}
                                                            className="w-5 h-5 accent-(--accent) cursor-pointer bg-(--surface-elevated) border-2 border-(--border)"
                                                        />
                                                    </td>
                                                    <td className="p-5">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-2 h-2 rounded-none shadow-[0.25em_0.25em_0em_var(--border)] ${score > 75 ? 'bg-(--error)' : score > 40 ? 'bg-orange-500' : isClean ? 'bg-(--success)' : 'bg-(--accent)'}`} />
                                                            <span className={`text-[0.5625rem] font-black border px-2 py-0.5 rounded ${score > 75 ? 'border-(--error)/30 text-(--error)' : score > 40 ? 'border-orange-500/30 text-orange-500' : isClean ? 'border-(--success)/30 text-(--success)' : 'border-(--accent)/30 text-(--accent)'}`}>
                                                                {isClean ? 'HEALTHY' : `SCORE: ${Math.round(score)}`}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="p-5">
                                                        <div className="flex flex-col max-w-xs md:max-w-md">
                                                            <span className="text-[0.6875rem] font-black truncate text-(--text) group-hover:text-(--accent) transition-colors" title={page.url}>
                                                                {pageUrl.pathname}
                                                            </span>
                                                            <span className="text-[0.5rem] font-bold text-(--text-muted) opacity-40 uppercase tracking-widest truncate">
                                                                {page.url}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="p-5">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[0.625rem] opacity-40 grayscale group-hover:grayscale-0 transition-grayscale">🌐</span>
                                                            <span className="text-[0.625rem] font-black uppercase tracking-widest text-(--text-muted) group-hover:text-(--text) transition-colors">
                                                                {pageUrl.hostname}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="p-5 text-center">
                                                        <span className={`text-[0.6875rem] font-black ${page.summary?.totalViolations > 0 ? 'text-(--accent)' : 'text-(--text-muted) opacity-20'}`}>
                                                            {page.summary?.totalViolations || 0}
                                                        </span>
                                                    </td>
                                                    <td className="p-5 text-center">
                                                        <span className={`text-[0.6875rem] font-black ${page.summary?.totalRiskCost > 0 ? 'text-(--error)' : 'text-(--text-muted) opacity-20'}`}>
                                                            ${(page.summary?.totalRiskCost || 0).toLocaleString()}
                                                        </span>
                                                    </td>
                                                    <td className="p-5 text-right whitespace-nowrap">
                                                        <span className="text-[0.5625rem] font-mono font-bold opacity-60">
                                                            {page.createdAt?.toDate().toLocaleString() || 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td className="p-5 text-right">
                                                        <div className="flex gap-2 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <button
                                                                onClick={() => { setExpandedScan(page.id); setDashboardTab('scans'); }}
                                                                className="p-2 rounded-none bg-(--surface-elevated) border border-(--border) hover:border-(--accent) text-(--accent) text-[0.625rem]"
                                                                title="View Full Audit"
                                                            >
                                                                📋
                                                            </button>
                                                            <button
                                                                onClick={() => { setUrl(page.url); setDashboardTab('new-scan'); }}
                                                                className="p-2 rounded-none bg-(--accent) text-black text-[0.625rem]"
                                                                title="Re-Scan Target"
                                                            >
                                                                🔄
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination Controls */}
                    {
                        pagesData.length > 0 && (
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-2 pt-4">
                                <div className="flex items-center gap-4">
                                    <span className="text-[0.5625rem] font-black uppercase tracking-widest text-(--text-muted) opacity-60">
                                        Showing {paginatedPages.length} of {pagesData.length} pages
                                    </span>
                                    <div className="flex items-center gap-2 bg-(--surface-elevated) border-2 border-(--border) rounded-none px-3 py-1.5">
                                        <span className="text-[0.5625rem] font-black uppercase tracking-widest opacity-80">Per Page</span>
                                        <select
                                            value={pagesPerPage}
                                            onChange={(e) => setPagesPerPage(e.target.value === 'All' ? 'All' : Number(e.target.value))}
                                            className="bg-transparent text-[0.625rem] font-black uppercase tracking-widest outline-none cursor-pointer text-(--accent)"
                                            aria-label="Pages per page selector"
                                        >
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value="All">All</option>
                                        </select>
                                    </div>
                                </div>
                                {pagesPerPage !== 'All' && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setPagesCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={pagesCurrentPage <= 1}
                                            className="px-4 py-2 text-[0.5625rem] font-black uppercase tracking-widest bg-(--surface-elevated) border-2 border-(--border) hover:border-(--accent) disabled:opacity-20 disabled:cursor-not-allowed transition-colors cursor-pointer"
                                        >
                                            ← Prev
                                        </button>
                                        <span className="text-[0.625rem] font-black uppercase tracking-widest text-(--text-muted) px-3">
                                            Page {pagesCurrentPage} of {Math.ceil(pagesData.length / pagesPerPage)}
                                        </span>
                                        <button
                                            onClick={() => setPagesCurrentPage(p => Math.min(Math.ceil(pagesData.length / pagesPerPage), p + 1))}
                                            disabled={pagesCurrentPage >= Math.ceil(pagesData.length / pagesPerPage)}
                                            className="px-4 py-2 text-[0.5625rem] font-black uppercase tracking-widest bg-(--surface-elevated) border-2 border-(--border) hover:border-(--accent) disabled:opacity-20 disabled:cursor-not-allowed transition-colors cursor-pointer"
                                        >
                                            Next →
                                        </button>
                                    </div>
                                )}
                            </div>
                        )
                    }
                </section >
            )
            }

            {
                dashboardTab === 'issues' && (
                    <section className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {selectedViolationRuleId ? (() => {
                            const ruleData = issuesData.rules[selectedViolationRuleId];
                            const allPages = Object.entries(ruleData?.pages || {});
                            // Compute unique sites for this specific rule's pages
                            const detailSites = [...new Set(allPages.map(([url]) => { try { return new URL(url).hostname; } catch { return null; } }).filter(Boolean))].sort();
                            // Filter pages if site or page filter is active
                            let filteredPages = allPages;
                            if (issuesSiteFilter.length > 0) {
                                filteredPages = filteredPages.filter(([url]) => {
                                    try { return issuesSiteFilter.includes(new URL(url).hostname); } catch { return false; }
                                });
                            }
                            if (issuesPageFilter.length > 0) {
                                filteredPages = filteredPages.filter(([url]) => issuesPageFilter.includes(url));
                            }
                            // Local URL search within detail view
                            if (issuesSearchQuery) {
                                filteredPages = filteredPages.filter(([url]) => url.toLowerCase().includes(issuesSearchQuery.toLowerCase()));
                            }
                            const estimatedCost = (VIOLATION_COSTS[ruleData?.impact] || 500) * (ruleData?.totalCount || 0);
                            const pagesAffected = filteredPages.length;
                            const hasActiveFilters = issuesSiteFilter.length > 0 || issuesPageFilter.length > 0 || issuesSearchQuery;

                            // Export helpers
                            const exportCSV = () => {
                                const header = 'URL,Issues\n';
                                const rows = filteredPages.sort((a, b) => b[1] - a[1]).map(([url, count]) => `"${url}",${count}`).join('\n');
                                const csv = header + rows;
                                const blob = new Blob([csv], { type: 'text/csv' });
                                const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
                                a.download = `${selectedViolationRuleId}-affected-pages.csv`; a.click();
                            };
                            const exportJSON = () => {
                                const data = { ruleId: selectedViolationRuleId, impact: ruleData?.impact, totalInstances: ruleData?.totalCount, pages: filteredPages.map(([url, count]) => ({ url, issues: count })) };
                                const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                                const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
                                a.download = `${selectedViolationRuleId}-detail.json`; a.click();
                            };

                            return (
                                <div className="space-y-6">
                                    {/* Back Button */}
                                    <div className="flex items-center gap-4">
                                        <button
                                            onClick={() => { setSelectedViolationRuleId(null); setShowIssuePrompt(false); }}
                                            className="group px-8 py-4 rounded-2xl bg-(--surface-stone) hover:bg-(--surface-elevated) border border-(--border) text-(--text) text-[0.6875rem] font-black uppercase tracking-widest flex items-center gap-3 transition-all hover:border-(--accent)/30 cursor-pointer shadow-sm"
                                        >
                                            <span className="text-xl group-hover:-translate-x-1 transition-transform">←</span> Back to Issues
                                        </button>
                                    </div>

                                    {/* Detail View Filter & Export Toolbar */}
                                    <div className="bento-card !p-3 flex flex-wrap items-center gap-4">
                                        {/* Site Filter */}
                                        <div className="flex items-center gap-3 bg-(--surface-elevated) border border-(--border) px-6 py-2.5 rounded-xl shadow-sm">
                                            <span className="text-[0.625rem] font-black uppercase tracking-widest text-(--text-muted) opacity-60">Site</span>
                                            <MultiSelectDropdown
                                                options={detailSites}
                                                value={issuesSiteFilter}
                                                onChange={setIssuesSiteFilter}
                                                placeholder="All Sites"
                                                className="min-w-[9.375em]"
                                            />
                                        </div>
                                        {/* URL Search */}
                                        <div className="relative flex-1 min-w-[18.75em] group">
                                            <input
                                                type="text"
                                                placeholder="Search Affected URLs..."
                                                value={issuesSearchQuery}
                                                onChange={(e) => setIssuesSearchQuery(e.target.value)}
                                                className="w-full bg-(--surface-elevated) border border-(--border) rounded-xl px-6 py-3 pl-14 text-[0.6875rem] font-bold uppercase tracking-widest text-(--text) placeholder-(--text-muted)/40 focus:border-(--accent) focus:ring-2 focus:ring-(--accent)/5 focus:outline-none transition-all shadow-sm"
                                            />
                                            <span className="absolute left-6 top-1/2 -translate-y-1/2 text-xl opacity-40 group-focus-within:opacity-100 transition-opacity">🔍</span>
                                        </div>
                                        {/* Clear Filters */}
                                        {hasActiveFilters && (
                                            <button
                                                onClick={() => { setIssuesSiteFilter([]); setIssuesPageFilter([]); setIssuesSearchQuery(''); }}
                                                className="text-[0.625rem] font-black uppercase tracking-widest text-(--fail-red) hover:brightness-110 transition-all px-6 py-3 rounded-xl border border-(--fail-red)/20 bg-(--fail-red)/5 shadow-sm cursor-pointer"
                                            >
                                                ✕ Reset
                                            </button>
                                        )}
                                        {/* Export Actions */}
                                        <div className="flex items-center gap-1 bg-(--surface-elevated) px-2 py-1.5 rounded-xl border border-(--border) shadow-sm">
                                            <button
                                                onClick={() => {
                                                    if (!planLimits?.allowSharing) {
                                                        setUpgradeModal({
                                                            isOpen: true,
                                                            title: 'Export Locked',
                                                            message: 'Exporting raw accessibility data to CSV is only available on premium plans. Upgrade to download and share findings with your team.'
                                                        });
                                                        return;
                                                    }
                                                    exportCSV();
                                                }}
                                                className={`text-[0.625rem] font-black uppercase tracking-widest px-4 py-2 hover:bg-(--surface-stone) rounded-lg transition-all flex items-center gap-2 cursor-pointer ${!planLimits?.allowSharing ? 'text-(--text-muted) opacity-50' : 'text-(--text-muted) hover:text-(--accent)'}`}
                                                title={!planLimits?.allowSharing ? "Export filtered pages as CSV (Locked)" : "Export filtered pages as CSV"}
                                            >
                                                <span>{!planLimits?.allowSharing ? '🔒' : '📥'}</span> CSV
                                            </button>
                                            <div className="w-px h-5 bg-(--border)"></div>
                                            <button
                                                onClick={() => {
                                                    if (!planLimits?.allowSharing) {
                                                        setUpgradeModal({
                                                            isOpen: true,
                                                            title: 'Export Locked',
                                                            message: 'Exporting raw accessibility data to JSON is only available on premium plans. Upgrade to download and integrate findings with your tools.'
                                                        });
                                                        return;
                                                    }
                                                    exportJSON();
                                                }}
                                                className={`text-[0.625rem] font-black uppercase tracking-widest px-4 py-2 hover:bg-(--surface-stone) rounded-lg transition-all flex items-center gap-2 cursor-pointer ${!planLimits?.allowSharing ? 'text-(--text-muted) opacity-50' : 'text-(--text-muted) hover:text-(--accent)'}`}
                                                title={!planLimits?.allowSharing ? "Export violation detail as JSON (Locked)" : "Export violation detail as JSON"}
                                            >
                                                <span>{!planLimits?.allowSharing ? '🔒' : '📥'}</span> JSON
                                            </button>
                                        </div>
                                    </div>

                                    <div className="bento-card bento-card-hover bg-(--surface-elevated) !p-12 md:!p-16">
                                        <div className="flex flex-wrap items-center gap-4 mb-8">
                                            <span className={`text-[0.625rem] font-black px-4 py-2 rounded-full border uppercase tracking-[0.2em] shadow-sm ${ruleData?.impact === 'critical' ? 'bg-(--fail-red)/10 text-(--fail-red) border-(--fail-red)/20' :
                                                ruleData?.impact === 'serious' ? 'bg-orange-500/10 text-orange-500 border-orange-500/20' :
                                                    ruleData?.impact === 'moderate' ? 'bg-(--warn-amber)/10 text-(--warn-amber) border-(--warn-amber)/20' :
                                                        'bg-(--accent)/10 text-(--accent) border-(--accent)/20'
                                                }`}>
                                                {ruleData?.impact?.toUpperCase() || 'UNKNOWN'} SEVERITY
                                            </span>
                                            <span className="text-[0.625rem] font-black text-(--text-muted) uppercase tracking-widest bg-(--surface-stone) px-4 py-2 rounded-full border border-(--border) shadow-sm">
                                                Rule ID: {selectedViolationRuleId}
                                            </span>
                                        </div>
                                        <h2 className="text-4xl md:text-5xl font-black text-(--text) uppercase tracking-tighter mb-8 leading-[1.1] break-words">
                                            {selectedViolationRuleId.replace(/-/g, ' ')}
                                        </h2>
                                        {ruleData?.description && (
                                            <div className="flex gap-6 items-start">
                                                <div className="w-1.5 h-12 bg-(--accent) rounded-full hidden md:block shrink-0" />
                                                <p className="text-xl md:text-2xl text-(--text-muted) leading-relaxed max-w-4xl font-medium opacity-90">
                                                    {ruleData.description}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Stats Grid */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                        <div className="bento-card bento-card-hover flex flex-col justify-between p-8 group">
                                            <p className="text-[0.625rem] font-black uppercase text-(--text-muted) tracking-widest mb-10 opacity-60 group-hover:opacity-100 transition-opacity">Total Instances</p>
                                            <h4 className="text-5xl font-black text-(--accent) tracking-tighter">{ruleData?.totalCount?.toLocaleString() || 0}</h4>
                                        </div>
                                        <div className="bento-card bento-card-hover flex flex-col justify-between p-8 group">
                                            <p className="text-[0.625rem] font-black uppercase text-(--text-muted) tracking-widest mb-10 opacity-60 group-hover:opacity-100 transition-opacity">Pages Affected</p>
                                            <h4 className="text-5xl font-black text-(--text) tracking-tighter">{pagesAffected}</h4>
                                        </div>
                                        <div className="bento-card bento-card-hover flex flex-col justify-between p-8 group">
                                            <p className="text-[0.625rem] font-black uppercase text-(--text-muted) tracking-widest mb-10 opacity-60 group-hover:opacity-100 transition-opacity">Cost per Instance</p>
                                            <h4 className="text-5xl font-black text-(--warn-amber) tracking-tighter">${(VIOLATION_COSTS[ruleData?.impact] || 500).toLocaleString()}</h4>
                                        </div>
                                        <div className="bento-card bento-card-hover !bg-(--fail-red)/5 !border-(--fail-red)/20 flex flex-col justify-between p-8 group transition-all">
                                            <p className="text-[0.625rem] font-black uppercase text-(--fail-red)/60 tracking-widest mb-10 group-hover:text-(--fail-red) transition-colors">Est. Risk Exposure</p>
                                            <h4 className="text-5xl font-black text-(--fail-red) tracking-tighter">${estimatedCost.toLocaleString()}</h4>
                                        </div>
                                    </div>

                                    {/* How to Fix Section */}
                                    <div className="bento-card !bg-(--surface-elevated) !p-0 overflow-hidden">
                                        <div className="px-10 py-6 border-b border-(--border) flex items-center gap-5 bg-(--surface-stone)/30">
                                            <div className="w-12 h-12 bg-(--pass-green)/10 text-(--pass-green) flex items-center justify-center rounded-2xl text-xl shadow-inner border border-(--pass-green)/10">🔧</div>
                                            <div>
                                                <h3 className="text-sm font-black uppercase tracking-widest text-(--text)">Remediation Engine</h3>
                                                <p className="text-[0.625rem] font-bold text-(--text-muted) uppercase tracking-wider opacity-60 mt-0.5">Strategic fix protocols</p>
                                            </div>
                                        </div>
                                        <div className="p-10 space-y-10">
                                            <p className="text-xl font-medium text-(--text-muted) leading-relaxed max-w-4xl">
                                                {ruleData?.description || `Fix all instances of "${selectedViolationRuleId.replace(/-/g, ' ')}" violations across your pages.`}
                                            </p>
                                            <div className="h-px bg-(--border) w-full opacity-50"></div>
                                            <div className="grid md:grid-cols-2 gap-12">
                                                <div className="space-y-6">
                                                    <p className="text-[0.625rem] font-black uppercase tracking-widest text-(--accent) flex items-center gap-3">
                                                        <span className="w-1.5 h-1.5 bg-(--accent) rounded-full"></span>
                                                        Remediation Checklist
                                                    </p>
                                                    <ul className="space-y-4">
                                                        {(selectedViolationRuleId.includes('color-contrast') || selectedViolationRuleId === 'color-contrast') && (
                                                            <>
                                                                <li className="flex items-start gap-4 text-(--text-muted) font-medium">
                                                                    <div className="w-5 h-5 bg-(--pass-green) text-white rounded-full flex items-center justify-center text-[0.625rem] flex-shrink-0 mt-0.5 shadow-sm">✓</div>
                                                                    <span className="text-base">Ensure text has at least 4.5:1 contrast ratio against background</span>
                                                                </li>
                                                                <li className="flex items-start gap-4 text-(--text-muted) font-medium">
                                                                    <div className="w-5 h-5 bg-(--pass-green) text-white rounded-full flex items-center justify-center text-[0.625rem] flex-shrink-0 mt-0.5 shadow-sm">✓</div>
                                                                    <span className="text-base">Use a contrast checker tool to verify color combinations</span>
                                                                </li>
                                                                <li className="flex items-start gap-4 text-(--text-muted) font-medium">
                                                                    <div className="w-5 h-5 bg-(--pass-green) text-white rounded-full flex items-center justify-center text-[0.625rem] flex-shrink-0 mt-0.5 shadow-sm">✓</div>
                                                                    <span className="text-base">Avoid light gray text on white backgrounds</span>
                                                                </li>
                                                            </>
                                                        )}
                                                        {(selectedViolationRuleId.includes('image-alt') || selectedViolationRuleId === 'image-alt') && (
                                                            <>
                                                                <li className="flex items-start gap-4 text-(--text-muted) font-medium">
                                                                    <div className="w-5 h-5 bg-(--pass-green) text-white rounded-full flex items-center justify-center text-[0.625rem] flex-shrink-0 mt-0.5 shadow-sm">✓</div>
                                                                    <span className="text-base">Add descriptive alt text to all images</span>
                                                                </li>
                                                                <li className="flex items-start gap-4 text-(--text-muted) font-medium">
                                                                    <div className="w-5 h-5 bg-(--pass-green) text-white rounded-full flex items-center justify-center text-[0.625rem] flex-shrink-0 mt-0.5 shadow-sm">✓</div>
                                                                    <span className="text-base">Use empty alt="" for decorative images</span>
                                                                </li>
                                                            </>
                                                        )}
                                                        {(selectedViolationRuleId.includes('label') || selectedViolationRuleId === 'label') && (
                                                            <>
                                                                <li className="flex items-start gap-4 text-(--text-muted) font-medium">
                                                                    <div className="w-5 h-5 bg-(--pass-green) text-white rounded-full flex items-center justify-center text-[0.625rem] flex-shrink-0 mt-0.5 shadow-sm">✓</div>
                                                                    <span className="text-base">Associate labels with form inputs using for/id</span>
                                                                </li>
                                                            </>
                                                        )}
                                                        {!selectedViolationRuleId.includes('color-contrast') && !selectedViolationRuleId.includes('image-alt') && !selectedViolationRuleId.includes('label') && (
                                                            <>
                                                                <li className="flex items-start gap-4 text-(--text-muted) font-medium">
                                                                    <div className="w-5 h-5 bg-(--pass-green) text-white rounded-full flex items-center justify-center text-[0.625rem] flex-shrink-0 mt-0.5 shadow-sm">✓</div>
                                                                    <span className="text-base">Review each affected element in context</span>
                                                                </li>
                                                                <li className="flex items-start gap-4 text-(--text-muted) font-medium">
                                                                    <div className="w-5 h-5 bg-(--pass-green) text-white rounded-full flex items-center justify-center text-[0.625rem] flex-shrink-0 mt-0.5 shadow-sm">✓</div>
                                                                    <span className="text-base">Follow WCAG 2.1 AA guidelines for remediation</span>
                                                                </li>
                                                            </>
                                                        )}
                                                    </ul>
                                                </div>
                                                <div className="space-y-6">
                                                    <p className="text-[0.625rem] font-black uppercase tracking-widest text-(--text-muted)">Technical Resources</p>
                                                    {ruleData?.helpUrl && (
                                                        <a
                                                            href={ruleData.helpUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="w-full flex items-center justify-between p-6 bg-(--surface-stone) hover:bg-(--surface-elevated) border border-(--border) rounded-2xl transition-all group hover:border-(--accent)/30"
                                                        >
                                                            <div className="flex items-center gap-5">
                                                                <span className="text-3xl">📚</span>
                                                                <div>
                                                                    <span className="text-xs font-black uppercase tracking-widest text-(--text) block">Official Documentation</span>
                                                                    <span className="text-[0.5625rem] font-bold text-(--text-muted) uppercase tracking-wider opacity-60">WCAG 2.1 Technical Specs</span>
                                                                </div>
                                                            </div>
                                                            <span className="text-(--accent) group-hover:translate-x-1 transition-transform text-xl">→</span>
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* AI Fix Prompt */}
                                    <div className="bento-card !bg-(--surface-elevated) !p-0 overflow-hidden shadow-sm">
                                        <div className="px-10 py-6 border-b border-(--border) flex items-center justify-between bg-(--accent)/5">
                                            <div className="flex items-center gap-5">
                                                <div className="w-12 h-12 bg-(--accent)/10 text-(--accent) flex items-center justify-center rounded-2xl text-xl shadow-inner border border-(--accent)/10">🤖</div>
                                                <div>
                                                    <h3 className="text-sm font-black uppercase tracking-widest text-(--accent)">AI Remediation Logic</h3>
                                                    <p className="text-[0.625rem] font-bold text-(--accent)/60 uppercase tracking-wider mt-0.5">Synthetic Fix Generator</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setShowIssuePrompt(!showIssuePrompt)}
                                                className="text-[0.625rem] font-black uppercase tracking-widest text-(--accent) hover:text-white hover:bg-(--accent) transition-all cursor-pointer bg-(--surface-elevated) px-6 py-2.5 border border-(--accent)/30 rounded-xl"
                                            >
                                                {showIssuePrompt ? 'Hide Logic' : 'Access Logic'}
                                            </button>
                                        </div>
                                        {showIssuePrompt && (() => {
                                            const promptPages = filteredPages.map(([url]) => url);
                                            const urlListing = promptPages.length > 0
                                                ? `\n**Affected Pages${issuesPageFilter.length > 0 || issuesSiteFilter.length > 0 ? ' (filtered)' : ''}:**\n${promptPages.map((url, i) => `  ${i + 1}. ${url}`).join('\n')}`
                                                : '';
                                            const prompt = `Fix this WCAG 2.1 AA accessibility violation in my code:\n\n**Issue:** ${ruleData?.description || selectedViolationRuleId.replace(/-/g, ' ')}\n**Rule ID:** ${selectedViolationRuleId}\n**Severity:** ${ruleData?.impact || 'unknown'}\n**Total Instances:** ${ruleData?.totalCount || 0}${urlListing}\n\n**How to Fix:** ${ruleData?.description || `Fix all instances of "${selectedViolationRuleId.replace(/-/g, ' ')}" violations across your pages.`}\n\nPlease review my code and identify elements that violate this rule. Show me the corrected HTML/JSX to be WCAG 2.1 Level AA compliant.`;
                                            return (
                                                <div className="p-10 space-y-6 animate-in slide-in-from-top-4 duration-300">
                                                    <div className="relative group">
                                                        <div className="absolute top-4 right-4 z-10">
                                                            <span className="text-[0.5625rem] font-black text-(--accent)/40 uppercase tracking-widest bg-(--surface-stone) px-3 py-1 rounded-md border border-(--border)">Context-Aware Prompt</span>
                                                        </div>
                                                        <pre className="text-sm bg-(--surface-stone) border border-(--border) p-8 text-(--text) overflow-x-auto whitespace-pre-wrap rounded-2xl font-mono leading-relaxed max-h-[31.25em] shadow-inner">
                                                            {prompt}
                                                        </pre>
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(prompt);
                                                            setIssueCopied(true);
                                                            setTimeout(() => setIssueCopied(false), 2000);
                                                        }}
                                                        className={`w-full md:w-auto px-10 py-4 rounded-full text-[0.625rem] font-black uppercase tracking-[0.2em] transition-all transform active:scale-95 cursor-pointer ${issueCopied
                                                            ? 'bg-(--pass-green) text-white'
                                                            : 'bg-(--accent) text-white hover:brightness-110 shadow-lg shadow-(--accent)/20'}`}
                                                    >
                                                        {issueCopied ? '✓ Copied to Mission Control' : '📋 Copy Remediation Prompt'}
                                                    </button>
                                                </div>
                                            );
                                        })()}
                                    </div>

                                    {/* Risk Assessment */}
                                    <div className="bento-card bento-card-hover !bg-(--fail-red)/5 !border-(--fail-red)/10 p-10">
                                        <div className="flex items-center gap-5 mb-10">
                                            <div className="w-12 h-12 bg-(--fail-red)/10 text-(--fail-red) flex items-center justify-center rounded-2xl text-2xl shadow-inner border border-(--fail-red)/10">⚠️</div>
                                            <div>
                                                <h3 className="text-sm font-black uppercase tracking-widest text-(--fail-red)">Risk Assessment</h3>
                                                <p className="text-[0.625rem] font-bold text-(--fail-red)/60 uppercase tracking-wider mt-0.5">Legal Liability Surface</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                                            <div className="space-y-4">
                                                <p className="text-[0.625rem] font-black uppercase tracking-widest text-(--fail-red) opacity-60 flex items-center gap-3">
                                                    <span className="w-1.5 h-1.5 bg-(--fail-red) rounded-full"></span>
                                                    Legal Exposure
                                                </p>
                                                <p className="text-lg text-(--text-muted) leading-relaxed font-medium">
                                                    {ruleData?.impact === 'critical' ?
                                                        'Critical violations pose immediate legal risk. They completely block access for users with disabilities and are top priority for ADA and Section 508 audits.' :
                                                        ruleData?.impact === 'serious' ?
                                                            'Serious violations significantly impair accessibility. They often result in legal complaints and should be prioritized for remediation.' :
                                                            ruleData?.impact === 'moderate' ?
                                                                'Moderate violations degrade user experience for many users. While less urgent, they represent compliance gaps that should be addressed.' :
                                                                'Minor violations are typically best practice issues. Fixing them improves overall accessibility but poses lower legal risk.'}
                                                </p>
                                            </div>
                                            <div className="space-y-4">
                                                <p className="text-[0.625rem] font-black uppercase tracking-widest text-(--fail-red) opacity-60 flex items-center gap-3">
                                                    <span className="w-1.5 h-1.5 bg-(--fail-red) rounded-full"></span>
                                                    User Impact
                                                </p>
                                                <p className="text-lg text-(--text-muted) leading-relaxed font-medium">
                                                    This issue affects {ruleData?.totalCount || 0} element{(ruleData?.totalCount || 0) !== 1 ? 's' : ''} across {pagesAffected} page{pagesAffected !== 1 ? 's' : ''}.
                                                    Users relying on assistive technologies may experience difficulty or complete barriers when interacting with affected content.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Affected Pages */}
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between px-2">
                                            <h3 className="text-[0.6875rem] font-black uppercase tracking-[0.3em] text-(--text-muted) flex items-center gap-4">
                                                <span className="w-2 h-8 bg-(--accent) rounded-full"></span>
                                                Affected Pages ({pagesAffected})
                                                {(issuesPageFilter.length > 0 || issuesSiteFilter.length > 0) && (
                                                    <span className="text-[0.5625rem] font-black text-(--accent) bg-(--accent)/10 px-3 py-1 rounded-full border border-(--accent)/20">
                                                        FILTERED
                                                    </span>
                                                )}
                                            </h3>
                                        </div>
                                        <div className="grid gap-4">
                                            {filteredPages
                                                .sort((a, b) => b[1] - a[1])
                                                .map(([url, count], idx) => (
                                                    <div key={url} className="bento-card bento-card-hover !p-5 flex items-center justify-between gap-6 group">
                                                        <div className="flex items-center gap-5 min-w-0 flex-1">
                                                            <span className="text-[0.6875rem] font-black text-(--accent) bg-(--accent)/5 w-10 h-10 flex items-center justify-center rounded-xl border border-(--accent)/10 shrink-0 group-hover:bg-(--accent) group-hover:text-white transition-all shadow-inner">
                                                                {idx + 1}
                                                            </span>
                                                            <div className="min-w-0">
                                                                <p className="text-base font-bold text-(--text) truncate group-hover:text-(--accent) transition-colors mb-1" title={url}>
                                                                    {url}
                                                                </p>
                                                                <p className="text-[0.5rem] font-black uppercase tracking-[0.2em] text-(--text-muted) opacity-50 flex items-center gap-2">
                                                                    <span className="w-1.5 h-1.5 bg-(--accent)/30 rounded-full"></span>
                                                                    Target URI
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-8 shrink-0">
                                                            <div className="text-right">
                                                                <p className="text-2xl font-black text-(--text) leading-none mb-1">{count}</p>
                                                                <p className="text-[0.5rem] font-black uppercase tracking-widest text-(--text-muted) opacity-50">Violations</p>
                                                            </div>
                                                            <a
                                                                href={url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-[0.625rem] font-black text-(--accent) uppercase tracking-widest hover:text-white hover:bg-(--accent) transition-all px-6 py-3 border border-(--border) rounded-full bg-(--surface-stone) flex items-center gap-3 group/btn cursor-pointer shadow-sm"
                                                            >
                                                                Inspect <span className="group-hover/btn:translate-x-1 transition-transform">→</span>
                                                            </a>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>

                                    {/* Footer Actions */}
                                    <div className="flex items-center justify-between gap-4 pt-8">
                                        {ruleData?.helpUrl && (
                                            <a
                                                href={ruleData.helpUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="px-6 py-3 bg-(--surface-stone) border border-(--border) text-(--text-muted) hover:text-(--accent) hover:border-(--accent) text-[0.625rem] font-black uppercase tracking-widest transition-all rounded-full shadow-sm"
                                            >
                                                Learn More →
                                            </a>
                                        )}
                                        <button
                                            onClick={() => { setSelectedViolationRuleId(null); setShowIssuePrompt(false); }}
                                            className="ml-auto px-10 py-3.5 bg-(--accent) text-white font-black text-[0.625rem] uppercase tracking-widest rounded-full hover:brightness-110 transition-all cursor-pointer shadow-lg shadow-(--accent)/20"
                                        >
                                            Dismiss Dashboard
                                        </button>
                                    </div>
                                </div>
                            );
                        })() : (
                            <div className="space-y-12">
                                {/* Header & Primary Filters */}
                                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 px-2 border-b border-(--border) pb-8">
                                    <div className="space-y-3">
                                        <h2 className="text-4xl font-black text-(--text) tracking-tight">
                                            Violation Ecosystem
                                        </h2>
                                        <p className="text-lg text-(--text-muted) max-w-2xl">
                                            Drill down into specific WCAG failures and technical roadblocks identified across your portfolio.
                                        </p>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-6">
                                        {/* Search Input */}
                                        <div className="relative group">
                                            <input
                                                type="text"
                                                value={issuesSearchQuery}
                                                onChange={(e) => setIssuesSearchQuery(e.target.value)}
                                                placeholder="Search specific violations..."
                                                className="w-72 bg-(--surface-stone) border border-(--border) rounded-full px-6 py-3 pl-12 text-sm font-medium text-(--text) placeholder-(--text-muted)/50 focus:border-(--accent) focus:ring-2 focus:ring-(--accent)/5 focus:outline-none transition-all"
                                            />
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg opacity-40 group-focus-within:opacity-100 transition-opacity">🔍</span>
                                        </div>

                                        {/* Severity Filter */}
                                        <div className="flex items-center gap-4 bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-3">
                                            <span className="text-xs font-bold text-(--text-muted) uppercase tracking-wider">Severity</span>
                                            <select
                                                value={issuesSeverityFilter}
                                                onChange={(e) => setIssuesSeverityFilter(e.target.value)}
                                                className="bg-transparent text-sm font-bold outline-none cursor-pointer text-(--accent)"
                                                aria-label="Filter violations by severity"
                                            >
                                                <option value="all">All Levels</option>
                                                <option value="critical">Critical Only</option>
                                                <option value="serious">Serious & Above</option>
                                                <option value="moderate">Moderate & Above</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Secondary Filters Row */}
                                <div className="flex flex-wrap items-center gap-6 px-2">
                                    <div className="flex items-center gap-4 bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-3">
                                        <span className="text-xs font-bold text-(--text-muted) uppercase tracking-wider">Site</span>
                                        <MultiSelectDropdown
                                            options={Array.from(issuesData.affectedSites || []).sort()}
                                            value={issuesSiteFilter}
                                            onChange={setIssuesSiteFilter}
                                            placeholder="All Domains"
                                        />
                                    </div>

                                    <div className="flex items-center gap-4 bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-3">
                                        <span className="text-xs font-bold text-(--text-muted) uppercase tracking-wider">Page</span>
                                        <MultiSelectDropdown
                                            options={Array.from(issuesData.affectedPages).sort()}
                                            value={issuesPageFilter}
                                            onChange={setIssuesPageFilter}
                                            placeholder="All Pages"
                                            className="max-w-[15.625em]"
                                        />
                                    </div>

                                    <div className="flex items-center gap-4 bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-3">
                                        <span className="text-xs font-bold text-(--text-muted) uppercase tracking-wider">Sort</span>
                                        <select
                                            value={issuesSort.key}
                                            onChange={(e) => setIssuesSort(prev => ({ ...prev, key: e.target.value }))}
                                            className="bg-transparent text-sm font-bold outline-none cursor-pointer text-(--accent)"
                                        >
                                            <option value="totalCount">Total Occurrences</option>
                                            <option value="impact">Severity Level</option>
                                            <option value="ruleId">Rule ID</option>
                                        </select>
                                        <button
                                            onClick={() => setIssuesSort(prev => ({ ...prev, direction: prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                            className="text-sm font-bold hover:text-(--accent) transition-all flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/50"
                                        >
                                            {issuesSort.direction === 'desc' ? '↓' : '↑'}
                                        </button>
                                    </div>

                                    <div className="flex items-center gap-3 bg-(--surface-stone) border border-(--border) rounded-2xl px-6 py-3 ml-auto">
                                        <span className="text-xs font-bold text-(--text-muted) uppercase tracking-wider">Group by Domain</span>
                                        <button
                                            onClick={() => setGroupByDomain(!groupByDomain)}
                                            className={`w-12 h-6 rounded-full transition-all relative ${groupByDomain ? 'bg-(--accent)' : 'bg-gray-300'}`}
                                        >
                                            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${groupByDomain ? 'left-7' : 'left-1'}`}></div>
                                        </button>
                                    </div>
                                </div>

                                {/* Executive Bento Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-6">
                                    {/* Score Card - 2x2 on LG */}
                                    <div className="md:col-span-2 md:row-span-2 bento-card flex flex-col items-center justify-center text-center">
                                        <p className="text-xs font-black uppercase text-(--text-muted) tracking-[0.2em] mb-4">Risk Exposure Score</p>
                                        <div className="relative">
                                            <h3 className="massive-score text-(--text-espresso)">
                                                {issuesData.riskScore || 0}/100
                                            </h3>
                                            <span className={`absolute -top-4 -right-12 text-4xl font-black ${issuesData.pass ? 'text-(--pass-green)' : (issuesData.riskScore > 30 ? 'text-(--fail-red)' : 'text-(--warn-amber)')}`}>
                                                {issuesData.grade || 'A+'}
                                            </span>
                                        </div>
                                        <p className="text-[0.625rem] font-black uppercase tracking-widest text-(--text-muted) mt-8 opacity-60">Security & Accessibility are business-critical</p>
                                    </div>

                                    {/* Critical Failures - 1x1 */}
                                    <div className="bento-card border-l-4 border-l-(--fail-red) flex flex-col justify-between">
                                        <div className="flex justify-between items-start">
                                            <span className="text-2xl">❌</span>
                                            <span className="text-[0.5625rem] font-black text-(--fail-red) uppercase tracking-widest">Critical</span>
                                        </div>
                                        <div>
                                            <h4 className="text-4xl font-black text-(--text-espresso) font-mono">{issuesData.critical}</h4>
                                            <p className="text-[0.5rem] font-black uppercase tracking-[0.2em] text-(--text-muted) mt-1">Legal Hazards</p>
                                        </div>
                                    </div>

                                    {/* Serious Roadblocks - 1x1 */}
                                    <div className="bento-card border-l-4 border-l-(--warn-amber) flex flex-col justify-between">
                                        <div className="flex justify-between items-start">
                                            <span className="text-2xl">⚠️</span>
                                            <span className="text-[0.5625rem] font-black text-(--warn-amber) uppercase tracking-widest">Serious</span>
                                        </div>
                                        <div>
                                            <h4 className="text-4xl font-black text-(--text-espresso) font-mono">{issuesData.serious || 0}</h4>
                                            <p className="text-[0.5rem] font-black uppercase tracking-[0.2em] text-(--text-muted) mt-1">UX Barriers</p>
                                        </div>
                                    </div>

                                    {/* Unique Rules - 1x1 */}
                                    <div className="bento-card flex flex-col justify-between">
                                        <div className="flex justify-between items-start">
                                            <span className="text-2xl">🧩</span>
                                            <span className="text-[0.5625rem] font-black text-(--accent) uppercase tracking-widest">Rules</span>
                                        </div>
                                        <div>
                                            <h4 className="text-4xl font-black text-(--text-espresso) font-mono">{issuesData.totalRules}</h4>
                                            <p className="text-[0.5rem] font-black uppercase tracking-[0.2em] text-(--text-muted) mt-1">Violation Vectors</p>
                                        </div>
                                    </div>

                                    {/* Impacted URLs - 1x1 */}
                                    <div className="bento-card flex flex-col justify-between">
                                        <div className="flex justify-between items-start">
                                            <span className="text-2xl">🌐</span>
                                            <span className="text-[0.5625rem] font-black text-(--accent) uppercase tracking-widest">Sites</span>
                                        </div>
                                        <div>
                                            <h4 className="text-4xl font-black text-(--text-espresso) font-mono">{issuesData.affectedSites ? issuesData.affectedSites.size : new Set(Object.keys(issuesData.rules).flatMap(r => Object.keys(issuesData.rules[r].pages).map(url => { try { return new URL(url).hostname; } catch { return url; } }))).size}</h4>
                                            <p className="text-[0.5rem] font-black uppercase tracking-[0.2em] text-(--text-muted) mt-1">Surface Area</p>
                                        </div>
                                    </div>

                                    {/* Impacted Pages - 1x1 */}
                                    <div className="bento-card flex flex-col justify-between">
                                        <div className="flex justify-between items-start">
                                            <span className="text-2xl">📄</span>
                                            <span className="text-[0.5625rem] font-black text-(--accent) uppercase tracking-widest">Pages</span>
                                        </div>
                                        <div>
                                            <h4 className="text-4xl font-black text-(--text-espresso) font-mono">{issuesData.affectedPages ? issuesData.affectedPages.size : new Set(Object.keys(issuesData.rules).flatMap(r => Object.keys(issuesData.rules[r].pages))).size}</h4>
                                            <p className="text-[0.5rem] font-black uppercase tracking-[0.2em] text-(--text-muted) mt-1">Total Coverage</p>
                                        </div>
                                    </div>

                                    {/* Total Instances - 1x1 on LG, 2x1 on MD */}
                                    <div className="md:col-span-2 lg:col-span-1 bento-card flex flex-col justify-between">
                                        <div className="flex justify-between items-start">
                                            <span className="text-2xl">📋</span>
                                            <span className="text-[0.5625rem] font-black text-(--text-muted) uppercase tracking-widest">Instances</span>
                                        </div>
                                        <div>
                                            <h4 className="text-3xl font-black text-(--text-espresso) font-mono">{issuesData.totalInstances.toLocaleString()}</h4>
                                            <p className="text-[0.5rem] font-black uppercase tracking-[0.2em] text-(--text-muted) mt-1">Total Failures</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Results Table Section */}
                                <div className="bg-white rounded-[2rem] border border-(--border) overflow-hidden shadow-sm">
                                    {(issuesSearchQuery || issuesSeverityFilter !== 'all' || issuesSiteFilter.length > 0 || issuesPageFilter.length > 0) && (
                                        <div className="bg-(--surface-stone)/50 border-b border-(--border) px-8 py-4 flex items-center justify-between">
                                            <span className="text-xs font-bold text-(--text-muted) uppercase tracking-widest flex items-center gap-2">
                                                <span className="w-1.5 h-1.5 rounded-full bg-(--accent) animate-pulse" />
                                                Active Insights: {Object.keys(issuesData.rules).length} matching filters
                                            </span>
                                            <button
                                                onClick={() => { setIssuesSearchQuery(''); setIssuesSeverityFilter('all'); setIssuesSiteFilter([]); setIssuesPageFilter([]); }}
                                                className="text-xs font-bold text-(--fail-red) hover:underline"
                                            >
                                                Clear All ✕
                                            </button>
                                        </div>
                                    )}
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse">
                                            <thead className="bg-(--surface-elevated) border-b-2 border-(--border)">
                                                <tr>
                                                    {[
                                                        { label: 'Violation Vector', key: 'ruleId', align: 'text-left' },
                                                        { label: 'Impact Severity', key: 'impact', align: 'text-center' },
                                                        { label: 'Sites', key: 'sites', align: 'text-center' },
                                                        { label: 'Pages', key: 'pages', align: 'text-center' },
                                                        { label: 'Total Occurrences', key: 'totalCount', align: 'text-center' },
                                                        { label: 'Trend Analysis', key: 'totalCount', align: 'text-right' },
                                                    ].map(col => (
                                                        <th
                                                            key={col.label}
                                                            onClick={() => setIssuesSort(prev => prev.key === col.key ? { ...prev, direction: prev.direction === 'desc' ? 'asc' : 'desc' } : { key: col.key, direction: 'desc' })}
                                                            className={`p-5 text-[0.5625rem] font-black uppercase tracking-widest opacity-80 ${col.align} cursor-pointer hover:opacity-100 hover:text-(--accent) transition-all select-none`}
                                                        >
                                                            {col.label}
                                                            {issuesSort.key === col.key && (
                                                                <span className="ml-1 text-(--accent)">{issuesSort.direction === 'desc' ? '▼' : '▲'}</span>
                                                            )}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-(--border)/30">
                                                {Object.keys(issuesData.rules).length === 0 ? (
                                                    <tr>
                                                        <td colSpan="6" className="p-20 text-center">
                                                            <div className="text-center space-y-6">
                                                                {issuesData.totalRules === 0 ? (
                                                                    <>
                                                                        <span className="text-5xl animate-bounce inline-block">🧩</span>
                                                                        <h3 className="text-sm font-black uppercase tracking-[0.4em] text-(--accent) opacity-80">Mapping Ecosystem...</h3>
                                                                        <p className="text-[0.625rem] font-bold uppercase tracking-[0.2em] max-w-lg mx-auto leading-loose opacity-20">
                                                                            Start a scan to begin systemic rule violation mapping.
                                                                        </p>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <span className="text-5xl inline-block">🔍</span>
                                                                        <h3 className="text-sm font-black uppercase tracking-[0.4em] text-(--text-muted) opacity-90">No Matching Results</h3>
                                                                        <p className="text-[0.625rem] font-bold uppercase tracking-[0.2em] max-w-lg mx-auto leading-loose opacity-80">
                                                                            Try adjusting your search or filter criteria.
                                                                        </p>
                                                                        <button
                                                                            onClick={() => { setIssuesSearchQuery(''); setIssuesSeverityFilter('all'); setIssuesPageFilter('all'); }}
                                                                            className="mt-4 px-6 py-2 bg-(--surface-elevated) border-2 border-(--border) text-[0.625rem] font-black uppercase tracking-widest text-(--accent) hover:border-(--accent) transition-colors"
                                                                        >
                                                                            Clear Filters
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    Object.entries(issuesData.rules).map(([ruleId, data]) => {
                                                        const rulesArray = Object.entries(issuesData.rules);
                                                        const maxCount = Math.max(...rulesArray.map(([, d]) => d.totalCount), 1);
                                                        const trendLevel = Math.ceil((data.totalCount / maxCount) * 5);
                                                        const trendLabel = trendLevel >= 5 ? 'Critical' : trendLevel >= 4 ? 'High' : trendLevel >= 3 ? 'Moderate' : trendLevel >= 2 ? 'Low' : 'Minimal';
                                                        return (
                                                            <tr
                                                                key={ruleId}
                                                                onClick={() => setSelectedViolationRuleId(ruleId)}
                                                                className="group hover:bg-(--accent)/5 transition-colors cursor-pointer"
                                                            >
                                                                <td className="p-5">
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[0.6875rem] font-black uppercase tracking-widest text-(--accent) mb-1">
                                                                            {ruleId.replace(/-/g, ' ')}
                                                                        </span>
                                                                        <span className="text-[0.5rem] font-mono text-(--text-muted) opacity-80 mb-2">
                                                                            RULE_VECTOR: {ruleId}
                                                                        </span>
                                                                        <span className={`self-start text-[0.4375rem] font-black border px-2 py-0.5 rounded inline-block tracking-[0.2em] uppercase ${getPolicyFromTags(data.tags) === 'BEST PRACTICE' || getPolicyFromTags(data.tags) === 'SECTION 508'
                                                                            ? 'bg-(--surface-elevated) text-(--text-muted) border-(--border)'
                                                                            : 'bg-blue-500/10 text-blue-400 border-blue-500/30'
                                                                            }`}>
                                                                            POLICY: {getPolicyFromTags(data.tags)}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="p-5 text-center">
                                                                    <span className={`text-[0.5rem] font-black border px-3 py-1 rounded inline-block tracking-widest ${data.impact === 'critical' ? 'bg-(--error)/10 text-(--error) border-(--error)/30' :
                                                                        data.impact === 'serious' ? 'bg-orange-500/10 text-orange-500 border-orange-500/30' :
                                                                            'bg-(--accent)/10 text-(--accent) border-(--accent)/30'
                                                                        }`}>
                                                                        {data.impact.toUpperCase()}
                                                                    </span>
                                                                </td>
                                                                <td className="p-5 text-center">
                                                                    <span className="text-[0.75rem] font-black text-(--text)">{new Set(Object.keys(data.pages).map(url => { try { return new URL(url).hostname; } catch { return url; } })).size}</span>
                                                                </td>
                                                                <td className="p-5 text-center">
                                                                    <span className="text-[0.75rem] font-black text-(--text)">{Object.keys(data.pages).length}</span>
                                                                </td>
                                                                <td className="p-5 text-center text-[0.75rem] font-black text-(--text)">
                                                                    {data.totalCount.toLocaleString()}
                                                                </td>
                                                                <td className="p-5 text-right">
                                                                    <div className="flex items-center justify-end gap-3">
                                                                        <div className="flex gap-1">
                                                                            {[...Array(5)].map((_, i) => (
                                                                                <div key={i} className={`w-1 h-3 rounded-none ${i < trendLevel ? (trendLevel >= 4 ? 'bg-(--error)' : trendLevel >= 3 ? 'bg-orange-500' : 'bg-(--accent)') : 'bg-white/10'}`} />
                                                                            ))}
                                                                        </div>
                                                                        <span className={`text-[0.5rem] font-black uppercase tracking-widest opacity-90 ${trendLevel >= 4 ? 'text-(--error)' : trendLevel >= 3 ? 'text-orange-500' : 'text-(--accent)'}`}>{trendLabel}</span>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )
                        }
                    </section>
                )}

            {
                dashboardTab === 'reports' && (
                    <section className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {selectedReportId ? (
                            <div className="space-y-6">
                                <button
                                    onClick={() => setSelectedReportId(null)}
                                    className="neu-btn px-6 py-3 text-[0.625rem] font-black uppercase tracking-widest flex items-center gap-3 hover:text-(--accent) transition-all"
                                >
                                    <span>←</span> Back to Reports List
                                </button>

                                {(() => {
                                    const report = reports.find(r => r.id === selectedReportId);
                                    if (!report) return <p>Report not found.</p>;
                                    return (
                                        <div className="space-y-10">
                                            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                                                <div className="space-y-2 pb-6 border-b border-(--border) w-full">
                                                    <h2 className="text-3xl font-black uppercase tracking-[0.2em] text-(--accent) glow-text pl-4 border-l-4 border-(--accent)">
                                                        {report.name}
                                                    </h2>
                                                    <p className="text-[0.625rem] text-(--text-muted) font-bold uppercase tracking-widest opacity-60 ml-4">
                                                        Generated on {report.createdAt?.toDate().toLocaleString()} • {report.summary?.totalPages} Pages Analyzed
                                                    </p>
                                                </div>
                                            </div>
                                            <React.Suspense fallback={<div className="flex justify-center items-center h-64 text-(--text-muted) font-bold uppercase tracking-widest text-[0.625rem]">Loading Interface...</div>}>
                                                <ScanResultsView
                                                    isReportSnapshot={true}
                                                    reportData={{
                                                        summary: report.summary,
                                                        ruleStats: report.ruleStats
                                                    }}
                                                    onDownloadPDF={planLimits?.allowPdfExport ? handleDownloadPDF : undefined}
                                                    thisScanId={report.id}
                                                    scanUrl={report.criteria?.domain}
                                                    onClose={() => setSelectedReportId(null)}
                                                />
                                            </React.Suspense>
                                        </div>
                                    );
                                })()}
                            </div>
                        ) : (
                            <>
                                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 px-2">
                                    <div className="space-y-2">
                                        <h2 className="text-2xl font-black uppercase tracking-[0.3em] text-(--accent) pl-2 border-l-4 border-(--accent) glow-text">
                                            Compliance Reporting
                                        </h2>
                                        <p className="text-[0.625rem] text-(--text-muted) font-bold uppercase tracking-widest opacity-60 ml-3">
                                            Persistent snapshots of accessibility data for formal documentation.
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setIsGenerateReportModalOpen(true)}
                                        className="bg-(--accent) text-black px-8 py-4 rounded-2xl text-[0.6875rem] font-black uppercase tracking-[0.2em] shadow-[0_0_1.25em_rgba(var(--accent-rgb),0.3)] hover:scale-[1.02] transition-all flex items-center gap-3"
                                    >
                                        <span className="text-lg">📊</span> Generate New Report
                                    </button>
                                </div>

                                {/* Reports Toolbar */}
                                <div className="flex flex-wrap items-center gap-3 px-2">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={reportsSearchQuery}
                                            onChange={(e) => setReportsSearchQuery(e.target.value)}
                                            placeholder="Search reports..."
                                            className="w-52 bg-(--surface-elevated) border-2 border-(--border) rounded-none px-4 py-2 pl-9 text-[0.625rem] font-bold uppercase tracking-widest text-(--text) placeholder-(--text-muted)/40 focus:border-(--accent) focus:outline-none transition-colors"
                                        />
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-(--text-muted) opacity-40">🔍</span>
                                    </div>
                                    <div className="flex items-center gap-2 bg-(--surface-elevated)/40 border-2 border-(--border) rounded-none px-4 py-2">
                                        <span className="text-[0.5625rem] font-black uppercase tracking-widest opacity-80">Sort</span>
                                        <select
                                            value={reportsSort.key}
                                            onChange={(e) => setReportsSort(prev => ({ ...prev, key: e.target.value }))}
                                            className="bg-transparent text-[0.625rem] font-black uppercase tracking-widest outline-none cursor-pointer text-(--accent)"
                                            aria-label="Sort reports by"
                                        >
                                            <option value="date">Date</option>
                                            <option value="name">Name</option>
                                            <option value="risk">Risk Score</option>
                                            <option value="violations">Violations</option>
                                        </select>
                                        <button
                                            onClick={() => setReportsSort(prev => ({ ...prev, direction: prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                            className="text-[0.625rem] font-black hover:text-(--accent) transition-colors w-4"
                                        >
                                            {reportsSort.direction === 'desc' ? '↓' : '↑'}
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-6">
                                    {(() => {
                                        let filtered = reports.filter(r => (viewMode === 'archived' ? !!r.archived : !r.archived));
                                        // Search
                                        if (reportsSearchQuery) {
                                            const q = reportsSearchQuery.toLowerCase();
                                            filtered = filtered.filter(r =>
                                                (r.name || '').toLowerCase().includes(q) ||
                                                (r.criteria?.domain || '').toLowerCase().includes(q)
                                            );
                                        }
                                        // Sort
                                        filtered = [...filtered].sort((a, b) => {
                                            const dir = reportsSort.direction === 'desc' ? -1 : 1;
                                            switch (reportsSort.key) {
                                                case 'name': return dir * (a.name || '').localeCompare(b.name || '');
                                                case 'risk': return dir * ((a.summary?.riskScore || 0) - (b.summary?.riskScore || 0));
                                                case 'violations': return dir * ((a.summary?.totalViolations || 0) - (b.summary?.totalViolations || 0));
                                                default: return dir * ((b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
                                            }
                                        });
                                        return filtered.length === 0 ? (
                                            <div className="neu-card p-24 text-center border-dashed border-2 border-(--border)">
                                                <span className="text-4xl mb-6 block opacity-20">📂</span>
                                                <p className="text-[0.625rem] text-(--text-muted) font-black uppercase tracking-[0.4em] opacity-40">
                                                    {reportsSearchQuery ? 'No reports match your search.' : 'No generated reports found. Create your first audit snapshot to begin.'}
                                                </p>
                                            </div>
                                        ) : (
                                            filtered.map((report) => (
                                                <div
                                                    key={report.id}
                                                    className="neu-card p-8 group hover:border-(--accent) transition-all flex flex-col md:flex-row items-center justify-between gap-8"
                                                >
                                                    <div className="flex-1 space-y-3">
                                                        <div className="flex items-center gap-4">
                                                            <span className="text-2xl group-hover:scale-125 transition-transform">📊</span>
                                                            <h3 className="text-lg font-black uppercase tracking-widest text-(--accent)">
                                                                {report.name}
                                                            </h3>
                                                        </div>
                                                        <div className="flex flex-wrap items-center gap-6 text-[0.5625rem] font-black uppercase tracking-tighter text-(--text-muted)">
                                                            <span className="bg-black/40 border border-(--border) px-2 py-0.5 rounded">Filter: {report.criteria?.domain?.toUpperCase()}</span>
                                                            <span className="bg-black/40 border border-(--border) px-2 py-0.5 rounded">Pages: {report.summary?.totalPages}</span>
                                                            <span className="bg-black/40 border border-(--border) px-2 py-0.5 rounded">Violations: {report.summary?.totalViolations}</span>
                                                            <span className="bg-black/40 border border-(--border) px-2 py-0.5 rounded">Date: {report.createdAt?.toDate().toLocaleDateString()}</span>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-4">
                                                        <div className="text-right px-6 border-r border-(--border)/20 mr-2">
                                                            <p className="text-[0.5rem] font-black text-(--text-muted) uppercase tracking-widest mb-1">Risk Score</p>
                                                            <p className={`text-xl font-black ${report.summary?.riskScore > 40 ? 'text-(--error)' : 'text-(--success)'} glow-text`}>
                                                                {report.summary?.riskScore}
                                                            </p>
                                                        </div>
                                                        <button
                                                            onClick={() => setSelectedReportId(report.id)}
                                                            className="neu-btn px-6 py-3 text-[0.625rem] font-black uppercase tracking-widest hover:border-(--accent) hover:text-(--accent) transition-all"
                                                        >
                                                            View Report
                                                        </button>
                                                        <button
                                                            onClick={() => handleToggleReportArchive(report.id, report.archived)}
                                                            className="neu-btn px-4 py-3 text-[0.625rem] hover:border-(--accent) hover:text-(--accent)"
                                                            title={report.archived ? "Restore to Active" : "Move to Archive"}
                                                        >
                                                            {report.archived ? '📤' : '📦'}
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setConfirmState({
                                                                    isOpen: true,
                                                                    title: 'Delete Report',
                                                                    message: `Are you sure you want to permanently delete the report "${report.name}"?`,
                                                                    onConfirm: async () => {
                                                                        try {
                                                                            await deleteDoc(doc(db, 'users', user.uid, 'reports', report.id));
                                                                            setToast({ message: 'Report deleted.', type: 'info' });
                                                                        } catch (err) {
                                                                            console.error("Failed to delete report:", err);
                                                                        }
                                                                        setConfirmState(prev => ({ ...prev, isOpen: false }));
                                                                    }
                                                                });
                                                            }}
                                                            className="neu-btn neu-btn-danger px-4 py-3 text-[0.625rem]"
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        );
                                    })()}
                                </div>
                            </>
                        )}
                    </section>
                )
            }



            {/* Crawl Review Modal */}
            {
                activeCrawlScan && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
                        <div className="bg-(--surface) border border-(--border)/30 rounded-[2.5rem] w-full max-w-5xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-300">
                            <div className="p-8 border-b border-(--border)/30 flex items-center justify-between bg-white/50 backdrop-blur-sm">
                                <h2 className="text-xl font-black uppercase tracking-tight text-(--accent) flex items-center gap-4 ">
                                    <span className="text-2xl animate-pulse">🔎</span> Review Found Pages
                                </h2>
                                <button
                                    onClick={() => setActiveCrawlScan(null)}
                                    className="w-10 h-10 rounded-full flex items-center justify-center text-(--text-muted) hover:bg-(--surface-stone) hover:text-(--accent) transition-all cursor-pointer"
                                >
                                    ✕
                                </button>
                            </div>
                            <div className="p-8 bg-(--surface-stone)/30 flex flex-col sm:flex-row gap-6 border-b border-(--border)/30 shadow-inner">
                                <div className="flex-1 space-y-3">
                                    <label className="text-[0.625rem] font-black uppercase tracking-widest text-(--text-muted) pl-4">Injection Inclusions (Regex)</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={crawlUrlFilter}
                                            onChange={(e) => {
                                                setCrawlUrlFilter(e.target.value);
                                                applyCrawlFilters(activeCrawlScan.discoveredUrls, e.target.value, crawlUrlExclude);
                                            }}
                                            className="w-full bg-white border border-(--border) focus:border-(--accent) py-3.5 px-6 text-xs font-mono text-(--accent) placeholder:opacity-20 rounded-2xl focus:outline-none transition-all shadow-sm"
                                            placeholder=".*"
                                        />
                                        <span className="absolute right-5 top-1/2 -translate-y-1/2 text-[0.625rem] text-(--accent) font-black opacity-30">FILTER_IN</span>
                                    </div>
                                </div>
                                <div className="flex-1 space-y-3">
                                    <label className="text-[0.625rem] font-black uppercase tracking-widest text-(--text-muted) pl-4">URL Exclusions (Regex)</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={crawlUrlExclude}
                                            onChange={(e) => {
                                                setCrawlUrlExclude(e.target.value);
                                                applyCrawlFilters(activeCrawlScan.discoveredUrls, crawlUrlFilter, e.target.value);
                                            }}
                                            className="w-full bg-white border border-(--border) focus:border-(--error) py-3.5 px-6 text-xs font-mono text-(--error) placeholder:opacity-20 rounded-2xl focus:outline-none transition-all shadow-sm"
                                            placeholder="EMPTY (NO EXCLUSIONS)"
                                        />
                                        <span className="absolute right-5 top-1/2 -translate-y-1/2 text-[0.625rem] text-(--error) font-black opacity-30">FILTER_OUT</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-0 bg-white/30 backdrop-blur-sm">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="sticky top-0 bg-white/95 backdrop-blur-md z-10 border-b border-(--border)/30">
                                            <th className="p-8 w-16 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={crawlSelectedUrls.size === activeCrawlScan.discoveredUrls?.length && activeCrawlScan.discoveredUrls?.length > 0}
                                                    onChange={(e) => {
                                                        if (e.target.checked) setCrawlSelectedUrls(new Set(activeCrawlScan.discoveredUrls));
                                                        else setCrawlSelectedUrls(new Set());
                                                    }}
                                                    className="w-5 h-5 accent-(--accent) rounded-md cursor-pointer scale-110"
                                                />
                                            </th>
                                            <th className="p-8 text-[0.75rem] font-black text-(--text-muted) uppercase tracking-widest flex items-center justify-between">
                                                <span className="opacity-60">FOUND PAGES ({activeCrawlScan.discoveredUrls?.length} NODES DETECTED)</span>
                                                <div className="flex items-center gap-3">
                                                    <button
                                                        onClick={() => setCrawlUrlSort(crawlUrlSort === 'asc' ? 'none' : 'asc')}
                                                        className={`px-4 py-2 rounded-xl border text-[0.5625rem] font-black transition-all ${crawlUrlSort === 'asc' ? 'border-(--accent) bg-(--accent) text-white shadow-lg' : 'border-(--border) hover:border-(--accent) text-gray-500 hover:bg-white'}`}
                                                        title="Ascending Sort"
                                                    >
                                                        ↑A-Z
                                                    </button>
                                                    <button
                                                        onClick={() => setCrawlUrlSort(crawlUrlSort === 'desc' ? 'none' : 'desc')}
                                                        className={`px-3 py-1.5 rounded-none border-2 text-[0.5625rem] font-black transition-all ${crawlUrlSort === 'desc' ? 'border-(--accent) bg-(--accent) text-black shadow-[0.25em_0.25em_0em_var(--border)]' : 'border-(--border) hover:border-(--accent) text-gray-500'}`}
                                                        title="Descending Sort"
                                                    >
                                                        ↓Z-A
                                                    </button>
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            let urls = [...(activeCrawlScan.discoveredUrls || [])];
                                            if (crawlUrlSort === 'asc') {
                                                urls.sort((a, b) => a.localeCompare(b));
                                            } else if (crawlUrlSort === 'desc') {
                                                urls.sort((a, b) => b.localeCompare(a));
                                            }
                                            return urls.map((url, i) => {
                                                const isSelected = crawlSelectedUrls.has(url);
                                                return (
                                                    <tr key={i} className={`border-b border-(--border) hover:bg-(--accent)/5 transition-all group ${isSelected ? 'bg-(--accent)/5' : ''}`}>
                                                        <td className="p-4 text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={(e) => {
                                                                    const newSet = new Set(crawlSelectedUrls);
                                                                    if (e.target.checked) newSet.add(url);
                                                                    else newSet.delete(url);
                                                                    setCrawlSelectedUrls(newSet);
                                                                }}
                                                                className="w-5 h-5 bg-(--surface) border-2 border-(--border) accent-(--accent) cursor-pointer"
                                                            />
                                                        </td>
                                                        <td className="p-5">
                                                            <p className={`text-xs font-mono truncate transition-colors tracking-tighter ${isSelected ? 'text-(--accent) font-black' : 'text-(--text-muted) opacity-50 line-through'}`}>{url}</p>
                                                        </td>
                                                    </tr>
                                                );
                                            });
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                             <div className="p-10 border-t border-(--border)/30 flex items-center justify-between bg-white/50 backdrop-blur-md ">
                                <div className="flex flex-col pl-2">
                                    <span className="text-[0.625rem] font-black text-(--text-muted) uppercase tracking-widest opacity-40">Selected Units</span>
                                    <span className="text-3xl font-mono font-black text-(--accent) tabular-nums tracking-tighter">{crawlSelectedUrls.size.toString().padStart(3, '0')}</span>
                                </div>
                                <div className="flex gap-4">
                                    <button
                                        onClick={() => setActiveCrawlScan(null)}
                                        className="px-10 py-4 bg-white border border-(--border) hover:border-(--text-muted) text-(--text-muted) hover:text-(--text) text-[0.625rem] font-black uppercase tracking-widest rounded-full transition-all"
                                    >
                                        Dismiss
                                    </button>
                                    <button
                                        onClick={handleSubmitCrawlSelections}
                                        disabled={crawlSelectedUrls.size === 0 || submitting}
                                        className="px-12 py-4 bg-(--accent) text-white shadow-xl hover:shadow-2xl hover:brightness-110 text-[0.625rem] font-black uppercase tracking-widest rounded-full transition-all disabled:opacity-20 flex items-center gap-3 active:scale-95"
                                    >
                                        {submitting ? (
                                            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                        ) : '🚀 Start Scan'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Generate Report Modal */}
            {
                isGenerateReportModalOpen && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
                        <div className="bg-(--surface) border border-(--border)/30 rounded-[2.5rem] w-full max-w-3xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-300">
                            <div className="p-10 border-b border-(--border)/30 flex items-center justify-between bg-white/50 backdrop-blur-sm">
                                <h2 className="text-xl font-black uppercase tracking-tight text-(--accent) flex items-center gap-4 ">
                                    <span className="text-2xl animate-pulse">📊</span> Initiate Analytics
                                </h2>
                                <button
                                    onClick={() => setIsGenerateReportModalOpen(false)}
                                    className="w-10 h-10 rounded-full flex items-center justify-center text-(--text-muted) hover:bg-(--surface-stone) hover:text-(--accent) transition-all cursor-pointer"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="p-10 space-y-8">
                                <div className="space-y-3">
                                    <label className="text-[0.625rem] font-black text-(--text-muted) uppercase tracking-widest pl-4 block opacity-50">Report Sector Name</label>
                                    <input
                                        type="text"
                                        value={reportForm.name}
                                        onChange={(e) => setReportForm(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="e.g. Q1 COMPLIANCE SNAPSHOT"
                                        className="w-full bg-white border border-(--border)/50 rounded-2xl px-6 py-4 text-sm font-mono text-(--accent) focus:border-(--accent) outline-none transition-all shadow-sm"
                                    />
                                </div>

                                <div className="space-y-3">
                                    <label className="text-[0.625rem] font-black text-(--text-muted) uppercase tracking-widest pl-4 block opacity-50">Website Scope</label>
                                    <select
                                        value={reportForm.domain}
                                        onChange={(e) => setReportForm(prev => ({ ...prev, domain: e.target.value }))}
                                        className="w-full bg-white border border-(--border)/50 rounded-2xl px-6 py-4 text-sm font-black uppercase tracking-widest text-(--accent) focus:border-(--accent) outline-none transition-all appearance-none cursor-pointer shadow-sm"
                                    >
                                        <option value="all">FULL PORTFOLIO</option>
                                        {allUniqueDomains.map(d => (
                                            <option key={d} value={d}>{d.toUpperCase()}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="space-y-4">
                                    <label className="text-[0.625rem] font-black text-(--text-muted) uppercase tracking-widest pl-4 block opacity-50">Tactical Period Presets</label>
                                    <div className="flex flex-wrap gap-3">
                                        {[
                                            { label: 'LAST 7D', value: '7d' },
                                            { label: 'LAST 14D', value: '14d' },
                                            { label: 'LAST 30D', value: '30d' },
                                            { label: 'LAST MONTH', value: 'lastMonth' },
                                            { label: 'THIS MONTH', value: 'thisMonth' },
                                            { label: 'ALL DATA', value: 'all' }
                                        ].map(q => (
                                            <button
                                                key={q.value}
                                                onClick={() => handleQuickSelectDate(q.value)}
                                                className="px-4 py-2 bg-white border border-(--border)/30 text-[0.5rem] font-black uppercase tracking-widest hover:border-(--accent) hover:text-(--accent) transition-all cursor-pointer rounded-xl shadow-sm active:scale-95"
                                            >
                                                {q.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-3">
                                        <label className="text-[0.625rem] font-black text-(--text-muted) uppercase tracking-widest pl-4 block opacity-50">Start Period</label>
                                        <input
                                            type="date"
                                            value={reportForm.startDate}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, startDate: e.target.value }))}
                                            className="w-full bg-white border border-(--border)/50 rounded-2xl px-6 py-4 text-sm font-mono text-(--accent) focus:border-(--accent) outline-none transition-all shadow-sm [color-scheme:light]"
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        <label className="text-[0.625rem] font-black text-(--text-muted) uppercase tracking-widest pl-4 block opacity-50">End Period</label>
                                        <input
                                            type="date"
                                            value={reportForm.endDate}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, endDate: e.target.value }))}
                                            className="w-full bg-white border border-(--border)/50 rounded-2xl px-6 py-4 text-sm font-mono text-(--accent) focus:border-(--accent) outline-none transition-all shadow-sm [color-scheme:light]"
                                        />
                                    </div>
                                </div>

                                <div className="p-6 bg-(--accent)/5 border border-(--accent)/10 rounded-2xl">
                                    <p className="text-[0.625rem] font-bold text-(--accent) uppercase tracking-widest text-center opacity-70">
                                        Your report will aggregate current compliance health metrics from {reportForm.domain === 'all' ? 'all' : reportForm.domain} clusters.
                                    </p>
                                </div>
                            </div>

                            <div className="p-10 bg-white/50 backdrop-blur-md flex gap-4 border-t border-(--border)/30">
                                <button
                                    onClick={() => setIsGenerateReportModalOpen(false)}
                                    className="flex-1 px-8 py-4 bg-white border border-(--border) hover:border-(--text-muted) text-(--text-muted) hover:text-(--text) text-[0.625rem] font-black uppercase tracking-widest rounded-full transition-all"
                                >
                                    Dismiss
                                </button>
                                <button
                                    onClick={handleGenerateReport}
                                    disabled={submitting || !reportForm.name}
                                    className="flex-[2] px-10 py-4 bg-(--accent) text-white shadow-xl hover:shadow-2xl hover:brightness-110 text-[0.625rem] font-black uppercase tracking-widest rounded-full transition-all disabled:opacity-20 flex items-center justify-center gap-3 active:scale-95"
                                >
                                    {submitting ? (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent animate-spin rounded-full" />
                                    ) : (
                                        <><span>EXECUTE ANALYTICS</span> 🚀</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* UI Components */}
            <ConfirmModal
                isOpen={confirmState.isOpen}
                title={confirmState.title}
                message={confirmState.message}
                onConfirm={confirmState.onConfirm}
                onCancel={() => setConfirmState(prev => ({ ...prev, isOpen: false }))}
            />

            {
                toast && (
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        onClose={() => setToast(null)}
                    />
                )
            }

            {/* Global Footer */}
            <Footer />
            
            <UpgradePromptModal
                isOpen={upgradeModal.isOpen}
                onClose={() => setUpgradeModal({ isOpen: false, title: '', message: '' })}
                title={upgradeModal.title}
                message={upgradeModal.message}
            />
        </div >
    );
};

export default DashboardPage;
